import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import {
    CSS2DRenderer,
    CSS2DObject
} from "three/examples/jsm/renderers/CSS2DRenderer.js";
import {
    createGradientTexture,
    loadObjFile,
    loadStlFile
} from "../modules/3d.js";
import { showToast } from "../modules/toast.js";
import toothClasses from "../../data/tooth_classes.json";
import defectClasses from "../../data/defect_classes.json";
import keypointClasses from "../../data/keypoint_classes_all.json";
import { drawKeypoints, drawMarginalRidgeLines } from "../modules/keypoints.js";
import { decodeRunLength } from "../modules/encoding.js";
import {
    estimateProcessingTime,
    updateProgressBar,
    processingTimeHistory
} from "../modules/processingtime.js";
import { globalState } from "./globals.js";
import {
    validatePatientIdConsistency,
    classifyArrangementFolderFiles
} from "./arrangementFiles.js";

// Global authentication state
let isAuthenticationEnabled = true; // Default to true for safety
import {
    showDownloadButton,
    processSeparation
} from "./teethreconstruction.js";
import {
    ApiService,
    setAutoLogoutCallback,
    setAuthenticationEnabled,
    getAccessToken,
    syncToken,
    resetLogoutState
} from "./api.js";
import {
    initializeFeedback,
    hideFeedbackButton,
    showFeedbackForm,
    feedbackManager
} from "../modules/feedback.js";
import {
    parseFeedbackFiles,
    formatProcessingMode,
    calculateFeedbackStatistics
} from "../modules/feedback-parser.js";
import {
    initializePostprocessingControls,
    collectPostprocessingParameters,
    collectFixedTeeth,
    readFixedTeethRaw,
    isPermanentToothFdi,
    updatePostprocessingSectionVisibility,
    applyMolarMovementToggleGate
} from "./postprocessing.js";
import {
    initializeLevelingControls,
    collectLevelingParameters,
    updateLevelingSectionVisibility,
    updateAchievedOverbite,
    applyLevelingActivationToggle
} from "./leveling.js";

// Camera utilities
const CAMERA_CONFIG = {
    FRUSTUM_SIZE: 100,
    NEAR: 0.1,
    FAR: 1000
};

// LocalStorage keys
const STORAGE_KEY_GINGIVA_ESTIMATION = "arrangement-gingiva-estimation";
const STORAGE_KEY_CALCULATE_SPACE_INTERSECTION =
    "arrangement-calculate-space-intersection";
const STORAGE_KEY_AUTO_ORIENTATION = "segmentation-auto-orientation";

/**
 * Create an orthographic camera with the given aspect ratio
 * @param {number} aspect - Width/height aspect ratio
 * @param {number} frustumSize - Size of the viewing frustum (default: 100)
 * @returns {THREE.OrthographicCamera}
 */
function createOrthographicCamera(
    aspect,
    frustumSize = CAMERA_CONFIG.FRUSTUM_SIZE
) {
    return new THREE.OrthographicCamera(
        (-frustumSize * aspect) / 2,
        (frustumSize * aspect) / 2,
        frustumSize / 2,
        -frustumSize / 2,
        CAMERA_CONFIG.NEAR,
        CAMERA_CONFIG.FAR
    );
}

/**
 * Update orthographic camera frustum for new aspect ratio
 * @param {THREE.OrthographicCamera} camera - The camera to update
 * @param {number} aspect - New width/height aspect ratio
 * @param {number} frustumSize - Size of the viewing frustum (default: 100)
 */
function updateOrthographicCamera(
    camera,
    aspect,
    frustumSize = CAMERA_CONFIG.FRUSTUM_SIZE
) {
    camera.left = (-frustumSize * aspect) / 2;
    camera.right = (frustumSize * aspect) / 2;
    camera.top = frustumSize / 2;
    camera.bottom = -frustumSize / 2;
    camera.updateProjectionMatrix();
}

// Check authentication status first, then validate tokens only if auth is enabled
async function initializeAuthentication() {
    console.log("🔐 Initializing authentication...");

    // Check if we're in OAuth callback flow (has ?code= parameter)
    const urlParams = new URLSearchParams(window.location.search);
    const oauthCode = urlParams.get("code");

    console.log(
        "🔍 URL params check - code param:",
        oauthCode ? "PRESENT" : "NOT FOUND"
    );
    console.log("🔍 Full URL:", window.location.href);

    if (oauthCode) {
        console.log("🔐 OAuth callback detected - SKIPPING all auth checks!");
        console.log(
            "🔐 Waiting for server to process OAuth code and redirect..."
        );
        // We're in OAuth callback flow - don't validate tokens yet
        // The server will process the code and set the cookie, then redirect
        // Just show a loading state and wait - DO NOT PROCEED WITH AUTH CHECKS
        return Promise.resolve(); // Explicitly return resolved promise
    }

    console.log("🔐 No OAuth code found - proceeding with normal auth flow");

    try {
        // First check system status to know if authentication is enabled
        // This API call doesn't require authentication
        const data = await ApiService.getSystemStatus();

        // Apply UI feature flags before any early returns below.
        applyProcessingModeGate(data.enable_processing_mode_selector);
        applyPatientIdMatchGate(data.enforce_patient_id_match);
        applyPatientIdMatchStrictnessGate(data.strict_patient_id_match);
        applyRequiredFilesGate(data.require_all_arrangement_files);
        applyInterproximalAdvancedParamsGate(
            data.enable_interproximal_advanced_params
        );
        applyLevelingActivationToggle(data.enable_leveling_activation_toggle);
        applySpaceIntersectionToggleGate(data.enable_space_intersection_toggle);
        applyMolarMovementToggleGate(data.enable_molar_movement_toggle);
        applyInfoJsonReviewGate(data.enable_infojson_review_gate);
        applyAutoOrientationGate(data.auto_orientation);

        // Store authentication status globally
        isAuthenticationEnabled = data.authentication_enabled;

        // Also update the API service with authentication status
        setAuthenticationEnabled(data.authentication_enabled);

        if (!data.authentication_enabled) {
            console.log(
                "🔓 Authentication is disabled - skipping token validation"
            );
            document.getElementById("authentication").style.display = "none";
            return;
        }

        console.log("🔐 Authentication is enabled - validating tokens...");

        // Only validate tokens if authentication is enabled
        const isTokenValid = await ApiService.validateTokenOnLoad();
        if (!isTokenValid) {
            // Token validation failed, auto logout already triggered
            return;
        }

        // Check if user is logged in
        if (data.current_user === null) {
            // User is not logged in - perform auto logout
            console.log(
                "Authentication required but user is null - auto logout"
            );
            performAutoLogout();
        } else {
            console.log("User is authenticated:", data.current_user);
        }
    } catch (error) {
        console.error("Error during authentication initialization:", error);

        // If we can't get system status, assume auth is enabled for safety
        // and check tokens
        console.log(
            "🔐 System status unavailable - assuming auth enabled, checking tokens..."
        );
        isAuthenticationEnabled = true;

        // Also update the API service with authentication status
        setAuthenticationEnabled(true);
        checkTokenAndLogout();
    }
}

/**
 * Hide the Processing Mode selector when the backend disables it (production).
 * UX gate only: the pipeline is locked to "arrangement" client-side. When the
 * flag is missing (older backend) or true, the selector stays visible.
 * @param {boolean|undefined} enabled Backend flag from /api/system-status
 */
// Whether the backend allows the Processing Mode selector. When false the
// selector stays hidden and the pipeline is locked to "arrangement". Stored at
// module scope so application-mode switches (Processing <-> Review) keep
// honoring it instead of re-showing the selector.
let processingModeSelectorEnabled = true;

function applyProcessingModeGate(enabled) {
    processingModeSelectorEnabled = enabled !== false;
    updateProcessingModeSelectorVisibility();
}

// When True, arrangement submission requires the patient ID (the first
// space-delimited token of each file name) to match across all uploaded
// files. Controlled by the ENFORCE_PATIENT_ID_MATCH server setting. Default
// True so a system-status fetch failure does not silently drop the check.
let enforcePatientIdMatch = true;

function applyPatientIdMatchGate(enabled) {
    enforcePatientIdMatch = enabled !== false;
}

// When True, a patient-ID mismatch blocks submission (production). When
// False, it only shows a dismissible warning and submission proceeds -
// testers need to get past false positives (e.g. lab-storage names that
// don't follow the naming convention). Controlled by the
// STRICT_PATIENT_ID_MATCH server setting. Default True so a system-status
// fetch failure does not silently relax the check.
let strictPatientIdMatch = true;

function applyPatientIdMatchStrictnessGate(enabled) {
    strictPatientIdMatch = enabled !== false;
}

// Apply the strict/warn split to a validatePatientIdConsistency() result.
// Returns an { error } object to propagate (blocks submission) when strict,
// or null after showing a dismissible warning toast (submission proceeds).
function resolvePatientIdCheckError(idError) {
    if (!idError) {
        return null;
    }
    if (strictPatientIdMatch) {
        return { error: idError };
    }
    showToast(idError, "exclamation-triangle", 6000);
    return null;
}

// When True, all four arrangement inputs (lower jaw, upper jaw, InfoJSON,
// landmarks CSV) are required before submitting. When False, only the jaws are
// required and InfoJSON / landmarks are optional. Controlled by the
// REQUIRE_ALL_ARRANGEMENT_FILES server setting.
let requireAllArrangementFiles = true;

function applyRequiredFilesGate(enabled) {
    requireAllArrangementFiles = enabled !== false;
    updateOptionalFileLabels();
}

// Show/hide the advanced interproximal parameters (Gap Range + Tightness).
// Controlled by ENABLE_INTERPROXIMAL_ADVANCED_PARAMS. When hidden, the sliders
// stay in the DOM at their defaults, so submitted parameters are unchanged.
function applyInterproximalAdvancedParamsGate(enabled) {
    const section = document.getElementById("interproximal-advanced-params");
    if (!section) {
        return;
    }
    section.style.display = enabled === false ? "none" : "";
}

// When False, the "Compute Space/Intersection" toggle is hidden and the
// computation is forced on (production). Controlled by the server setting
// ENABLE_SPACE_INTERSECTION_TOGGLE.
let spaceIntersectionToggleEnabled = true;

// Show/hide the Compute Space/Intersection toggle. When hidden, force the
// checkbox checked so the computation always runs; collectArrangementModelOptions
// also honors the flag so saved state cannot turn it off behind a hidden toggle.
function applySpaceIntersectionToggleGate(enabled) {
    spaceIntersectionToggleEnabled = enabled !== false;
    const group = document.getElementById("space-intersection-control-group");
    const checkbox = document.getElementById(
        "calculate-space-intersection-checkbox"
    );
    if (group) {
        group.style.display = spaceIntersectionToggleEnabled ? "" : "none";
    }
    if (!spaceIntersectionToggleEnabled && checkbox) {
        checkbox.checked = true;
    }
}

// When False, the deployment has turned segmentation auto orientation off
// entirely (server setting AUTO_ORIENTATION), so the checkbox is hidden and
// forced off. Unlike the toggles above this is not a UX gate: with it off the
// worker will not re-orient anything, and leaving a ticked checkbox on screen
// would promise a rotation that cannot happen.
let autoOrientationEnabled = true;

// Show/hide the Auto Orientation checkbox. isAutoOrientationEnabled and
// updateModelOptionsSectionVisibility both honor the flag, so saved state cannot
// request auto orientation behind a hidden checkbox.
function applyAutoOrientationGate(enabled) {
    autoOrientationEnabled = enabled !== false;
    const section = document.getElementById("segmentation-options-section");
    const checkbox = document.getElementById(
        "enable-auto-orientation-checkbox"
    );
    if (!autoOrientationEnabled) {
        if (section) {
            section.style.display = "none";
        }
        if (checkbox) {
            checkbox.checked = false;
        }
    }
}

// When True, the arrangement InfoJSON download is gated behind an explicit
// Accept/Reject review (production): Accept logs a compliance acceptance before
// downloading; Reject requires a reason (also logged). When False, a plain
// Download button is shown (legacy dev behavior). Controlled by the server
// setting ENABLE_INFOJSON_REVIEW_GATE.
let infoJsonReviewGateEnabled = true;

const INFOJSON_ACTION_BUTTON_IDS = [
    "download-infojson-button",
    "accept-infojson-button",
    "reject-infojson-button"
];

// Select the review gate mode. Re-applies the visible button set if a result is
// already on screen, so a late system-status response cannot leave a stale set.
function applyInfoJsonReviewGate(enabled) {
    infoJsonReviewGateEnabled = enabled !== false;
    const anyVisible = INFOJSON_ACTION_BUTTON_IDS.some((id) => {
        const el = document.getElementById(id);
        return el && el.style.display && el.style.display !== "none";
    });
    if (anyVisible) {
        showInfoJsonActions();
    }
}

// Reveal the correct InfoJSON action buttons for the active gate mode: plain
// Download when the gate is off, Accept + Reject when it is on.
function showInfoJsonActions() {
    const download = document.getElementById("download-infojson-button");
    const accept = document.getElementById("accept-infojson-button");
    const reject = document.getElementById("reject-infojson-button");
    if (infoJsonReviewGateEnabled) {
        if (download) download.style.display = "none";
        if (accept) accept.style.display = "block";
        if (reject) reject.style.display = "block";
    } else {
        if (download) download.style.display = "block";
        if (accept) accept.style.display = "none";
        if (reject) reject.style.display = "none";
    }
}

// Hide every InfoJSON action button (used on reset / start-over / new run).
function hideInfoJsonActions() {
    INFOJSON_ACTION_BUTTON_IDS.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.style.display = "none";
    });
}

// Accept: record the acceptance to the audit log (blocking on the server), then
// download the InfoJSON. On a logging failure the download is refused so the
// acceptance is never silently skipped.
async function acceptInfoJson() {
    if (!globalState.lastArrangementInfoJson) {
        showToast(
            "No InfoJSON data available for download",
            "exclamation-triangle"
        );
        return;
    }
    const itemId = globalState.lastProcessedItemId;
    if (!itemId) {
        showToast(
            "Cannot record acceptance: missing job id",
            "exclamation-triangle"
        );
        return;
    }
    try {
        await ApiService.submitReviewDecision(itemId, "accept");
    } catch (error) {
        console.error("Failed to record acceptance:", error);
        showToast(
            `Failed to record acceptance: ${error.message}`,
            "exclamation-triangle"
        );
        return;
    }
    downloadInfoJson();
}

// Reject: record the rejection decision immediately, then open the feedback
// form so the clinician can explain why. A rejection is negative feedback, so
// the reason is captured as feedback (linked to the same audit row via
// feedback_path) rather than a separate prompt.
async function rejectInfoJson() {
    const itemId = globalState.lastProcessedItemId;
    if (!itemId) {
        showToast(
            "Cannot record rejection: missing job id",
            "exclamation-triangle"
        );
        return;
    }
    try {
        await ApiService.submitReviewDecision(itemId, "reject");
        hideInfoJsonActions();
    } catch (error) {
        console.error("Failed to record rejection:", error);
        showToast(
            `Failed to record rejection: ${error.message}`,
            "exclamation-triangle"
        );
        // Fall through: still open the feedback form so the clinician's reason
        // is captured even if the decision flag could not be persisted.
    }
    // Open the feedback form to capture the reason for the rejection.
    showFeedbackForm("arrangement");
}

// Append "(Optional)" to the InfoJSON / landmarks labels when those inputs are
// not required, so the UI hint matches the active requiredness rule. Labels
// carry their base text in the data-optional-label attribute.
function updateOptionalFileLabels() {
    const labels = document.querySelectorAll("[data-optional-label]");
    labels.forEach((label) => {
        const base = label.getAttribute("data-optional-label");
        if (requireAllArrangementFiles) {
            label.textContent = base;
        } else {
            label.innerHTML =
                base +
                ' <span style="font-style: italic; color: #666;">(Optional)</span>';
        }
    });
}

// Show/hide the Processing Mode selector and reconcile the two dividers around
// it for the current application mode. The selector is shown only in Processing
// mode when enabled. When the selector is hidden in Processing mode, the two
// dividers (one above the selector, one above File Selection) would stack into
// two lines, so we keep the leading divider (right after Application Mode) and
// hide the trailing one. In every other case both dividers stay visible.
function updateProcessingModeSelectorVisibility() {
    const section = document.getElementById("processing-mode-section");
    if (!section) {
        return;
    }

    const inProcessing = !(globalState && globalState.feedbackMode);
    const showSelector = inProcessing && processingModeSelectorEnabled;
    section.style.display = showSelector ? "" : "none";

    if (!showSelector) {
        const dropdown = document.getElementById("processing-mode-dropdown");
        if (dropdown) {
            dropdown.value = "arrangement";
        }
    }

    // Leading divider (after Application Mode) is always visible.
    const leadingDivider = section.previousElementSibling;
    if (
        leadingDivider &&
        leadingDivider.classList.contains("section-divider")
    ) {
        leadingDivider.style.display = "";
    }

    // Trailing divider (above File Selection) is hidden only when the selector
    // is hidden in Processing mode, to avoid two stacked lines.
    const fileSelection = document.getElementById("file-selection-section");
    const trailingDivider = fileSelection
        ? fileSelection.previousElementSibling
        : null;
    if (
        trailingDivider &&
        trailingDivider.classList.contains("section-divider")
    ) {
        const hideTrailing = inProcessing && !processingModeSelectorEnabled;
        trailingDivider.style.display = hideTrailing ? "none" : "";
    }
}

// Initialize authentication when page loads
initializeAuthentication();

/**
 * Perform auto logout when no valid token is present
 */
function performAutoLogout() {
    console.log("🚪 Performing auto logout...");

    // Clear tokens using the unified function
    syncToken(null);

    // Clear any processing state
    clearAllFilesAndState();

    // Hide processing elements
    const processingIndicator = document.getElementById("processing-indicator");
    if (processingIndicator) {
        processingIndicator.style.display = "none";
    }

    // Show authentication section
    const authenticationSection = document.getElementById("authentication");
    if (authenticationSection) {
        authenticationSection.style.display = "block";
    }

    // Only redirect to login page if authentication is enabled
    if (isAuthenticationEnabled) {
        // Show logout notification only when authentication is enabled
        showToast(
            "Session expired - Please log in to continue",
            "exclamation-triangle"
        );

        // setTimeout(() => {
        //     window.location.href = "/login";
        // }, 1500); // Small delay to allow toast notification to be seen
    } else {
        console.log(
            "Authentication disabled - skipping login redirect and session expired message"
        );
    }

    // Reset logout state after a short delay
    setTimeout(() => {
        resetLogoutState();
    }, 2000);
}

// Set up the auto logout callback for the API service
setAutoLogoutCallback(performAutoLogout);

/**
 * Check if token exists and perform logout if not (only when authentication is enabled)
 */
function checkTokenAndLogout() {
    // Skip token checking if authentication is disabled
    if (!isAuthenticationEnabled) {
        console.log("🔓 Authentication disabled - skipping token check");
        return true;
    }

    const accessToken = getAccessToken();

    if (!accessToken) {
        console.log("🚪 No valid access token found - performing auto logout");
        performAutoLogout();
        return false;
    }

    return true;
}

window.addEventListener("load", () => {
    // Authentication is now handled by initializeAuthentication()
    // which is called immediately when the script loads

    // Sync access token between cookie and input field
    const accessToken = getAccessToken();
    if (accessToken) {
        syncToken(accessToken); // Ensure cookie and input field are synchronized
    }

    const copyTokenButton = document.getElementById("copy-token-button");
    copyTokenButton.addEventListener("click", () => {
        const accessTokenInput = document.getElementById("access-token");
        accessTokenInput.select();
        document.execCommand("copy");
        alert("Access token copied to clipboard");
    });

    // Add input mode change handler for toggle switch
    const inputModeToggle = document.getElementById("input-mode-toggle");
    if (inputModeToggle) {
        const toggleOptions =
            inputModeToggle.querySelectorAll(".toggle-option");
        toggleOptions.forEach((option) => {
            option.addEventListener("click", (e) => {
                const mode = e.target.getAttribute("data-mode");

                // Update active state
                toggleOptions.forEach((opt) => opt.classList.remove("active"));
                e.target.classList.add("active");

                // Update UI based on mode
                updateInputModeUI(mode);
                updateArrangementActionButtons();

                // Add animation class
                e.target.classList.add("fade-in");
                setTimeout(() => {
                    e.target.classList.remove("fade-in");
                }, 300);
            });
        });
    }

    // Add processing mode change handler for dropdown
    const processingModeDropdown = document.getElementById(
        "processing-mode-dropdown"
    );
    if (processingModeDropdown) {
        processingModeDropdown.addEventListener("change", async (e) => {
            const mode = e.target.value;
            const boundaryViews = [
                globalState.boundaryRenderer.domElement,
                globalState.boundaryLabelRenderer.domElement,
                globalState.simplifiedBoundaryRenderer.domElement,
                globalState.simplifiedBoundaryLabelRenderer.domElement
            ];

            // Update postprocessing section visibility based on mode
            updatePostprocessingSectionVisibility();

            // Update model processing options section visibility based on mode
            updateModelOptionsSectionVisibility(mode);

            // Reset postprocessing checkbox when switching away from arrangement
            if (mode !== "arrangement") {
                const enableCheckbox = document.getElementById(
                    "enable-postprocessing-checkbox"
                );
                if (enableCheckbox) {
                    enableCheckbox.checked = false;
                    const controlsContainer = document.getElementById(
                        "postprocessing-controls"
                    );
                    if (controlsContainer) {
                        controlsContainer.style.display = "none";
                    }
                }
            }

            // Ensure all previous processing results are cleared when switching modes
            clearAllFilesAndState();

            // Reset multi-view container if it exists
            resetMultiViewContainer();

            // Ensure the main renderer is visible again
            globalState.renderer.domElement.style.display = "block";
            globalState.labelRenderer.domElement.style.display = "block";

            // Reset any processing state variables
            globalState.renderMultiView = false;
            globalState.separationPhase = 0;
            globalState.lastInferenceResult = null;

            // Update file selection UI based on processing mode
            updateFileSelectionUI(mode);

            // Update instructions based on processing mode
            updateModeInstructions(mode);

            // Validate file type if file is already selected
            if (mode === "arrangement") {
                // For arrangement mode, validate both lower and upper jaw files
                const lowerJawFileInput =
                    document.getElementById("lower-jaw-file");
                const upperJawFileInput =
                    document.getElementById("upper-jaw-file");
                const lowerJawAzurePathInput = document.getElementById(
                    "lower-jaw-azure-path"
                );
                const upperJawAzurePathInput = document.getElementById(
                    "upper-jaw-azure-path"
                );

                const lowerFile = lowerJawFileInput?.files[0];
                const upperFile = upperJawFileInput?.files[0];
                const lowerAzurePath =
                    lowerJawAzurePathInput?.value.trim() || null;
                const upperAzurePath =
                    upperJawAzurePathInput?.value.trim() || null;

                // Validate lower jaw file
                if (lowerFile || lowerAzurePath) {
                    await validateFileTypeAndShowWarning(
                        lowerFile,
                        lowerAzurePath,
                        "arrangement",
                        null
                    );
                }

                // Validate upper jaw file
                if (upperFile || upperAzurePath) {
                    await validateFileTypeAndShowWarning(
                        upperFile,
                        upperAzurePath,
                        "arrangement",
                        null
                    );
                }
            } else if (
                mode === "teethml" ||
                mode === "defectml" ||
                mode === "keypointml" ||
                mode === "teethseparation"
            ) {
                const fileSelect = document.getElementById("file-select");
                const azurePathInput =
                    document.getElementById("azure-path-input");
                const file = fileSelect?.files[0];
                const azurePath = azurePathInput?.value.trim() || null;
                const jawSelection = getJawSelection();

                if (file || azurePath) {
                    // For teethseparation, use teethml validation (same requirements)
                    const modelForValidation =
                        mode === "teethseparation" ? "teethml" : mode;
                    await validateFileTypeAndShowWarning(
                        file,
                        azurePath,
                        modelForValidation,
                        jawSelection
                    );
                }
            }

            // Trigger window resize to properly layout views for the new mode
            setTimeout(() => {
                onWindowResize();
            }, 100);
        });
    }

    // Add app mode toggle handler for switching between processing and feedback modes
    const appModeToggle = document.getElementById("app-mode-toggle");
    if (appModeToggle) {
        const toggleOptions = appModeToggle.querySelectorAll(".toggle-option");
        toggleOptions.forEach((option) => {
            option.addEventListener("click", (e) => {
                const mode = e.target.getAttribute("data-mode");

                // Update active state
                toggleOptions.forEach((opt) => opt.classList.remove("active"));
                e.target.classList.add("active");

                // Switch between modes
                switchApplicationMode(mode);

                // Add animation class
                e.target.classList.add("fade-in");
                setTimeout(() => {
                    e.target.classList.remove("fade-in");
                }, 300);
            });
        });
    }

    // Add arrangement view mode toggle handler
    const arrangementViewModeToggle = document.getElementById(
        "arrangement-view-mode-toggle"
    );
    if (arrangementViewModeToggle) {
        const toggleOptions =
            arrangementViewModeToggle.querySelectorAll(".toggle-option");
        toggleOptions.forEach((option) => {
            option.addEventListener("click", (e) => {
                const mode = e.target.getAttribute("data-mode");

                // Update active state
                toggleOptions.forEach((opt) => opt.classList.remove("active"));
                e.target.classList.add("active");

                // Switch between dual and overlay view
                switchArrangementViewMode(mode);

                // Add animation class
                e.target.classList.add("fade-in");
                setTimeout(() => {
                    e.target.classList.remove("fade-in");
                }, 300);
            });
        });
    }

    // Add opacity slider event listeners for overlay mode
    const originalOpacitySlider = document.getElementById(
        "original-opacity-slider"
    );
    const transformedOpacitySlider = document.getElementById(
        "transformed-opacity-slider"
    );
    const originalOpacityValue = document.getElementById(
        "original-opacity-value"
    );
    const transformedOpacityValue = document.getElementById(
        "transformed-opacity-value"
    );

    if (originalOpacitySlider) {
        originalOpacitySlider.addEventListener("input", (e) => {
            const opacity = e.target.value / 100;
            originalOpacityValue.textContent = `${e.target.value}%`;

            if (globalState.overlayOriginalMeshes) {
                globalState.overlayOriginalMeshes.forEach((mesh) => {
                    if (mesh.material) {
                        mesh.material.opacity = opacity;
                    }
                });
            }
        });
    }

    if (transformedOpacitySlider) {
        transformedOpacitySlider.addEventListener("input", (e) => {
            const opacity = e.target.value / 100;
            transformedOpacityValue.textContent = `${e.target.value}%`;

            if (globalState.overlayTransformedMeshes) {
                globalState.overlayTransformedMeshes.forEach((mesh) => {
                    if (mesh.material) {
                        mesh.material.opacity = opacity;
                    }
                });
            }
        });
    }

    // Add feedback navigation handlers
    const prevFeedbackBtn = document.getElementById("prev-feedback-btn");
    const nextFeedbackBtn = document.getElementById("next-feedback-btn");
    const loadFeedbackDirectoryBtn = document.getElementById(
        "load-feedback-directory"
    );

    if (prevFeedbackBtn) {
        prevFeedbackBtn.addEventListener("click", () => {
            navigateFeedback(-1);
        });
    }

    if (nextFeedbackBtn) {
        nextFeedbackBtn.addEventListener("click", () => {
            navigateFeedback(1);
        });
    }

    if (loadFeedbackDirectoryBtn) {
        loadFeedbackDirectoryBtn.addEventListener("click", () => {
            handleFeedbackDirectorySelection();
        });
    }

    // Add feedback details panel toggle
    const toggleFeedbackDetails = document.getElementById(
        "toggle-feedback-details"
    );
    if (toggleFeedbackDetails) {
        toggleFeedbackDetails.addEventListener("click", () => {
            toggleFeedbackDetailsPanel();
        });
    }

    // Add keyboard navigation for feedback mode
    document.addEventListener("keydown", (event) => {
        if (!globalState.feedbackMode || globalState.feedbackData.length === 0)
            return;

        switch (event.key) {
            case "ArrowLeft":
                event.preventDefault();
                navigateFeedback(-1);
                break;
            case "ArrowRight":
                event.preventDefault();
                navigateFeedback(1);
                break;
        }
    });

    // Add next phase button to the body for the teeth separation workflow
    const nextPhaseButton = document.createElement("button");
    nextPhaseButton.id = "next-phase-button";
    nextPhaseButton.textContent = "Continue to Separation";
    nextPhaseButton.addEventListener("click", proceedToNextPhase);
    document.body.appendChild(nextPhaseButton);

    // Add download InfoJSON button (shown directly when the review gate is off)
    const downloadInfoJsonButton = document.createElement("button");
    downloadInfoJsonButton.id = "download-infojson-button";
    downloadInfoJsonButton.textContent = "Download InfoJSON";
    downloadInfoJsonButton.addEventListener("click", downloadInfoJson);
    document.body.appendChild(downloadInfoJsonButton);

    // Add Accept / Reject buttons (shown instead of the plain download when the
    // review gate is on). Accept logs a compliance acceptance then downloads;
    // Reject requires a reason and logs it. See applyInfoJsonReviewGate.
    const acceptInfoJsonButton = document.createElement("button");
    acceptInfoJsonButton.id = "accept-infojson-button";
    acceptInfoJsonButton.textContent = "Accept & Download InfoJSON";
    acceptInfoJsonButton.addEventListener("click", acceptInfoJson);
    document.body.appendChild(acceptInfoJsonButton);

    const rejectInfoJsonButton = document.createElement("button");
    rejectInfoJsonButton.id = "reject-infojson-button";
    rejectInfoJsonButton.textContent = "Reject";
    rejectInfoJsonButton.addEventListener("click", rejectInfoJson);
    document.body.appendChild(rejectInfoJsonButton);

    // Setup displacement table button and modal
    const displacementTableButton = document.getElementById(
        "displacement-table-button"
    );
    if (displacementTableButton) {
        displacementTableButton.addEventListener(
            "click",
            showDisplacementTable
        );
    }

    const closeDisplacementModal = document.getElementById(
        "close-displacement-modal"
    );
    if (closeDisplacementModal) {
        closeDisplacementModal.addEventListener("click", () => {
            const modal = document.getElementById("displacement-table-modal");
            if (modal) {
                modal.style.display = "none";
            }
        });
    }

    // Close modal when clicking outside of it
    const displacementTableModal = document.getElementById(
        "displacement-table-modal"
    );
    if (displacementTableModal) {
        displacementTableModal.addEventListener("click", (event) => {
            if (event.target === displacementTableModal) {
                displacementTableModal.style.display = "none";
            }
        });
    }

    // Setup file input event listeners
    setupFileInputListeners();

    // Add jaw selection change handler for buttons
    const jawSelectionOptions = document.getElementById(
        "jaw-selection-options"
    );
    if (jawSelectionOptions) {
        const jawButtons = jawSelectionOptions.querySelectorAll(".jaw-button");
        jawButtons.forEach((button) => {
            button.addEventListener("click", (e) => {
                const jaw = e.target.getAttribute("data-jaw");

                // Update active state
                jawButtons.forEach((btn) => btn.classList.remove("active"));
                e.target.classList.add("active");

                // Add animation class
                e.target.classList.add("slide-in");
                setTimeout(() => {
                    e.target.classList.remove("slide-in");
                }, 300);
            });
        });
    }

    // Initialize file selection UI based on default processing mode
    const defaultMode =
        document.getElementById("processing-mode-dropdown")?.value ||
        "arrangement";
    console.log("Default mode:", defaultMode);
    updateFileSelectionUI(defaultMode);
    updateModeInstructions(defaultMode);

    // Ensure postprocessing section is visible on initial page load
    // Use setTimeout to ensure all DOM elements are fully initialized
    // Call after updateFileSelectionUI to ensure it runs last
    setTimeout(() => {
        updatePostprocessingSectionVisibility();
        updateModelOptionsSectionVisibility(defaultMode);
        // Double-check after a short delay to ensure visibility is set
        setTimeout(() => {
            const postprocessingSection = document.getElementById(
                "postprocessing-section"
            );
            if (
                postprocessingSection &&
                defaultMode === "arrangement" &&
                !globalState.feedbackMode
            ) {
                postprocessingSection.style.display = "block";
                postprocessingSection.style.setProperty(
                    "display",
                    "block",
                    "important"
                );
            }
        }, 50);
    }, 150);

    // Apply correct view layout for the default mode
    onWindowResize();
});

/**
 * Get the current input mode from the toggle switch
 * @returns {string} The current input mode ('file' or 'azure')
 */
function getInputMode() {
    const activeToggle = document.querySelector(
        "#input-mode-toggle .toggle-option.active"
    );
    return activeToggle ? activeToggle.getAttribute("data-mode") : "file";
}

/**
 * Whether the current input mode reads from Azure rather than local file uploads.
 * In Azure mode the user gives one folder path (arrangement) or a single blob
 * path (other modes); the resolved per-file azure fields feed the same
 * submit/preview chain.
 * @returns {boolean}
 */
function isAzureInputMode() {
    return getInputMode() === "azure";
}

// Scroll a text input so the END of a long value is visible (the file name at
// the end of an azure:// path, not the container prefix at the start).
function scrollInputToEnd(el) {
    if (!el) return;
    if (el.value) {
        el.title = el.value;
    } else {
        el.removeAttribute("title");
    }
    const apply = () => {
        el.scrollLeft = el.scrollWidth;
    };
    apply();
    // Browsers reset an input's horizontal scroll to the start AFTER the blur
    // event is handled, so a synchronous set gets clobbered. Re-apply on the next
    // frame (once the field is unfocused) to keep the end pinned in view.
    requestAnimationFrame(apply);
}

// Show or hide the per-file Azure path fields based on the "Override individual
// file paths" checkbox. Only meaningful in Azure + arrangement mode.
function updateAzureOverrideVisibility() {
    const checkbox = document.getElementById("override-azure-paths-checkbox");
    const pairedAzurePath = document.getElementById("paired-azure-path");
    if (!pairedAzurePath) return;
    const show = isAzureInputMode() && checkbox && checkbox.checked;
    pairedAzurePath.style.display = show ? "block" : "none";
    if (show) {
        [
            "lower-jaw-azure-path",
            "upper-jaw-azure-path",
            "info-json-azure-path",
            "landmarks-csv-azure-path"
        ].forEach((id) => scrollInputToEnd(document.getElementById(id)));
    }
}

/**
 * Get the current jaw selection from the buttons
 * @returns {string} The current jaw selection ('upper' or 'lower')
 */
function getJawSelection() {
    const activeButton = document.querySelector(
        "#jaw-selection-options .jaw-button.active"
    );
    return activeButton ? activeButton.getAttribute("data-jaw") : "upper";
}

/**
 * Update mode instructions based on selected processing mode
 * @param {string} mode - The selected processing mode
 */
function updateModeInstructions(mode) {
    const instructionsElement = document.getElementById("mode-instructions");
    if (!instructionsElement) return;

    const instructions = {
        teethml:
            "<strong>Teeth Segmentation:</strong> Use prepared .obj files (with bottom) for best results.",
        defectml:
            "<strong>Defect Segmentation:</strong> Use raw files (.stl, without bottom) for optimal detection.",
        keypointml:
            "<strong>Keypoint Detection:</strong> Use separated objects (.obj files after segmentation).",
        teethseparation:
            "<strong>Teeth Reconstruction:</strong> Use prepared .obj files (with bottom) for reconstruction.",
        arrangement:
            "<strong>Teeth Arrangement:</strong> Use separated objects (.obj files) for both upper and lower jaws."
    };

    instructionsElement.innerHTML =
        instructions[mode] || instructions.arrangement;
    instructionsElement.classList.add("fade-in");
    setTimeout(() => {
        instructionsElement.classList.remove("fade-in");
    }, 300);
}

/**
 * Update input mode UI (file upload vs Azure path)
 * @param {string} mode - The selected input mode ('file' or 'azure')
 */
function updateInputModeUI(mode) {
    // Update single file selection sections
    const singleFileUpload = document.getElementById("single-file-upload");
    const singleAzurePath = document.getElementById("single-azure-path");

    // Update paired file selection sections
    const pairedFileUpload = document.getElementById("paired-file-upload");
    const pairedAzureFolder = document.getElementById("paired-azure-folder");

    if (mode === "azure") {
        // Azure mode: single blob path (non-arrangement) or the folder input
        // (arrangement). The per-file path fields are shown only when the
        // "Override individual file paths" checkbox is on (handled below).
        if (singleFileUpload) singleFileUpload.style.display = "none";
        if (singleAzurePath) singleAzurePath.style.display = "block";
        if (pairedFileUpload) pairedFileUpload.style.display = "none";
        if (pairedAzureFolder) pairedAzureFolder.style.display = "block";
    } else {
        // File upload mode.
        if (singleFileUpload) singleFileUpload.style.display = "block";
        if (singleAzurePath) singleAzurePath.style.display = "none";
        if (pairedFileUpload) pairedFileUpload.style.display = "block";
        if (pairedAzureFolder) pairedAzureFolder.style.display = "none";
    }
    // Per-file paths visibility is driven by the override checkbox.
    updateAzureOverrideVisibility();
}

// Write the resolved azure:// references (or "") into the hidden per-file azure
// path inputs that the arrangement submit/preview paths already read.
function setArrangementAzureFields(resolved) {
    const set = (id, value) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.value = value || "";
        // Keep the file name (end of the long path) in view, not the prefix.
        scrollInputToEnd(el);
    };
    set("lower-jaw-azure-path", resolved ? resolved.lower : "");
    set("upper-jaw-azure-path", resolved ? resolved.upper : "");
    set("info-json-azure-path", resolved ? resolved.info : "");
    set("landmarks-csv-azure-path", resolved ? resolved.landmarks : "");
}

// Clear the folder resolution: empties the resolved azure fields and the
// preview/error displays so a stale/partial set can never be submitted.
function resetArrangementFolderResolution() {
    setArrangementAzureFields(null);
    const preview = document.getElementById("folder-resolve-preview");
    const error = document.getElementById("folder-resolve-error");
    if (preview) {
        preview.textContent = "";
        preview.style.display = "none";
    }
    if (error) {
        error.textContent = "";
        error.style.display = "none";
    }
    // Collapse the per-file override back to hidden.
    const checkbox = document.getElementById("override-azure-paths-checkbox");
    if (checkbox) checkbox.checked = false;
    updateAzureOverrideVisibility();
    updateArrangementActionButtons();
}

// Resolve one Azure folder into the 4 arrangement files: list the folder, match
// files by naming convention, populate the per-file Azure path fields, and warn
// about anything not resolved automatically. Whatever resolves is filled in; the
// fields stay editable so the user can correct or enter the rest by hand.
async function resolveArrangementFolder(folderPath) {
    const preview = document.getElementById("folder-resolve-preview");
    const error = document.getElementById("folder-resolve-error");
    const setPreview = (html) => {
        if (!preview) return;
        preview.innerHTML = html || "";
        preview.style.display = html ? "block" : "none";
    };
    const setError = (msg) => {
        if (!error) return;
        error.textContent = msg || "";
        error.style.display = msg ? "block" : "none";
    };

    let response;
    try {
        response = await ApiService.listAzureFolder(folderPath);
    } catch (e) {
        const detail =
            e?.response?.data?.detail ||
            e?.message ||
            "Failed to list Azure folder";
        setPreview("");
        setError(`Could not read folder: ${detail}`);
        setArrangementAzureFields(null);
        updateArrangementActionButtons();
        return;
    }

    const { resolved, errors, unmatched } = classifyArrangementFolderFiles(
        response.files || []
    );

    // Populate whatever resolved. Unresolved roles become empty, editable fields.
    setArrangementAzureFields(resolved);

    const baseName = (ref) => (ref ? String(ref).split("/").pop() : null);
    const esc = (s) =>
        String(s).replace(
            /[&<>"]/g,
            (c) =>
                ({
                    "&": "&amp;",
                    "<": "&lt;",
                    ">": "&gt;",
                    '"': "&quot;"
                })[c]
        );
    // Middle-ellipsis: split the name into a shrinkable head and a pinned tail so
    // the distinguishing suffix (L/U role + extension) is ALWAYS visible while the
    // redundant leading id gets the ellipsis. Done in markup (two flex cells)
    // rather than CSS direction:rtl, which reorders names that start with digits.
    const midName = (name, tailLen) => {
        const n = String(name);
        if (n.length <= tailLen + 6) {
            return `<span class="fn-head">${esc(n)}</span>`;
        }
        return (
            `<span class="fn-head">${esc(
                n.slice(0, n.length - tailLen)
            )}</span>` +
            `<span class="fn-tail">${esc(n.slice(-tailLen))}</span>`
        );
    };
    // One row per role: status icon + label on top, file name (middle-ellipsis)
    // full-width below. Required jaws that did not resolve are flagged so the user
    // knows to fill them in manually.
    const roleRow = (label, name, required) => {
        if (name) {
            return (
                `<div class="resolve-row ok">` +
                `<div class="resolve-head"><span class="ic">✓</span>` +
                `<span class="role">${label}</span></div>` +
                `<div class="fname" title="${esc(name)}">${midName(
                    name,
                    18
                )}</div>` +
                `</div>`
            );
        }
        const cls = required ? "missing" : "none";
        const text = required ? "not found - enter manually" : "none";
        const icon = required ? "⚠" : "–";
        return (
            `<div class="resolve-row ${cls}">` +
            `<div class="resolve-head"><span class="ic">${icon}</span>` +
            `<span class="role">${label}</span></div>` +
            `<div class="fname placeholder">${text}</div>` +
            `</div>`
        );
    };
    let html =
        roleRow("Lower jaw", baseName(resolved.lower), true) +
        roleRow("Upper jaw", baseName(resolved.upper), true) +
        roleRow("Info JSON", baseName(resolved.info), false) +
        roleRow("Landmarks", baseName(resolved.landmarks), false);
    if (unmatched.length > 0) {
        html +=
            `<details class="resolve-ignored">` +
            `<summary>${unmatched.length} file(s) ignored</summary>` +
            `<ul>${unmatched
                .map((n) => `<li title="${esc(n)}">${midName(n, 16)}</li>`)
                .join("")}</ul>` +
            `</details>`;
    }
    setPreview(html);

    if (errors.length > 0) {
        setError(
            "Some files could not be selected automatically - tick 'Override " +
                "individual file paths' and enter them manually:\n" +
                errors.join("\n")
        );
        // Auto-reveal the per-file paths so the user can fix the unresolved ones.
        const checkbox = document.getElementById(
            "override-azure-paths-checkbox"
        );
        if (checkbox) checkbox.checked = true;
    } else {
        setError("");
    }
    updateAzureOverrideVisibility();

    updateArrangementActionButtons();
    checkAndLoadArrangementFiles();
}

/**
 * Update file selection UI based on processing mode
 * @param {string} mode - The selected processing mode
 */
function updateFileSelectionUI(mode) {
    const singleFileSelection = document.getElementById(
        "single-file-selection"
    );
    const pairedFileSelection = document.getElementById(
        "paired-file-selection"
    );
    const jawSelectionSection = document.getElementById(
        "jaw-selection-section"
    );
    const jawSelectionOptions = document.getElementById(
        "jaw-selection-options"
    );

    if (mode === "arrangement") {
        // Show paired file selection for arrangement
        singleFileSelection.style.display = "none";
        pairedFileSelection.style.display = "block";
        jawSelectionSection.style.display = "none";
        jawSelectionOptions.style.display = "none";
    } else {
        // Show single file selection for other modes
        singleFileSelection.style.display = "block";
        pairedFileSelection.style.display = "none";
        jawSelectionSection.style.display = "block";
        jawSelectionOptions.style.display = "block";

        // Hide view mode toggle for non-arrangement modes
        const viewModeContainer = document.getElementById(
            "arrangement-view-mode-container"
        );
        if (viewModeContainer) {
            viewModeContainer.style.display = "none";
        }
    }

    // The Azure folder input (top of Azure mode) is arrangement-only; the paired
    // container that holds it is already shown only for arrangement, so no extra
    // handling is needed here. Re-sync per-file path visibility for the mode.
    updateAzureOverrideVisibility();

    // Update visibility controls based on processing mode
    updateVisibilityControlsForMode(mode);

    // Update keypoint labels checkbox based on processing mode
    // Disable it initially for all modes (will be enabled after inference for keypoint mode)
    disableKeypointLabelsCheckbox();

    // Update arrangement labels checkbox based on processing mode
    // Only enable for arrangement mode
    if (mode === "arrangement") {
        disableArrangementLabelsCheckbox(); // Disabled until inference completes
    } else {
        disableArrangementLabelsCheckbox(); // Disabled for all other modes
    }

    // Also update input mode UI to ensure correct display
    const currentInputMode = getInputMode();
    updateInputModeUI(currentInputMode);
}

/**
 * Update visibility controls (Gingiva, Brackets) based on processing mode
 * These controls should only be visible in arrangement mode
 * @param {string} mode - The selected processing mode
 */
function updateVisibilityControlsForMode(mode) {
    const visibilityContainer = document.querySelector(
        ".visibility-options-container"
    );

    if (!visibilityContainer) {
        return;
    }

    if (mode === "arrangement") {
        // Show visibility controls in arrangement mode
        visibilityContainer.style.display = "block";
    } else {
        // Hide visibility controls in other modes
        visibilityContainer.style.display = "none";
    }
}

/**
 * Whether segmentation should auto-orient the scan before inference.
 * @returns {boolean}
 */
function isAutoOrientationEnabled() {
    if (!autoOrientationEnabled) {
        return false;
    }
    const checkbox = document.getElementById(
        "enable-auto-orientation-checkbox"
    );
    return checkbox ? checkbox.checked : true;
}

/**
 * Initialize the auto orientation checkbox from localStorage.
 */
function initializeAutoOrientation() {
    const checkbox = document.getElementById(
        "enable-auto-orientation-checkbox"
    );
    if (!checkbox) {
        console.warn("Auto orientation checkbox not found");
        return;
    }

    // Load saved value from localStorage, default to true (checked)
    const savedValue = localStorage.getItem(STORAGE_KEY_AUTO_ORIENTATION);
    if (!autoOrientationEnabled) {
        // Killed deployment-wide: ignore any saved opt-in, nothing will rotate.
        checkbox.checked = false;
    } else if (savedValue !== null) {
        checkbox.checked = savedValue === "true";
        console.log(
            `[AutoOrientation] Loaded from localStorage: ${checkbox.checked}`
        );
    } else {
        checkbox.checked = true;
        console.log("[AutoOrientation] Using default: true");
    }

    // Add change listener to save to localStorage
    checkbox.addEventListener("change", function () {
        localStorage.setItem(
            STORAGE_KEY_AUTO_ORIENTATION,
            this.checked.toString()
        );
        console.log(`[AutoOrientation] Saved to localStorage: ${this.checked}`);
    });
}

/**
 * Update model processing options section visibility based on processing mode
 * Model Processing Options (Gingiva Movement Estimation) should only be visible in arrangement mode
 * @param {string} mode - The selected processing mode
 */
function updateModelOptionsSectionVisibility(mode) {
    const modelOptionsSection = document.getElementById(
        "model-options-section"
    );
    const segmentationOptionsSection = document.getElementById(
        "segmentation-options-section"
    );

    if (modelOptionsSection) {
        modelOptionsSection.style.display =
            mode === "arrangement" ? "block" : "none";
    }
    if (segmentationOptionsSection) {
        segmentationOptionsSection.style.display =
            mode === "teethml" && autoOrientationEnabled ? "block" : "none";
    }
}

/**
 * Setup event listeners for file inputs
 */
// Function to validate file type and show warning if needed
async function validateFileTypeAndShowWarning(file, azurePath, model, jaw) {
    if (!file && !azurePath) {
        return;
    }

    try {
        const validationResponse = await ApiService.validateFileType(
            file,
            azurePath,
            model,
            jaw
        );

        if (validationResponse && validationResponse.warning) {
            showToast(validationResponse.warning, "exclamation-triangle", 5000);
        }
    } catch (error) {
        console.warn("Failed to validate file type:", error);
        // Don't block file selection if validation fails
    }
}

function setupFileInputListeners() {
    // Single file input listener (existing)
    const fileSelect = document.getElementById("file-select");
    if (fileSelect) {
        fileSelect.addEventListener("change", async (event) => {
            const file = event.target.files[0];
            const fileNameDiv = document.getElementById("file-name");
            if (file && fileNameDiv) {
                fileNameDiv.textContent = file.name;
                // Clear previous inference results when selecting a new file
                clearInferenceResults();

                // Validate file type for current model
                const processingMode =
                    document.getElementById("processing-mode-dropdown")
                        ?.value || "arrangement";
                const jawSelection = getJawSelection();

                // Only validate for non-arrangement modes
                if (
                    processingMode !== "arrangement" &&
                    (processingMode === "teethml" ||
                        processingMode === "defectml" ||
                        processingMode === "keypointml" ||
                        processingMode === "teethseparation")
                ) {
                    // For teethseparation, use teethml validation (same requirements)
                    const modelForValidation =
                        processingMode === "teethseparation"
                            ? "teethml"
                            : processingMode;
                    await validateFileTypeAndShowWarning(
                        file,
                        null,
                        modelForValidation,
                        jawSelection
                    );
                }
            }
        });
    }

    // Lower jaw file input listener
    const lowerJawFile = document.getElementById("lower-jaw-file");
    if (lowerJawFile) {
        lowerJawFile.addEventListener("change", async (event) => {
            const file = event.target.files[0];
            const fileNameDiv = document.getElementById("lower-jaw-file-name");
            if (file) {
                if (file.name.toLowerCase().endsWith(".obj")) {
                    fileNameDiv.textContent = file.name;
                    fileNameDiv.style.color = "#666";
                } else {
                    fileNameDiv.textContent = "Please select a .obj file";
                    fileNameDiv.style.color = "#ff6b6b";
                }
                // Clear previous inference results when selecting a new file
                clearInferenceResults();

                // Validate file type for arrangement (requires RECONSTRUCTED files)
                const processingMode =
                    document.getElementById("processing-mode-dropdown")
                        ?.value || "arrangement";
                if (processingMode === "arrangement") {
                    await validateFileTypeAndShowWarning(
                        file,
                        null,
                        "arrangement",
                        null
                    );
                }

                // Check if both files are now selected and load them
                checkAndLoadArrangementFiles();
            } else if (fileNameDiv) {
                fileNameDiv.textContent = "";
                clearInferenceResults();
                checkAndLoadArrangementFiles();
            }
        });
    }

    // Upper jaw file input listener
    const upperJawFile = document.getElementById("upper-jaw-file");
    if (upperJawFile) {
        upperJawFile.addEventListener("change", async (event) => {
            const file = event.target.files[0];
            const fileNameDiv = document.getElementById("upper-jaw-file-name");
            if (file) {
                if (file.name.toLowerCase().endsWith(".obj")) {
                    fileNameDiv.textContent = file.name;
                    fileNameDiv.style.color = "#666";
                } else {
                    fileNameDiv.textContent = "Please select a .obj file";
                    fileNameDiv.style.color = "#ff6b6b";
                }
                // Clear previous inference results when selecting a new file
                clearInferenceResults();

                // Validate file type for arrangement (requires RECONSTRUCTED files)
                const processingMode =
                    document.getElementById("processing-mode-dropdown")
                        ?.value || "arrangement";
                if (processingMode === "arrangement") {
                    await validateFileTypeAndShowWarning(
                        file,
                        null,
                        "arrangement",
                        null
                    );
                }

                // Check if both files are now selected and load them
                checkAndLoadArrangementFiles();
            } else if (fileNameDiv) {
                fileNameDiv.textContent = "";
                clearInferenceResults();
                checkAndLoadArrangementFiles();
            }
        });
    }

    // InfoJSON file input listener (optional)
    const infoJsonFile = document.getElementById("info-json-file");
    if (infoJsonFile) {
        infoJsonFile.addEventListener("change", (event) => {
            const file = event.target.files[0];
            const fileNameDiv = document.getElementById("info-json-file-name");
            if (file) {
                if (file.name.toLowerCase().endsWith(".json")) {
                    fileNameDiv.textContent = file.name;
                    fileNameDiv.style.color = "#666";
                } else {
                    fileNameDiv.textContent = "Please select a .json file";
                    fileNameDiv.style.color = "#ff6b6b";
                }
                // InfoJSON is optional, so we still check and load files
                checkAndLoadArrangementFiles();
            } else {
                fileNameDiv.textContent = "";
            }
        });
    }

    // Landmarks CSV file input listener (optional)
    const landmarksCsvFile = document.getElementById("landmarks-csv-file");
    if (landmarksCsvFile) {
        landmarksCsvFile.addEventListener("change", (event) => {
            const file = event.target.files[0];
            const fileNameDiv = document.getElementById(
                "landmarks-csv-file-name"
            );
            if (file) {
                if (file.name.toLowerCase().endsWith(".csv")) {
                    fileNameDiv.textContent = file.name;
                    fileNameDiv.style.color = "#666";
                } else {
                    fileNameDiv.textContent = "Please select a .csv file";
                    fileNameDiv.style.color = "#ff6b6b";
                }
                // Landmarks CSV is optional, so we still check and load files
                checkAndLoadArrangementFiles();
            } else {
                fileNameDiv.textContent = "";
            }
        });
    }

    // Azure path input listener for single file mode
    const azurePathInput = document.getElementById("azure-path-input");
    if (azurePathInput) {
        let azurePathDebounceTimeout;
        azurePathInput.addEventListener("input", async () => {
            const azurePath = azurePathInput.value.trim();
            // Clear previous timeout
            clearTimeout(azurePathDebounceTimeout);

            if (azurePath) {
                // Debounce validation to avoid too many requests while typing
                azurePathDebounceTimeout = setTimeout(async () => {
                    const processingMode =
                        document.getElementById("processing-mode-dropdown")
                            ?.value || "arrangement";
                    const jawSelection = getJawSelection();

                    // Only validate for non-arrangement modes
                    if (
                        processingMode !== "arrangement" &&
                        (processingMode === "teethml" ||
                            processingMode === "defectml" ||
                            processingMode === "keypointml" ||
                            processingMode === "teethseparation")
                    ) {
                        // For teethseparation, use teethml validation (same requirements)
                        const modelForValidation =
                            processingMode === "teethseparation"
                                ? "teethml"
                                : processingMode;
                        await validateFileTypeAndShowWarning(
                            null,
                            azurePath,
                            modelForValidation,
                            jawSelection
                        );
                    }
                }, 1000); // Wait 1 second after user stops typing
            }
        });
    }

    // Azure path input listeners for arrangement
    const lowerJawAzurePath = document.getElementById("lower-jaw-azure-path");
    if (lowerJawAzurePath) {
        let lowerJawAzurePathDebounceTimeout;
        lowerJawAzurePath.addEventListener("input", async () => {
            const azurePath = lowerJawAzurePath.value.trim();
            // Clear previous inference results when path changes
            clearInferenceResults();
            // Clear previous timeout
            clearTimeout(lowerJawAzurePathDebounceTimeout);

            if (azurePath) {
                // Debounce validation to avoid too many requests while typing
                lowerJawAzurePathDebounceTimeout = setTimeout(async () => {
                    const processingMode =
                        document.getElementById("processing-mode-dropdown")
                            ?.value || "arrangement";
                    if (processingMode === "arrangement") {
                        await validateFileTypeAndShowWarning(
                            null,
                            azurePath,
                            "arrangement",
                            null
                        );
                    }
                    checkAndLoadArrangementFiles();
                }, 1000); // Wait 1 second after user stops typing
            } else {
                // If path is cleared, still check and load files
                lowerJawAzurePathDebounceTimeout = setTimeout(() => {
                    checkAndLoadArrangementFiles();
                }, 500);
            }
        });
    }

    const upperJawAzurePath = document.getElementById("upper-jaw-azure-path");
    if (upperJawAzurePath) {
        let upperJawAzurePathDebounceTimeout;
        upperJawAzurePath.addEventListener("input", async () => {
            const azurePath = upperJawAzurePath.value.trim();
            // Clear previous inference results when path changes
            clearInferenceResults();
            // Clear previous timeout
            clearTimeout(upperJawAzurePathDebounceTimeout);

            if (azurePath) {
                // Debounce validation to avoid too many requests while typing
                upperJawAzurePathDebounceTimeout = setTimeout(async () => {
                    const processingMode =
                        document.getElementById("processing-mode-dropdown")
                            ?.value || "arrangement";
                    if (processingMode === "arrangement") {
                        await validateFileTypeAndShowWarning(
                            null,
                            azurePath,
                            "arrangement",
                            null
                        );
                    }
                    checkAndLoadArrangementFiles();
                }, 1000); // Wait 1 second after user stops typing
            } else {
                // If path is cleared, still check and load files
                upperJawAzurePathDebounceTimeout = setTimeout(() => {
                    checkAndLoadArrangementFiles();
                }, 500);
            }
        });
    }

    // Azure folder mode (arrangement only): resolve the 4 files from one folder.
    const arrangementFolderInput = document.getElementById(
        "arrangement-folder-path"
    );
    if (arrangementFolderInput) {
        let folderDebounceTimeout;
        arrangementFolderInput.addEventListener("input", () => {
            clearInferenceResults();
            clearTimeout(folderDebounceTimeout);
            const folderPath = arrangementFolderInput.value.trim();
            if (!folderPath) {
                resetArrangementFolderResolution();
                return;
            }
            // Debounce so we do not list on every keystroke.
            folderDebounceTimeout = setTimeout(() => {
                resolveArrangementFolder(folderPath);
            }, 1000);
        });
    }
    const resolveFolderBtn = document.getElementById("resolve-folder-btn");
    if (resolveFolderBtn) {
        resolveFolderBtn.addEventListener("click", () => {
            const folderPath = (
                document.getElementById("arrangement-folder-path")?.value || ""
            ).trim();
            if (!folderPath) {
                resetArrangementFolderResolution();
                return;
            }
            resolveArrangementFolder(folderPath);
        });
    }

    // "Override individual file paths" checkbox reveals/hides the per-file inputs.
    const overrideCheckbox = document.getElementById(
        "override-azure-paths-checkbox"
    );
    if (overrideCheckbox) {
        overrideCheckbox.addEventListener("change", () => {
            updateAzureOverrideVisibility();
        });
    }

    // Keep the end (file name) of long Azure paths visible when editing stops.
    [
        "arrangement-folder-path",
        "lower-jaw-azure-path",
        "upper-jaw-azure-path",
        "info-json-azure-path",
        "landmarks-csv-azure-path"
    ].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.addEventListener("blur", () => scrollInputToEnd(el));
    });
}

init();
animate();

// Note: Postprocessing functions moved to postprocessing.js module

/**
 * Initialize gingiva estimation checkbox from localStorage
 */
function initializeGingivaEstimation() {
    const gingivaCheckbox = document.getElementById(
        "enable-gingiva-estimation-checkbox"
    );
    if (!gingivaCheckbox) {
        console.warn("Gingiva estimation checkbox not found");
        return;
    }

    // Load saved value from localStorage, default to true (checked)
    const savedValue = localStorage.getItem(STORAGE_KEY_GINGIVA_ESTIMATION);
    if (savedValue !== null) {
        gingivaCheckbox.checked = savedValue === "true";
        console.log(
            `[GingivaEstimation] Loaded from localStorage: ${gingivaCheckbox.checked}`
        );
    } else {
        gingivaCheckbox.checked = true;
        console.log("[GingivaEstimation] Using default: true");
    }

    // Add change listener to save to localStorage
    gingivaCheckbox.addEventListener("change", function () {
        localStorage.setItem(
            STORAGE_KEY_GINGIVA_ESTIMATION,
            this.checked.toString()
        );
        console.log(
            `[GingivaEstimation] Saved to localStorage: ${this.checked}`
        );
    });
}

/**
 * Initialize calculate space/intersection checkbox from localStorage
 */
function initializeCalculateSpaceIntersection() {
    const checkbox = document.getElementById(
        "calculate-space-intersection-checkbox"
    );
    if (!checkbox) {
        console.warn("Calculate space/intersection checkbox not found");
        return;
    }

    // Load saved value from localStorage, default to true (checked)
    const savedValue = localStorage.getItem(
        STORAGE_KEY_CALCULATE_SPACE_INTERSECTION
    );
    if (savedValue !== null && spaceIntersectionToggleEnabled) {
        checkbox.checked = savedValue === "true";
        console.log(
            `[CalculateSpaceIntersection] Loaded from localStorage: ${checkbox.checked}`
        );
    } else if (!spaceIntersectionToggleEnabled) {
        // Toggle hidden: computation always on, ignore any saved opt-out.
        checkbox.checked = true;
    } else {
        checkbox.checked = true;
        console.log("[CalculateSpaceIntersection] Using default: true");
    }

    // Add change listener to save to localStorage
    checkbox.addEventListener("change", function () {
        localStorage.setItem(
            STORAGE_KEY_CALCULATE_SPACE_INTERSECTION,
            this.checked.toString()
        );
        console.log(
            `[CalculateSpaceIntersection] Saved to localStorage: ${this.checked}`
        );
    });
}

function init() {
    // Main view setup
    globalState.scene = new THREE.Scene();
    const mainViewer = document.getElementById("main-viewer");
    const viewerRect = mainViewer.getBoundingClientRect();
    const aspect = viewerRect.width / viewerRect.height;

    globalState.camera = createOrthographicCamera(aspect);
    globalState.renderer = new THREE.WebGLRenderer({ antialias: true });
    globalState.renderer.setSize(viewerRect.width, viewerRect.height);
    mainViewer.appendChild(globalState.renderer.domElement);

    // Label renderer setup for main view
    globalState.labelRenderer = new CSS2DRenderer();
    globalState.labelRenderer.setSize(viewerRect.width, viewerRect.height);
    globalState.labelRenderer.domElement.style.position = "absolute";
    globalState.labelRenderer.domElement.style.top = "0";
    globalState.labelRenderer.domElement.style.left = "0";
    mainViewer.appendChild(globalState.labelRenderer.domElement);

    // Boundary view setup
    globalState.boundaryScene = new THREE.Scene();
    const boundaryAspect = window.innerWidth / 2 / (window.innerHeight / 2);
    globalState.boundaryCamera = createOrthographicCamera(boundaryAspect);
    globalState.boundaryRenderer = new THREE.WebGLRenderer({ antialias: true });
    globalState.boundaryRenderer.setSize(
        window.innerWidth / 2,
        window.innerHeight / 2
    );
    globalState.boundaryRenderer.domElement.style.position = "absolute";
    globalState.boundaryRenderer.domElement.style.right = "0";
    globalState.boundaryRenderer.domElement.style.top = "0";
    document.body.appendChild(globalState.boundaryRenderer.domElement);

    // Label renderer setup for boundary view
    globalState.boundaryLabelRenderer = new CSS2DRenderer();
    globalState.boundaryLabelRenderer.setSize(
        window.innerWidth / 2,
        window.innerHeight / 2
    );
    globalState.boundaryLabelRenderer.domElement.style.position = "absolute";
    globalState.boundaryLabelRenderer.domElement.style.right = "0";
    globalState.boundaryLabelRenderer.domElement.style.top = "0";
    document.body.appendChild(globalState.boundaryLabelRenderer.domElement);

    // Simplified boundary view setup
    globalState.simplifiedBoundaryScene = new THREE.Scene();
    const simplifiedBoundaryAspect =
        window.innerWidth / 2 / (window.innerHeight / 2);
    globalState.simplifiedBoundaryCamera = createOrthographicCamera(
        simplifiedBoundaryAspect
    );
    globalState.simplifiedBoundaryRenderer = new THREE.WebGLRenderer({
        antialias: true
    });
    globalState.simplifiedBoundaryRenderer.setSize(
        window.innerWidth / 2,
        window.innerHeight / 2
    );
    globalState.simplifiedBoundaryRenderer.domElement.style.position =
        "absolute";
    globalState.simplifiedBoundaryRenderer.domElement.style.right = "0";
    globalState.simplifiedBoundaryRenderer.domElement.style.bottom = "0";
    document.body.appendChild(
        globalState.simplifiedBoundaryRenderer.domElement
    );

    // Label renderer setup for simplified boundary view
    globalState.simplifiedBoundaryLabelRenderer = new CSS2DRenderer();
    globalState.simplifiedBoundaryLabelRenderer.setSize(
        window.innerWidth / 2,
        window.innerHeight / 2
    );
    globalState.simplifiedBoundaryLabelRenderer.domElement.style.position =
        "absolute";
    globalState.simplifiedBoundaryLabelRenderer.domElement.style.right = "0";
    globalState.simplifiedBoundaryLabelRenderer.domElement.style.bottom = "0";
    document.body.appendChild(
        globalState.simplifiedBoundaryLabelRenderer.domElement
    );

    // Add vertical line separator
    const separator = document.createElement("div");
    separator.classList.add("view-separator");
    separator.style.position = "absolute";
    separator.style.top = "0";
    separator.style.left = "50%";
    separator.style.width = "2px";
    separator.style.height = "100%";
    separator.style.backgroundColor = "#000";
    document.body.appendChild(separator);

    // Add horizontal line separator for boundary views
    const horizontalSeparator = document.createElement("div");
    horizontalSeparator.classList.add("view-separator");
    horizontalSeparator.style.position = "absolute";
    horizontalSeparator.style.top = "50%";
    horizontalSeparator.style.right = "0";
    horizontalSeparator.style.width = "50%";
    horizontalSeparator.style.height = "2px";
    horizontalSeparator.style.backgroundColor = "#000";
    document.body.appendChild(horizontalSeparator);

    // Create gradient background for all views
    const bgTexture = createGradientTexture();
    globalState.scene.background = bgTexture;
    globalState.boundaryScene.background = bgTexture;
    globalState.simplifiedBoundaryScene.background = bgTexture;

    // Improve lighting for better object visibility in all views
    globalState.ambientLight = new THREE.AmbientLight(
        0xffffff,
        0.3 * globalState.brightnessAmount
    );
    globalState.scene.add(globalState.ambientLight);
    globalState.boundaryScene.add(globalState.ambientLight.clone());
    globalState.simplifiedBoundaryScene.add(globalState.ambientLight.clone());

    globalState.directionalLight1 = new THREE.DirectionalLight(
        0xffffff,
        0.7 * globalState.brightnessAmount
    );
    globalState.directionalLight1.position.set(1, 1, 1);
    globalState.scene.add(globalState.directionalLight1);
    globalState.boundaryScene.add(globalState.directionalLight1.clone());
    globalState.simplifiedBoundaryScene.add(
        globalState.directionalLight1.clone()
    );

    globalState.directionalLight2 = new THREE.DirectionalLight(
        0xffffff,
        0.5 * globalState.brightnessAmount
    );
    globalState.directionalLight2.position.set(-1, -1, -1);
    globalState.scene.add(globalState.directionalLight2);
    globalState.boundaryScene.add(globalState.directionalLight2.clone());
    globalState.simplifiedBoundaryScene.add(
        globalState.directionalLight2.clone()
    );

    // Adjust camera position to see the entire object in all views
    globalState.camera.position.set(0, 0, 60);
    globalState.boundaryCamera.position.set(0, 0, 60);
    globalState.simplifiedBoundaryCamera.position.set(0, 0, 60);

    globalState.controls = new OrbitControls(
        globalState.camera,
        globalState.labelRenderer.domElement
    );
    globalState.controls.enableDamping = true;
    globalState.controls.dampingFactor = 0.5;
    globalState.controls.screenSpacePanning = false;
    globalState.controls.enableRotate = true;
    globalState.controls.autoRotate = false;
    globalState.controls.enableZoom = true;
    globalState.controls.enablePan = true;

    // Add event listener for control changes
    globalState.controls.addEventListener("change", () => {
        drawMarginalRidgeLines(globalState);
    });

    globalState.boundaryControls = new OrbitControls(
        globalState.boundaryCamera,
        globalState.boundaryLabelRenderer.domElement
    );
    globalState.boundaryControls.enableDamping = true;
    globalState.boundaryControls.dampingFactor = 0.5;
    globalState.boundaryControls.screenSpacePanning = false;
    globalState.boundaryControls.enableRotate = true;
    globalState.boundaryControls.autoRotate = false;
    globalState.boundaryControls.enableZoom = true;
    globalState.boundaryControls.enablePan = true;

    globalState.simplifiedBoundaryControls = new OrbitControls(
        globalState.simplifiedBoundaryCamera,
        globalState.simplifiedBoundaryLabelRenderer.domElement
    );
    globalState.simplifiedBoundaryControls.enableDamping = true;
    globalState.simplifiedBoundaryControls.dampingFactor = 0.5;
    globalState.simplifiedBoundaryControls.screenSpacePanning = false;
    globalState.simplifiedBoundaryControls.enableRotate = true;
    globalState.simplifiedBoundaryControls.autoRotate = false;
    globalState.simplifiedBoundaryControls.enableZoom = true;
    globalState.simplifiedBoundaryControls.enablePan = true;

    // Link the three views
    globalState.controls.addEventListener("change", () => {
        globalState.boundaryCamera.position.copy(globalState.camera.position);
        globalState.boundaryCamera.rotation.copy(globalState.camera.rotation);
        globalState.boundaryControls.target.copy(globalState.controls.target);

        globalState.simplifiedBoundaryCamera.position.copy(
            globalState.camera.position
        );
        globalState.simplifiedBoundaryCamera.rotation.copy(
            globalState.camera.rotation
        );
        globalState.simplifiedBoundaryControls.target.copy(
            globalState.controls.target
        );
    });

    globalState.boundaryControls.addEventListener("change", () => {
        globalState.camera.position.copy(globalState.boundaryCamera.position);
        globalState.camera.rotation.copy(globalState.boundaryCamera.rotation);
        globalState.controls.target.copy(globalState.boundaryControls.target);

        globalState.simplifiedBoundaryCamera.position.copy(
            globalState.boundaryCamera.position
        );
        globalState.simplifiedBoundaryCamera.rotation.copy(
            globalState.boundaryCamera.rotation
        );
        globalState.simplifiedBoundaryControls.target.copy(
            globalState.boundaryControls.target
        );
    });

    globalState.simplifiedBoundaryControls.addEventListener("change", () => {
        globalState.camera.position.copy(
            globalState.simplifiedBoundaryCamera.position
        );
        globalState.camera.rotation.copy(
            globalState.simplifiedBoundaryCamera.rotation
        );
        globalState.controls.target.copy(
            globalState.simplifiedBoundaryControls.target
        );

        globalState.boundaryCamera.position.copy(
            globalState.simplifiedBoundaryCamera.position
        );
        globalState.boundaryCamera.rotation.copy(
            globalState.simplifiedBoundaryCamera.rotation
        );
        globalState.boundaryControls.target.copy(
            globalState.simplifiedBoundaryControls.target
        );
    });

    globalState.wireframeMaterial = new THREE.LineBasicMaterial({
        color: 0xffffff
    });
    globalState.pointsMaterial = new THREE.PointsMaterial({
        size: 0.05,
        vertexColors: true
    });

    window.addEventListener("resize", onWindowResize);

    // Setup drag and drop
    const dropZone = document.body;
    dropZone.addEventListener("dragover", handleDragOver);
    dropZone.addEventListener("drop", handleDrop);

    document
        .getElementById("file-select")
        .addEventListener("change", handleFileSelect);
    document
        .getElementById("show-wire-frame")
        .addEventListener("change", (e) => {
            globalState.showWireframe = e.target.checked;
            updateMeshVisualization();
        });
    document
        .getElementById("show-point-cloud")
        .addEventListener("change", (e) => {
            globalState.showPointCloud = e.target.checked;
            updateMeshVisualization();
        });

    document
        .getElementById("show-keypoint-labels")
        .addEventListener("change", (e) => {
            globalState.showKeypointLabels = e.target.checked;
            // Update visibility of existing keypoint labels instead of redrawing
            updateKeypointLabelsVisibility(e.target.checked);
        });

    document
        .getElementById("show-arrangement-labels")
        .addEventListener("change", (e) => {
            const showLabels = e.target.checked;
            // Update arrangement object label visibility
            updateArrangementLabelsVisibility(showLabels);
            // Force a render update
            if (globalState.renderMultiView) {
                renderMultiView();
            }
        });

    // Add brightness control
    document
        .getElementById("brightness-amount")
        .addEventListener("input", (e) => {
            globalState.brightnessAmount = parseFloat(e.target.value);
            document.getElementById("brightness-amount-value").textContent =
                globalState.brightnessAmount.toFixed(1);
            updateBrightness();
        });

    // Add Process button
    const processButton = document.getElementById("process-button");
    processButton.addEventListener("click", processFile);
    const clearResultsButton = document.getElementById("clear-results-button");
    if (clearResultsButton) {
        clearResultsButton.addEventListener("click", performInferenceClear);
    }
    const refineButton = document.getElementById("refine-button");
    refineButton.addEventListener("click", refineArrangement);
    updateClearButtonFromState();
    updateArrangementActionButtons();

    // Apply default values
    updateLighting();

    // Initialize brightness control
    document.getElementById("brightness-amount-value").textContent =
        globalState.brightnessAmount.toFixed(1);
    updateBrightness();

    // Add text labels to views
    addViewLabel(globalState.labelRenderer.domElement, "Main View");
    addViewLabel(globalState.boundaryLabelRenderer.domElement, "Boundary View");
    addViewLabel(
        globalState.simplifiedBoundaryLabelRenderer.domElement,
        "Simplified Boundary View"
    );

    // Apply correct layout based on default processing mode
    // Since arrangement is the default, hide boundary views initially
    const defaultMode = "arrangement"; // Since we set arrangement as default
    if (defaultMode === "arrangement") {
        // Set up single view layout for arrangement mode
        hideAllBoundaryViews();
        hideViewSeparators();

        // Resize main view to full width
        const aspect = window.innerWidth / window.innerHeight;
        updateOrthographicCamera(globalState.camera, aspect);
        globalState.renderer.setSize(window.innerWidth, window.innerHeight);
        globalState.labelRenderer.setSize(
            window.innerWidth,
            window.innerHeight
        );

        // Update postprocessing section visibility
        updatePostprocessingSectionVisibility();
        updateModelOptionsSectionVisibility("arrangement");
    } else {
        // Update postprocessing section visibility for other modes
        updatePostprocessingSectionVisibility();
        const currentMode =
            document.getElementById("processing-mode-dropdown")?.value ||
            "arrangement";
        updateModelOptionsSectionVisibility(currentMode);
    }

    // Add start over button functionality
    const startOverButton = document.getElementById("start-over-button");
    if (startOverButton) {
        startOverButton.addEventListener("click", () => {
            clearAllFilesAndState();
            hideStartOverAndFeedbackButtons();

            // Ensure left sidebar is expanded for new processing
            const leftSidebar = document.getElementById("left-sidebar");
            if (leftSidebar && leftSidebar.classList.contains("collapsed")) {
                toggleLeftSidebar();
            }

            showToast("Ready for new processing", "check-circle");
        });
    }

    // Setup event listeners for file handling
    window.addEventListener("dragover", handleDragOver, false);
    window.addEventListener("drop", handleDrop, false);
    document
        .getElementById("file-select")
        .addEventListener("change", handleFileSelect, false);

    // Process button event listener already added above

    // Initialize other UI elements
    window.addEventListener("resize", onWindowResize, false);

    // Call setupControls function
    setupControls();

    // Initialize postprocessing controls (async - loads defaults from server)
    initializePostprocessingControls().catch((error) => {
        console.error("Failed to initialize postprocessing controls:", error);
    });
    initializeLevelingControls().catch((error) => {
        console.error("Failed to initialize leveling controls:", error);
    });

    // Initialize gingiva estimation checkbox
    initializeGingivaEstimation();

    // Initialize calculate space/intersection checkbox
    initializeCalculateSpaceIntersection();
    initializeAutoOrientation();

    // Update postprocessing section visibility on page load
    // Use setTimeout to ensure all DOM elements are fully initialized
    setTimeout(() => {
        updatePostprocessingSectionVisibility();
        const processingMode =
            document.getElementById("processing-mode-dropdown")?.value ||
            "arrangement";
        updateModelOptionsSectionVisibility(processingMode);
        // Double-check after a short delay to ensure visibility is set
        setTimeout(() => {
            const postprocessingSection = document.getElementById(
                "postprocessing-section"
            );
            if (
                postprocessingSection &&
                processingMode === "arrangement" &&
                !globalState.feedbackMode
            ) {
                postprocessingSection.style.display = "block";
                postprocessingSection.style.setProperty(
                    "display",
                    "block",
                    "important"
                );
            }
        }, 50);
    }, 150);
}

function handleDragOver(event) {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
}

function handleDrop(event) {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
        handleFile(file);
    }
}

function handleFileSelect(event) {
    const file = event.target.files[0];
    if (file) {
        clearVisualization();
        document.getElementById("process-button").textContent = "Process";
        setTimeout(() => {
            handleFile(file);
        }, 200);
    }
}
function clearVisualization() {
    // Reset other states as needed
    globalState.mesh = null;
    globalState.boundaryMesh = null;
    globalState.simplifiedBoundaryMesh = null;

    // Remove existing class labels
    globalState.classLabels.forEach((label) => globalState.scene.remove(label));
    globalState.classLabels = [];
    // Remove all keypoint objects and labels from scene
    for (let i = globalState.scene.children.length - 1; i >= 0; i--) {
        const child = globalState.scene.children[i];
        if (child.name?.startsWith("keypoint-")) {
            globalState.scene.remove(child);
        }
    }
    globalState.keypoints = {};
    globalState.keypointLabels = [];

    // Remove arrangement object labels from main scene
    for (let i = globalState.scene.children.length - 1; i >= 0; i--) {
        const child = globalState.scene.children[i];
        if (
            child.isCSS2DObject &&
            child.name?.startsWith("arrangement-label-")
        ) {
            globalState.scene.remove(child);
        }
    }

    // Clear overlay view if it exists
    if (globalState.overlayScene) {
        while (globalState.overlayScene.children.length > 0) {
            const child = globalState.overlayScene.children[0];
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
                if (Array.isArray(child.material)) {
                    child.material.forEach((mat) => mat.dispose());
                } else {
                    child.material.dispose();
                }
            }
            globalState.overlayScene.remove(child);
        }
    }

    // Remove overlay legend
    const overlayViewElement = document.getElementById("overlay-view");
    if (overlayViewElement) {
        const existingLegend =
            overlayViewElement.querySelector(".overlay-legend");
        if (existingLegend) {
            existingLegend.remove();
        }
    }

    // Remove boundary points and lines
    if (globalState.boundaryPoints)
        globalState.boundaryScene.remove(globalState.boundaryPoints);
    if (globalState.boundaryLines)
        globalState.boundaryScene.remove(globalState.boundaryLines);
    if (globalState.simplifiedBoundaryPoints)
        globalState.simplifiedBoundaryScene.remove(
            globalState.simplifiedBoundaryPoints
        );
    if (globalState.simplifiedBoundaryLines)
        globalState.simplifiedBoundaryScene.remove(
            globalState.simplifiedBoundaryLines
        );

    // Update visualizations
    updateMeshVisualization();
}

function clearFile() {
    if (globalState.scene.getObjectByName("mesh")) {
        globalState.scene.remove(globalState.scene.getObjectByName("mesh"));
    }
    if (globalState.boundaryScene.getObjectByName("boundaryMesh")) {
        globalState.boundaryScene.remove(
            globalState.boundaryScene.getObjectByName("boundaryMesh")
        );
    }
    if (
        globalState.simplifiedBoundaryScene.getObjectByName(
            "simplifiedBoundaryMesh"
        )
    ) {
        globalState.simplifiedBoundaryScene.remove(
            globalState.simplifiedBoundaryScene.getObjectByName(
                "simplifiedBoundaryMesh"
            )
        );
    }

    // Clear file input
    document.getElementById("file-select").value = "";

    // Clear arrangement file inputs
    const lowerJawFile = document.getElementById("lower-jaw-file");
    const upperJawFile = document.getElementById("upper-jaw-file");
    const infoJsonFile = document.getElementById("info-json-file");
    const landmarksCsvFile = document.getElementById("landmarks-csv-file");
    if (lowerJawFile) lowerJawFile.value = "";
    if (upperJawFile) upperJawFile.value = "";
    if (infoJsonFile) infoJsonFile.value = "";
    if (landmarksCsvFile) landmarksCsvFile.value = "";

    // Clear Azure path inputs
    const singleAzurePath = document.getElementById("azure-path-input");
    const lowerJawAzurePath = document.getElementById("lower-jaw-azure-path");
    const upperJawAzurePath = document.getElementById("upper-jaw-azure-path");
    const infoJsonAzurePath = document.getElementById("info-json-azure-path");
    const landmarksCsvAzurePath = document.getElementById(
        "landmarks-csv-azure-path"
    );
    if (singleAzurePath) singleAzurePath.value = "";
    if (lowerJawAzurePath) lowerJawAzurePath.value = "";
    if (upperJawAzurePath) upperJawAzurePath.value = "";
    if (infoJsonAzurePath) infoJsonAzurePath.value = "";
    if (landmarksCsvAzurePath) landmarksCsvAzurePath.value = "";

    // Clear the Azure folder input and its resolution preview/error
    const arrangementFolderPath = document.getElementById(
        "arrangement-folder-path"
    );
    if (arrangementFolderPath) arrangementFolderPath.value = "";
    const folderResolvePreview = document.getElementById(
        "folder-resolve-preview"
    );
    if (folderResolvePreview) {
        folderResolvePreview.textContent = "";
        folderResolvePreview.style.display = "none";
    }
    const folderResolveError = document.getElementById("folder-resolve-error");
    if (folderResolveError) {
        folderResolveError.textContent = "";
        folderResolveError.style.display = "none";
    }
    const overrideAzurePathsCheckbox = document.getElementById(
        "override-azure-paths-checkbox"
    );
    if (overrideAzurePathsCheckbox) overrideAzurePathsCheckbox.checked = false;

    // Clear file name displays
    const lowerJawFileName = document.getElementById("lower-jaw-file-name");
    const upperJawFileName = document.getElementById("upper-jaw-file-name");
    const infoJsonFileName = document.getElementById("info-json-file-name");
    const landmarksCsvFileName = document.getElementById(
        "landmarks-csv-file-name"
    );
    if (lowerJawFileName) lowerJawFileName.textContent = "";
    if (upperJawFileName) upperJawFileName.textContent = "";
    if (infoJsonFileName) infoJsonFileName.textContent = "";
    if (landmarksCsvFileName) landmarksCsvFileName.textContent = "";

    // Remove jaw toggle controls
    const existingControls = document.getElementById("jaw-toggle-controls");
    if (existingControls) {
        existingControls.remove();
    }

    // Clear arrangement objects from main scene
    const meshesToRemove = globalState.scene.children.filter(
        (child) =>
            child.isMesh && child.userData && child.userData.originalObject
    );
    meshesToRemove.forEach((mesh) => {
        globalState.scene.remove(mesh);
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
            if (Array.isArray(mesh.material)) {
                mesh.material.forEach((mat) => mat.dispose());
            } else {
                mesh.material.dispose();
            }
        }
    });

    clearVisualization();

    // Clear multi-view if active
    if (globalState.renderMultiView) {
        // Reset multi-view container visibility
        document.getElementById("multi-view-container").style.display = "none";
        globalState.renderer.domElement.style.display = "block";
        globalState.labelRenderer.domElement.style.display = "block";

        // Clear all scenes
        Object.keys(globalState.multiViewScenes).forEach((view) => {
            if (globalState.multiViewScenes[view]) {
                // Keep only lights by removing other objects
                for (
                    let i =
                        globalState.multiViewScenes[view].children.length - 1;
                    i >= 0;
                    i--
                ) {
                    const obj = globalState.multiViewScenes[view].children[i];
                    if (!(obj instanceof THREE.Light)) {
                        globalState.multiViewScenes[view].remove(obj);
                    }
                }
            }
        });

        // Reset multi-view container and turn off multi-view rendering
        resetMultiViewContainer();
        globalState.renderMultiView = false;
        globalState.multiViewLayoutInitialized = false;
    }

    // Also check for arrangement multi-view (when multi-view container is visible)
    const multiViewContainer = document.getElementById("multi-view-container");
    if (multiViewContainer && multiViewContainer.style.display !== "none") {
        resetMultiViewContainer();
        globalState.renderer.domElement.style.display = "block";
        globalState.labelRenderer.domElement.style.display = "block";

        // Clear multi-view scenes for arrangement
        if (globalState.multiViewScenes) {
            Object.keys(globalState.multiViewScenes).forEach((view) => {
                if (globalState.multiViewScenes[view]) {
                    // Keep only lights by removing other objects
                    for (
                        let i =
                            globalState.multiViewScenes[view].children.length -
                            1;
                        i >= 0;
                        i--
                    ) {
                        const obj =
                            globalState.multiViewScenes[view].children[i];
                        if (!(obj instanceof THREE.Light)) {
                            globalState.multiViewScenes[view].remove(obj);
                            // Dispose of geometry and materials
                            if (obj.geometry) obj.geometry.dispose();
                            if (obj.material) {
                                if (Array.isArray(obj.material)) {
                                    obj.material.forEach((mat) =>
                                        mat.dispose()
                                    );
                                } else {
                                    obj.material.dispose();
                                }
                            }
                        }
                    }
                }
            });
        }
    }

    // Reset separation phase and data regardless of whether multi-view was active
    globalState.separationPhase = 0;
    globalState.separationData = {
        originalMesh: null,
        segmentedMesh: null,
        analyzedTeeth: null,
        finalResult: null,
        reconstructionResult: null
    };

    // Clear last inference result
    globalState.lastInferenceResult = null;
    globalState.lastArrangementInfoJson = null;
    globalState.displacementTable = null;
    updateAchievedOverbite(null);

    // Hide the next phase button
    const nextPhaseButton = document.getElementById("next-phase-button");
    if (nextPhaseButton) {
        nextPhaseButton.style.display = "none";
    }

    // Hide the download InfoJSON button
    hideInfoJsonActions();

    // Hide displacement table button
    const displacementTableButton = document.getElementById(
        "displacement-table-button"
    );
    if (displacementTableButton) {
        displacementTableButton.style.display = "none";
    }

    // Hide download buttons from teeth reconstruction
    const downloadButtonsContainer = document.getElementById(
        "download-buttons-container"
    );
    if (downloadButtonsContainer) {
        downloadButtonsContainer.style.display = "none";
    }

    // Hide start over button and feedback button
    hideStartOverAndFeedbackButtons();
    hideFeedbackButton();

    // Re-enable all input fields for next inference
    enableAllInputs();

    showToast("File cleared", "check-circle");
    document.getElementById("process-button").textContent = "Process";
    updateClearButtonFromState();
    updateArrangementActionButtons();
}

async function handleFile(file) {
    if (globalState.loadingFile) return;
    globalState.loadingFile = true;

    const fileExtension = file.name.split(".").pop().toLowerCase();
    let geometry;
    let tmpMesh;

    try {
        if (fileExtension === "obj") {
            tmpMesh = await loadObjFile(file);
        } else if (fileExtension === "stl") {
            tmpMesh = await loadStlFile(file);
        } else {
            throw new Error("Unsupported file format");
        }
    } catch (error) {
        console.error("Error loading file:", error.message);
        showToast(`Failed to load file: ${error.message}`, "error");
        globalState.loadingFile = false;
        clearFile();
        return;
    }

    if (!tmpMesh) {
        throw new Error("Failed to load mesh");
    }

    geometry = tmpMesh.geometry;

    globalState.mesh = new THREE.Mesh(
        geometry,
        new THREE.MeshStandardMaterial({
            side: THREE.DoubleSide,
            vertexColors: true,
            metalness: 0.3,
            roughness: 0.5
        })
    );

    // Create boundary mesh
    globalState.boundaryMesh = new THREE.Mesh(
        geometry,
        new THREE.MeshStandardMaterial({
            side: THREE.DoubleSide,
            color: 0xffffff,
            metalness: 0.3,
            roughness: 0.5,
            opacity: 0.95,
            transparent: true
        })
    );

    // Create simplified boundary mesh
    globalState.simplifiedBoundaryMesh = new THREE.Mesh(
        geometry,
        new THREE.MeshStandardMaterial({
            side: THREE.DoubleSide,
            color: 0xffffff,
            metalness: 0.3,
            roughness: 0.5,
            opacity: 0.95,
            transparent: true
        })
    );

    if (globalState.scene.getObjectByName("mesh")) {
        globalState.scene.remove(globalState.scene.getObjectByName("mesh"));
    }
    globalState.mesh.name = "mesh";
    globalState.scene.add(globalState.mesh);

    if (globalState.boundaryScene.getObjectByName("boundaryMesh")) {
        globalState.boundaryScene.remove(
            globalState.boundaryScene.getObjectByName("boundaryMesh")
        );
    }
    globalState.boundaryMesh.name = "boundaryMesh";
    globalState.boundaryScene.add(globalState.boundaryMesh);

    if (
        globalState.simplifiedBoundaryScene.getObjectByName(
            "simplifiedBoundaryMesh"
        )
    ) {
        globalState.simplifiedBoundaryScene.remove(
            globalState.simplifiedBoundaryScene.getObjectByName(
                "simplifiedBoundaryMesh"
            )
        );
    }
    globalState.simplifiedBoundaryMesh.name = "simplifiedBoundaryMesh";
    globalState.simplifiedBoundaryScene.add(globalState.simplifiedBoundaryMesh);

    // Initialize color attribute if not present
    if (!globalState.mesh.geometry.attributes.color) {
        const colors = new Float32Array(
            globalState.mesh.geometry.attributes.position.count * 3
        );
        colors.fill(1);
        globalState.mesh.geometry.setAttribute(
            "color",
            new THREE.BufferAttribute(colors, 3)
        );
    }

    updateMeshVisualization();
    globalState.loadingFile = false;
}

/**
 * Disable all input fields after successful inference
 */
function disableAllInputs() {
    // Disable all file input groups by adding disabled class
    const fileInputGroups = document.querySelectorAll(".file-input-group");
    fileInputGroups.forEach((group) => {
        group.classList.add("disabled");
    });

    // Disable file inputs
    const fileSelect = document.getElementById("file-select");
    const lowerJawFile = document.getElementById("lower-jaw-file");
    const upperJawFile = document.getElementById("upper-jaw-file");
    const infoJsonFile = document.getElementById("info-json-file");

    if (fileSelect) {
        fileSelect.disabled = true;
        const parentGroup = fileSelect.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.add("disabled");
    }
    if (lowerJawFile) {
        lowerJawFile.disabled = true;
        const parentGroup = lowerJawFile.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.add("disabled");
    }
    if (upperJawFile) {
        upperJawFile.disabled = true;
        const parentGroup = upperJawFile.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.add("disabled");
    }
    if (infoJsonFile) {
        infoJsonFile.disabled = true;
        const parentGroup = infoJsonFile.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.add("disabled");
    }

    // Disable Azure path inputs
    const singleAzurePath = document.getElementById("azure-path-input");
    const lowerJawAzurePath = document.getElementById("lower-jaw-azure-path");
    const upperJawAzurePath = document.getElementById("upper-jaw-azure-path");
    const infoJsonAzurePath = document.getElementById("info-json-azure-path");

    if (singleAzurePath) {
        singleAzurePath.disabled = true;
        const parentGroup = singleAzurePath.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.add("disabled");
    }
    if (lowerJawAzurePath) {
        lowerJawAzurePath.disabled = true;
        const parentGroup = lowerJawAzurePath.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.add("disabled");
    }
    if (upperJawAzurePath) {
        upperJawAzurePath.disabled = true;
        const parentGroup = upperJawAzurePath.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.add("disabled");
    }
    if (infoJsonAzurePath) {
        infoJsonAzurePath.disabled = true;
        const parentGroup = infoJsonAzurePath.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.add("disabled");
    }

    // Disable the Azure folder input and its resolve button
    const arrangementFolderPath = document.getElementById(
        "arrangement-folder-path"
    );
    if (arrangementFolderPath) {
        arrangementFolderPath.disabled = true;
        const parentGroup = arrangementFolderPath.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.add("disabled");
    }
    const resolveFolderBtn = document.getElementById("resolve-folder-btn");
    if (resolveFolderBtn) resolveFolderBtn.disabled = true;
    const overrideAzurePathsCheckbox = document.getElementById(
        "override-azure-paths-checkbox"
    );
    if (overrideAzurePathsCheckbox) overrideAzurePathsCheckbox.disabled = true;

    // Disable processing mode dropdown
    const processingModeDropdown = document.getElementById(
        "processing-mode-dropdown"
    );
    if (processingModeDropdown) {
        processingModeDropdown.disabled = true;
        processingModeDropdown.style.opacity = "0.5";
        processingModeDropdown.style.cursor = "not-allowed";
    }

    // Disable input mode toggle buttons
    const inputModeToggle = document.getElementById("input-mode-toggle");
    if (inputModeToggle) {
        inputModeToggle.classList.add("disabled");
        const toggleButtons =
            inputModeToggle.querySelectorAll(".toggle-option");
        toggleButtons.forEach((btn) => {
            btn.style.pointerEvents = "none";
            btn.style.opacity = "0.5";
            btn.style.cursor = "not-allowed";
        });
    }

    // Disable jaw selection buttons
    const jawSelectionOptions = document.getElementById(
        "jaw-selection-options"
    );
    if (jawSelectionOptions) {
        jawSelectionOptions.classList.add("disabled");
        const jawSelectionButtons =
            jawSelectionOptions.querySelectorAll(".jaw-button");
        jawSelectionButtons.forEach((btn) => {
            btn.disabled = true;
            btn.style.opacity = "0.5";
            btn.style.cursor = "not-allowed";
            btn.style.pointerEvents = "none";
        });
    }
}

/**
 * Enable all input fields when clearing to prepare for new inference
 */
function enableAllInputs() {
    // Remove disabled class from all file input groups
    const fileInputGroups = document.querySelectorAll(".file-input-group");
    fileInputGroups.forEach((group) => {
        group.classList.remove("disabled");
    });

    // Enable file inputs
    const fileSelect = document.getElementById("file-select");
    const lowerJawFile = document.getElementById("lower-jaw-file");
    const upperJawFile = document.getElementById("upper-jaw-file");
    const infoJsonFile = document.getElementById("info-json-file");

    if (fileSelect) {
        fileSelect.disabled = false;
        const parentGroup = fileSelect.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.remove("disabled");
    }
    if (lowerJawFile) {
        lowerJawFile.disabled = false;
        const parentGroup = lowerJawFile.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.remove("disabled");
    }
    if (upperJawFile) {
        upperJawFile.disabled = false;
        const parentGroup = upperJawFile.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.remove("disabled");
    }
    if (infoJsonFile) {
        infoJsonFile.disabled = false;
        const parentGroup = infoJsonFile.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.remove("disabled");
    }

    // Enable Azure path inputs
    const singleAzurePath = document.getElementById("azure-path-input");
    const lowerJawAzurePath = document.getElementById("lower-jaw-azure-path");
    const upperJawAzurePath = document.getElementById("upper-jaw-azure-path");
    const infoJsonAzurePath = document.getElementById("info-json-azure-path");

    if (singleAzurePath) {
        singleAzurePath.disabled = false;
        const parentGroup = singleAzurePath.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.remove("disabled");
    }
    if (lowerJawAzurePath) {
        lowerJawAzurePath.disabled = false;
        const parentGroup = lowerJawAzurePath.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.remove("disabled");
    }
    if (upperJawAzurePath) {
        upperJawAzurePath.disabled = false;
        const parentGroup = upperJawAzurePath.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.remove("disabled");
    }
    if (infoJsonAzurePath) {
        infoJsonAzurePath.disabled = false;
        const parentGroup = infoJsonAzurePath.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.remove("disabled");
    }

    // Enable the Azure folder input and its resolve button
    const arrangementFolderPath = document.getElementById(
        "arrangement-folder-path"
    );
    if (arrangementFolderPath) {
        arrangementFolderPath.disabled = false;
        const parentGroup = arrangementFolderPath.closest(".file-input-group");
        if (parentGroup) parentGroup.classList.remove("disabled");
    }
    const resolveFolderBtn = document.getElementById("resolve-folder-btn");
    if (resolveFolderBtn) resolveFolderBtn.disabled = false;
    const overrideAzurePathsCheckbox = document.getElementById(
        "override-azure-paths-checkbox"
    );
    if (overrideAzurePathsCheckbox) overrideAzurePathsCheckbox.disabled = false;

    // Enable processing mode dropdown
    const processingModeDropdown = document.getElementById(
        "processing-mode-dropdown"
    );
    if (processingModeDropdown) {
        processingModeDropdown.disabled = false;
        processingModeDropdown.style.opacity = "1";
        processingModeDropdown.style.cursor = "pointer";
    }

    // Enable input mode toggle buttons
    const inputModeToggle = document.getElementById("input-mode-toggle");
    if (inputModeToggle) {
        inputModeToggle.classList.remove("disabled");
        const toggleButtons =
            inputModeToggle.querySelectorAll(".toggle-option");
        toggleButtons.forEach((btn) => {
            btn.style.pointerEvents = "auto";
            btn.style.opacity = "1";
            btn.style.cursor = "pointer";
        });
    }

    // Enable jaw selection buttons
    const jawSelectionOptions = document.getElementById(
        "jaw-selection-options"
    );
    if (jawSelectionOptions) {
        jawSelectionOptions.classList.remove("disabled");
        const jawSelectionButtons =
            jawSelectionOptions.querySelectorAll(".jaw-button");
        jawSelectionButtons.forEach((btn) => {
            btn.disabled = false;
            btn.style.opacity = "1";
            btn.style.cursor = "pointer";
            btn.style.pointerEvents = "auto";
        });
    }
}

/**
 * Reset the process button to its default state
 */
function resetProcessButton() {
    const processButton = document.getElementById("process-button");
    const processingIndicator = document.getElementById("processing-indicator");

    processButton.textContent = "Process";
    processButton.disabled = false;
    processingIndicator.style.display = "none";
    globalState.processingFile = false;
    updateClearButtonFromState();
    updateArrangementActionButtons();
}

/**
 * Enable Clear when there is something to reset; disable while a task is running.
 */
function updateClearButtonFromState() {
    const btn = document.getElementById("clear-results-button");
    if (!btn) return;
    if (globalState.processingFile) {
        btn.disabled = true;
        return;
    }
    const hasResults =
        globalState.lastInferenceResult != null ||
        globalState.lastArrangementInfoJson != null ||
        globalState.displacementTable != null ||
        (globalState.keypoints &&
            Object.keys(globalState.keypoints).length > 0) ||
        globalState.separationPhase > 0 ||
        (globalState.separationData &&
            (globalState.separationData.segmentedMesh != null ||
                globalState.separationData.finalResult != null ||
                globalState.separationData.analyzedTeeth != null));

    btn.disabled = !hasResults;
}

function performInferenceClear() {
    const btn = document.getElementById("clear-results-button");
    if (!btn || btn.disabled) return;
    clearFile();
}

function updateArrangementActionButtons() {
    const processButton = document.getElementById("process-button");
    const refineButton = document.getElementById("refine-button");
    const processingMode =
        document.getElementById("processing-mode-dropdown")?.value ||
        "arrangement";
    const hasArrangementResult = Boolean(globalState.lastArrangementInfoJson);
    const showRefine =
        processingMode === "arrangement" &&
        !globalState.feedbackMode &&
        hasArrangementResult;

    if (
        processButton &&
        processingMode === "arrangement" &&
        !globalState.processingFile
    ) {
        if (processButton.textContent !== "Processing...") {
            processButton.textContent = "Process";
        }
    }

    if (refineButton) {
        refineButton.style.display = showRefine ? "block" : "none";
        refineButton.disabled = globalState.processingFile || !showRefine;
    }
}

function collectArrangementInputs() {
    if (isAzureInputMode()) {
        const lowerJawAzurePath = document
            .getElementById("lower-jaw-azure-path")
            .value.trim();
        const upperJawAzurePath = document
            .getElementById("upper-jaw-azure-path")
            .value.trim();
        const infoJsonPath = document
            .getElementById("info-json-azure-path")
            .value.trim();
        const landmarksCsvPath = document
            .getElementById("landmarks-csv-azure-path")
            .value.trim();

        if (!lowerJawAzurePath || !upperJawAzurePath) {
            return {
                error: "Please provide both lower and upper jaw Azure paths for arrangement"
            };
        }

        if (
            requireAllArrangementFiles &&
            (!infoJsonPath || !landmarksCsvPath)
        ) {
            return {
                error: "Please provide all four Azure paths for arrangement (lower jaw, upper jaw, InfoJSON, landmarks CSV)"
            };
        }

        if (enforcePatientIdMatch) {
            const idError = validatePatientIdConsistency([
                { label: "Lower jaw", name: lowerJawAzurePath },
                { label: "Upper jaw", name: upperJawAzurePath },
                { label: "InfoJSON", name: infoJsonPath },
                { label: "Landmarks CSV", name: landmarksCsvPath }
            ]);
            const blockingError = resolvePatientIdCheckError(idError);
            if (blockingError) {
                return blockingError;
            }
        }

        return {
            file: null,
            pairedFile: null,
            azurePath: lowerJawAzurePath,
            pairedAzurePath: upperJawAzurePath,
            infoJsonFile: null,
            infoJsonAzurePath: infoJsonPath || null,
            landmarksCsvFile: null,
            landmarksCsvAzurePath: landmarksCsvPath || null
        };
    }

    const lowerJawFile = document.getElementById("lower-jaw-file").files[0];
    const upperJawFile = document.getElementById("upper-jaw-file").files[0];
    const infoJsonFile = document.getElementById("info-json-file").files[0];
    const landmarksCsvFile =
        document.getElementById("landmarks-csv-file").files[0];

    if (!lowerJawFile || !upperJawFile) {
        return {
            error: "Please select both lower and upper jaw files for arrangement"
        };
    }

    if (requireAllArrangementFiles && (!infoJsonFile || !landmarksCsvFile)) {
        return {
            error: "Please select all four files for arrangement (lower jaw, upper jaw, InfoJSON, landmarks CSV)"
        };
    }

    if (!lowerJawFile.name.toLowerCase().endsWith(".obj")) {
        return { error: "Lower jaw file must be a .obj file" };
    }

    if (!upperJawFile.name.toLowerCase().endsWith(".obj")) {
        return { error: "Upper jaw file must be a .obj file" };
    }

    if (infoJsonFile && !infoJsonFile.name.toLowerCase().endsWith(".json")) {
        return { error: "InfoJSON file must be a .json file" };
    }

    if (
        landmarksCsvFile &&
        !landmarksCsvFile.name.toLowerCase().endsWith(".csv")
    ) {
        return { error: "Landmarks CSV file must be a .csv file" };
    }

    if (enforcePatientIdMatch) {
        const idError = validatePatientIdConsistency([
            { label: "Lower jaw", name: lowerJawFile.name },
            { label: "Upper jaw", name: upperJawFile.name },
            { label: "InfoJSON", name: infoJsonFile ? infoJsonFile.name : "" },
            {
                label: "Landmarks CSV",
                name: landmarksCsvFile ? landmarksCsvFile.name : ""
            }
        ]);
        const blockingError = resolvePatientIdCheckError(idError);
        if (blockingError) {
            return blockingError;
        }
    }

    return {
        file: lowerJawFile,
        pairedFile: upperJawFile,
        azurePath: null,
        pairedAzurePath: null,
        infoJsonFile: infoJsonFile || null,
        infoJsonAzurePath: null,
        landmarksCsvFile: landmarksCsvFile || null,
        landmarksCsvAzurePath: null
    };
}

function collectArrangementModelOptions() {
    const enableGingivaEstimation =
        document.getElementById("enable-gingiva-estimation-checkbox")
            ?.checked ?? true;
    // When the toggle is hidden (production), the computation always runs
    // regardless of the (hidden) checkbox state.
    const calculateSpaceIntersection =
        !spaceIntersectionToggleEnabled ||
        document.getElementById("calculate-space-intersection-checkbox")
            ?.checked !== false;
    return {
        enable_gingiva_estimation: enableGingivaEstimation,
        calculate_space_intersection: calculateSpaceIntersection
    };
}

function handleArrangementWarnings(uploadResponse) {
    if (uploadResponse.warnings) {
        if (uploadResponse.warnings.main_file) {
            showToast(
                uploadResponse.warnings.main_file,
                "exclamation-triangle",
                5000
            );
        }
        if (uploadResponse.warnings.paired_file) {
            showToast(
                uploadResponse.warnings.paired_file,
                "exclamation-triangle",
                5000
            );
        }
    } else if (uploadResponse.warning) {
        showToast(uploadResponse.warning, "exclamation-triangle", 5000);
    }
}

async function refineArrangement() {
    if (globalState.processingFile) {
        return;
    }

    const processingMode =
        document.getElementById("processing-mode-dropdown")?.value ||
        "arrangement";
    if (processingMode !== "arrangement") {
        return;
    }

    if (!globalState.lastArrangementInfoJson) {
        showToast(
            "Process arrangement first before running Refine",
            "exclamation-triangle"
        );
        return;
    }

    const postprocessingData = collectPostprocessingParameters();
    const levelingData = collectLevelingParameters();
    if (!postprocessingData.enabled && !levelingData.enabled) {
        showToast(
            "Enable Interproximal and/or Leveling refinement to run Refine",
            "exclamation-triangle"
        );
        return;
    }

    const arrangementInputs = collectArrangementInputs();
    if (arrangementInputs.error) {
        showToast(arrangementInputs.error, "exclamation-triangle");
        return;
    }

    const processButton = document.getElementById("process-button");
    const refineButton = document.getElementById("refine-button");
    const processingIndicator = document.getElementById("processing-indicator");
    const leftSidebar = document.getElementById("left-sidebar");
    if (leftSidebar && !leftSidebar.classList.contains("collapsed")) {
        toggleLeftSidebar();
    }

    globalState.processingFile = true;
    processingIndicator.style.display = "block";
    updateProgressBar(0);
    document.querySelector("#processing-indicator .progress-task").textContent =
        "Refining ARRANGEMENT...";

    processButton.disabled = true;
    refineButton.disabled = true;
    const clearResultsButtonRefine = document.getElementById(
        "clear-results-button"
    );
    if (clearResultsButtonRefine) {
        clearResultsButtonRefine.disabled = true;
    }

    let refineSucceeded = false;
    try {
        const baseOptions = collectArrangementModelOptions();
        const fileSize = arrangementInputs.file
            ? arrangementInputs.file.size
            : 1000000;
        const estimatedTime = estimateProcessingTime(fileSize, "arrangement");
        updateProgressBar(5, estimatedTime);
        const startTime = Date.now();

        let currentInfoJson = globalState.lastArrangementInfoJson;
        let lastDecodedResult = null;
        let achievedOverbite = null;

        if (levelingData.enabled) {
            const levelOptions = {
                ...baseOptions,
                leveling: levelingData.parameters
            };
            // Leveling must honour the freeze list even when the postprocessing
            // checkbox is off: the freeze table lives inside the postprocessing
            // group, but /level consumes only fixed_teeth from that section (the
            // server enables validation whenever a postprocessing section is
            // present, independent of the checkbox). When postprocessing is on,
            // forward its full parameters; otherwise forward just the freeze list
            // so frozen teeth are still held during leveling instead of moving.
            if (postprocessingData.parameters) {
                levelOptions.postprocessing = postprocessingData.parameters;
            } else {
                const frozenTeeth = collectFixedTeeth();
                if (frozenTeeth.length > 0) {
                    levelOptions.postprocessing = { fixed_teeth: frozenTeeth };
                }
            }
            const infoJsonBlob = new File(
                [JSON.stringify(currentInfoJson, null, 2)],
                "arrangement_info.json",
                { type: "application/json" }
            );
            const uploadResponse = await ApiService.uploadForArrangementLevel(
                arrangementInputs.file,
                arrangementInputs.azurePath,
                arrangementInputs.pairedFile,
                arrangementInputs.pairedAzurePath,
                infoJsonBlob,
                arrangementInputs.landmarksCsvFile,
                arrangementInputs.landmarksCsvAzurePath,
                levelOptions
            );
            handleArrangementWarnings(uploadResponse);
            globalState.lastProcessedItemId = uploadResponse.item_id;
            lastDecodedResult = await ApiService.pollUntilCompletion(
                uploadResponse.item_id,
                updateProgressBar,
                estimatedTime,
                startTime
            );
            currentInfoJson =
                lastDecodedResult?.arrangement_result?.info_json ||
                currentInfoJson;
            // Capture achieved overbite from the level step before a
            // subsequent postprocessing step overwrites lastDecodedResult.
            achievedOverbite =
                lastDecodedResult?.arrangement_result?.leveling_stats
                    ?.leveling_targets?.achieved_overbite ?? achievedOverbite;
        }

        if (postprocessingData.enabled) {
            const refineOptions = {
                ...baseOptions,
                postprocessing: postprocessingData.parameters
            };
            const infoJsonBlob = new File(
                [JSON.stringify(currentInfoJson, null, 2)],
                "arrangement_info.json",
                { type: "application/json" }
            );
            const uploadResponse = await ApiService.uploadForArrangementRefine(
                arrangementInputs.file,
                arrangementInputs.azurePath,
                arrangementInputs.pairedFile,
                arrangementInputs.pairedAzurePath,
                infoJsonBlob,
                arrangementInputs.landmarksCsvFile,
                arrangementInputs.landmarksCsvAzurePath,
                refineOptions
            );
            handleArrangementWarnings(uploadResponse);
            globalState.lastProcessedItemId = uploadResponse.item_id;
            lastDecodedResult = await ApiService.pollUntilCompletion(
                uploadResponse.item_id,
                updateProgressBar,
                estimatedTime,
                startTime
            );
        }

        if (!lastDecodedResult) {
            throw new Error("No refinement step produced a result");
        }

        await displayArrangementResults(lastDecodedResult.arrangement_result);
        // Prefer the value captured from the level step; fall back to the
        // final result in case only postprocessing ran.
        updateAchievedOverbite(
            achievedOverbite ??
                lastDecodedResult.arrangement_result?.leveling_stats
                    ?.leveling_targets?.achieved_overbite
        );
        await new Promise((resolve) => setTimeout(resolve, 750));
        processingIndicator.style.display = "none";
        showToast("Refinement completed", "check-circle");
        showStartOverAndFeedbackButtons();
        updateStartOverButtonVisibility();
        refineSucceeded = true;
    } catch (error) {
        console.error("Error during arrangement refinement:", error);
        const errorMessage =
            error.message ||
            error.response?.data?.detail ||
            "Error during arrangement refinement";
        showToast(errorMessage, "exclamation-triangle");
        processingIndicator.style.display = "none";
    } finally {
        globalState.processingFile = false;
        processButton.disabled = false;
        if (refineSucceeded) {
            disableAllInputs();
        }
        updateClearButtonFromState();
        updateArrangementActionButtons();
    }
}

async function processFile() {
    if (globalState.processingFile) {
        return;
    }

    // Get processing mode early to check if mesh is required
    const processingMode =
        document.getElementById("processing-mode-dropdown")?.value ||
        "arrangement";

    // For arrangement mode, we don't need a pre-loaded mesh
    if (processingMode !== "arrangement" && !globalState.mesh) {
        return;
    }

    const processButton = document.getElementById("process-button");

    // Collapse left sidebar at the start of processing to maximize viewer space
    const leftSidebar = document.getElementById("left-sidebar");
    if (leftSidebar && !leftSidebar.classList.contains("collapsed")) {
        toggleLeftSidebar();
    }

    // Hide the download InfoJSON button during processing
    hideInfoJsonActions();

    const processingIndicator = document.getElementById("processing-indicator");
    processingIndicator.style.display = "block";

    // Reset progress bar
    updateProgressBar(0);

    processButton.textContent = "Processing...";
    processButton.disabled = true;
    const refineButton = document.getElementById("refine-button");
    if (refineButton) {
        refineButton.disabled = true;
    }
    const clearResultsButton = document.getElementById("clear-results-button");
    if (clearResultsButton) {
        clearResultsButton.disabled = true;
    }

    // Disable keypoint labels checkbox during processing
    disableKeypointLabelsCheckbox();

    // Disable arrangement labels checkbox during processing
    disableArrangementLabelsCheckbox();

    let processSucceeded = false;

    try {
        let file, pairedFile, jawSelection, azurePath, pairedAzurePath;

        // Get current input mode
        const inputMode = getInputMode();

        let infoJsonFile, infoJsonAzurePath;
        let landmarksCsvFile, landmarksCsvAzurePath;

        if (processingMode === "arrangement") {
            const arrangementInputs = collectArrangementInputs();
            if (arrangementInputs.error) {
                showToast(arrangementInputs.error, "exclamation-triangle");
                resetProcessButton();
                return;
            }

            // Freeze is a postprocessing control. With postprocessing off the
            // frozen-teeth table (which persists in localStorage and is hidden
            // with the collapsed controls) would be silently dropped - ML fully
            // re-arranges the "frozen" teeth and returns 200. Block here instead,
            // mirroring the server's 400 for fixed_teeth without postprocessing.
            // Read the checkbox and the raw table directly (not
            // collectPostprocessingParameters/collectFixedTeeth) so this gate
            // does not re-run the toast-raising collectors; validation and the
            // per-FDI toasts happen once, later, at the real collection. Count
            // only valid permanent-tooth FDIs, so a stray invalid row does not
            // trip the gate (it would be dropped at collection regardless).
            const postprocessingEnabled = document.getElementById(
                "enable-postprocessing-checkbox"
            )?.checked;
            const hasFrozenTeeth =
                readFixedTeethRaw().some(isPermanentToothFdi);
            if (!postprocessingEnabled && hasFrozenTeeth) {
                showToast(
                    "Frozen teeth require Enable Postprocessing. Enable it or " +
                        "clear the frozen-teeth list before running arrangement.",
                    "exclamation-triangle"
                );
                resetProcessButton();
                return;
            }

            file = arrangementInputs.file;
            pairedFile = arrangementInputs.pairedFile;
            azurePath = arrangementInputs.azurePath;
            pairedAzurePath = arrangementInputs.pairedAzurePath;
            infoJsonFile = arrangementInputs.infoJsonFile;
            infoJsonAzurePath = arrangementInputs.infoJsonAzurePath;
            landmarksCsvFile = arrangementInputs.landmarksCsvFile;
            landmarksCsvAzurePath = arrangementInputs.landmarksCsvAzurePath;
            jawSelection = null; // Not needed for arrangement
        } else {
            if (inputMode === "azure") {
                // Single Azure path for other modes
                azurePath = document
                    .getElementById("azure-path-input")
                    .value.trim();

                if (!azurePath) {
                    showToast(
                        "Please provide an Azure path",
                        "exclamation-triangle"
                    );
                    resetProcessButton();
                    return;
                }

                file = null;
            } else {
                // Single file for other modes
                file = document.getElementById("file-select").files[0];
                azurePath = null;

                if (!file) {
                    showToast("Please select a file", "exclamation-triangle");
                    resetProcessButton();
                    return;
                }
            }

            jawSelection = getJawSelection();
        }

        // Special handling for teeth separation mode
        if (processingMode === "teethseparation") {
            // Store the original mesh for later use
            globalState.separationData.originalMesh = globalState.mesh.clone();
            globalState.separationPhase = 1; // Mark that we're in the segmentation phase

            // Continue with standard teeth segmentation as the first step
            // For Azure mode, file will be null, so use a default size for estimation
            const fileSize = file ? file.size : 1000000; // Default 1MB for Azure mode
            const estimatedTime = estimateProcessingTime(fileSize, "teethml");
            updateProgressBar(5, estimatedTime);

            // Record start time
            const startTime = Date.now();

            // Use the ApiService to upload the file
            const uploadResponse = await ApiService.uploadForProcessing(
                file,
                azurePath,
                "teethml",
                jawSelection,
                isAutoOrientationEnabled()
            );

            // Show warning if present
            if (uploadResponse.warning) {
                showToast(uploadResponse.warning, "exclamation-triangle", 5000);
            }

            const itemId = uploadResponse.item_id;
            globalState.lastProcessedItemId = itemId;

            // Update task label
            document.querySelector(
                "#processing-indicator .progress-task"
            ).textContent = "Processing Teeth Segmentation...";

            // Poll for status until completed using the ApiService
            const decodedResult = await ApiService.pollUntilCompletion(
                itemId,
                updateProgressBar,
                estimatedTime,
                startTime
            );

            // Record processing time
            const processingTimeInSeconds = (Date.now() - startTime) / 1000;
            processingTimeHistory["teethml"].push({
                timeInSeconds: processingTimeInSeconds
            });

            // Keep only the last 5 entries for each model type
            if (processingTimeHistory["teethml"].length > 5) {
                processingTimeHistory["teethml"].shift();
            }

            // Apply segmentation colors
            applyInferenceColors(decodedResult);

            // Short delay to show 100% completion before hiding
            await new Promise((resolve) => setTimeout(resolve, 750));

            // Hide processing indicator
            processingIndicator.style.display = "none";

            // Show the "next phase" button
            const nextPhaseButton =
                document.getElementById("next-phase-button");
            nextPhaseButton.style.display = "block";
            nextPhaseButton.textContent = "Continue to Separation";

            showToast(
                "Segmentation completed. Click 'Continue' to proceed.",
                "check-circle"
            );

            // Show start over button if sidebar is collapsed
            showStartOverAndFeedbackButtons();
            updateStartOverButtonVisibility();

            processSucceeded = true;
        } else {
            // Standard processing for other modes
            // Estimate processing time based on file size and model
            // For Azure mode, file will be null, so use a default size for estimation
            const fileSize = file ? file.size : 1000000; // Default 1MB for Azure mode
            const estimatedTime = estimateProcessingTime(
                fileSize,
                processingMode
            );
            updateProgressBar(5, estimatedTime);

            // Record start time
            const startTime = Date.now();

            // Update task label
            document.querySelector(
                "#processing-indicator .progress-task"
            ).textContent = `Processing ${processingMode.toUpperCase()}...`;

            let uploadResponse, itemId;

            if (processingMode === "arrangement") {
                const options = collectArrangementModelOptions();
                const postprocessingData = collectPostprocessingParameters();
                if (
                    postprocessingData.enabled &&
                    postprocessingData.parameters
                ) {
                    options.postprocessing = {
                        ...postprocessingData.parameters
                    };
                }
                const levelingData = collectLevelingParameters();
                if (levelingData.enabled && levelingData.parameters) {
                    options.leveling = { ...levelingData.parameters };
                }

                // For arrangement, use the special upload method with both files.
                // When Enable Postprocessing is checked, ML inference runs and refinement
                // (ArrangementRefiner) runs in the same worker task; otherwise inference only.
                // When Leveling refinement is checked, LevelingRefiner runs between ML
                // inference and ArrangementRefiner inside the same worker task.
                uploadResponse = await ApiService.uploadForArrangement(
                    file, // lower jaw file
                    azurePath, // lower jaw Azure path
                    pairedFile, // upper jaw file
                    pairedAzurePath, // upper jaw Azure path
                    infoJsonFile, // optional InfoJSON file
                    infoJsonAzurePath, // optional InfoJSON Azure path
                    landmarksCsvFile, // optional landmarks CSV file
                    landmarksCsvAzurePath, // optional landmarks CSV Azure path
                    options,
                    postprocessingData.enabled,
                    levelingData.enabled
                );

                handleArrangementWarnings(uploadResponse);

                itemId = uploadResponse.item_id;
            } else {
                // Use the standard upload method for other modes
                uploadResponse = await ApiService.uploadForProcessing(
                    file,
                    azurePath,
                    processingMode,
                    jawSelection,
                    isAutoOrientationEnabled()
                );

                // Log upload response for debugging
                console.log("Upload response received:", uploadResponse);

                // Show warning if present
                if (uploadResponse && uploadResponse.warning) {
                    console.log(
                        "Showing warning toast:",
                        uploadResponse.warning
                    );
                    showToast(
                        uploadResponse.warning,
                        "exclamation-triangle",
                        5000
                    );
                } else {
                    console.log("No warning in upload response");
                }

                itemId = uploadResponse.item_id;
            }
            globalState.lastProcessedItemId = itemId;

            // Poll for status until completed using the ApiService
            const decodedResult = await ApiService.pollUntilCompletion(
                itemId,
                updateProgressBar,
                estimatedTime,
                startTime
            );

            // Record processing time
            const processingTimeInSeconds = (Date.now() - startTime) / 1000;
            processingTimeHistory[processingMode].push({
                timeInSeconds: processingTimeInSeconds
            });

            // Keep only the last 5 entries for each model type
            if (processingTimeHistory[processingMode].length > 5) {
                processingTimeHistory[processingMode].shift();
            }

            // Process results based on the mode
            if (processingMode === "keypointml") {
                addKeypoints(decodedResult.keypoints);
                // Enable keypoint labels checkbox for keypoint detection mode
                enableKeypointLabelsCheckbox();
                // Disable arrangement labels checkbox (not used in keypoint mode)
                disableArrangementLabelsCheckbox();
            } else if (processingMode === "defectml") {
                applyInferenceColors(decodedResult);
                // Disable keypoint labels checkbox for defect segmentation mode
                disableKeypointLabelsCheckbox();
                // Disable arrangement labels checkbox (not used in defect mode)
                disableArrangementLabelsCheckbox();
            } else if (processingMode === "arrangement") {
                await displayArrangementResults(
                    decodedResult.arrangement_result
                );
                updateAchievedOverbite(
                    decodedResult.arrangement_result?.leveling_stats
                        ?.leveling_targets?.achieved_overbite
                );
                // Disable keypoint labels checkbox for arrangement mode
                disableKeypointLabelsCheckbox();
                // Enable arrangement labels checkbox for arrangement mode
                enableArrangementLabelsCheckbox();
            } else {
                applyInferenceColors(decodedResult);
                addBoundaryPointsAndLines(
                    decodedResult.boundary_points,
                    decodedResult.simplified_boundary_points
                );
                // Disable keypoint labels checkbox for teeth segmentation mode
                disableKeypointLabelsCheckbox();
                // Disable arrangement labels checkbox (not used in segmentation mode)
                disableArrangementLabelsCheckbox();
            }

            // Short delay to show 100% completion before hiding
            await new Promise((resolve) => setTimeout(resolve, 750));

            // Hide processing indicator to show results
            processingIndicator.style.display = "none";
            showToast("Processing completed", "check-circle");

            // Initialize feedback system after successful processing
            setTimeout(() => {
                initializeFeedback(processingMode, decodedResult);
            }, 1000);

            // Show start over button if sidebar is collapsed
            showStartOverAndFeedbackButtons();
            updateStartOverButtonVisibility();

            processSucceeded = true;
        }
    } catch (error) {
        console.error("Error during inference:", error);

        // Show detailed error message
        const errorMessage =
            error.message ||
            error.response?.data?.detail ||
            "Error during inference";
        showToast(errorMessage, "exclamation-triangle");

        // Make sure to hide the indicator on error
        document.getElementById("processing-indicator").style.display = "none";
    } finally {
        globalState.processingFile = false;
        processButton.textContent = "Process";
        processButton.disabled = false;

        if (processSucceeded) {
            // Lock file/mode inputs until user clicks Clear
            disableAllInputs();
        }
        updateClearButtonFromState();
        updateArrangementActionButtons();
    }
}

function addKeypoints(keypoints) {
    if (!keypoints || !Array.isArray(keypoints)) {
        console.warn("Invalid keypoints data:", keypoints);
        return;
    }

    // Remove any existing keypoints and labels
    for (let i = globalState.scene.children.length - 1; i >= 0; i--) {
        const child = globalState.scene.children[i];
        if (child.name?.startsWith("keypoint-")) {
            globalState.scene.remove(child);
        }
    }
    globalState.keypoints = {};
    globalState.keypointLabels = [];

    // Create material for keypoints
    const sphereGeometry = new THREE.SphereGeometry(0.5, 32, 32);

    try {
        keypoints.forEach((keypoint) => {
            // Validate keypoint data
            if (
                !keypoint ||
                typeof keypoint.x !== "number" ||
                typeof keypoint.y !== "number" ||
                typeof keypoint.z !== "number"
            ) {
                console.warn("Invalid keypoint data:", keypoint);
                return;
            }

            // Skip if coordinates are not finite numbers
            if (
                !isFinite(keypoint.x) ||
                !isFinite(keypoint.y) ||
                !isFinite(keypoint.z)
            ) {
                console.warn("Non-finite keypoint coordinates:", keypoint);
                return;
            }

            // Convert from snake_case to camelCase if needed
            const className =
                keypoint.className || keypoint.class_name || "unknown";
            const toothId = keypoint.toothId || keypoint.tooth_id || null;

            // Find keypoint class info
            const keypointClass = keypointClasses.find(
                (c) => c.name === className
            );
            if (!keypointClass) {
                console.warn(`Unknown keypoint class: ${className}`);
                return;
            }

            // Create unique ID for the keypoint
            const pointName =
                keypoint.pointName ||
                `keypoint-point-${Math.random().toString(36).substr(2, 9)}`;

            // Prepare keypoint object for storage
            const keypointObject = {
                x: keypoint.x,
                y: keypoint.y,
                z: keypoint.z,
                className,
                pointName,
                toothId
            };

            // Store keypoint in global state
            globalState.keypoints[pointName] = keypointObject;

            // Create visual representation of keypoint
            const sphereMaterial = new THREE.MeshBasicMaterial({
                color: new THREE.Color(keypointClass.color)
            });

            const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
            sphere.position.set(keypoint.x, keypoint.y, keypoint.z);
            sphere.name = pointName;
            globalState.scene.add(sphere);

            // Create label for keypoint
            const labelDiv = document.createElement("div");
            labelDiv.className = "keypoint-label";
            labelDiv.textContent = globalState.showToothIDForKeypoints
                ? `${className} (${toothId || "?"})`
                : className;
            labelDiv.style.color = keypointClass.color;
            labelDiv.style.backgroundColor = "rgba(0,0,0,0.5)";
            labelDiv.style.padding = "2px 6px";
            labelDiv.style.borderRadius = "3px";
            labelDiv.style.fontSize = "12px";

            const label = new CSS2DObject(labelDiv);
            label.position.set(keypoint.x, keypoint.y, keypoint.z);
            label.name = `keypoint-label-${pointName}`;
            label.visible = globalState.showKeypointLabels;
            globalState.scene.add(label);
            globalState.keypointLabels.push(label);
        });

        // Draw marginal ridge lines
        if (globalState.showMarginalRidgeLines) {
            drawMarginalRidgeLines(globalState);
        }
    } catch (error) {
        console.error("Error adding keypoints:", error);
    }
}

function applyInferenceColors(inferenceResult) {
    if (!globalState.mesh || !inferenceResult) {
        console.warn("Missing mesh or inference result");
        return;
    }

    // Store the inference result for later download
    globalState.lastInferenceResult = inferenceResult;

    // Ensure labels are properly decoded
    let labels = inferenceResult.labels;
    if (!labels) {
        console.warn("No labels found in inference result");
        return;
    }

    // If labels are in compressed format, decode them
    if (typeof labels === "object" && labels.encoding === "runlength") {
        labels = decodeRunLength(labels.data);
    }

    const geometry = globalState.mesh.geometry;
    const colors = geometry.attributes.color;

    // Remove existing class labels
    globalState.classLabels.forEach((label) => globalState.scene.remove(label));
    globalState.classLabels = [];

    const labelPositions = {};

    const processingMode =
        document.getElementById("processing-mode-dropdown")?.value ||
        "arrangement";
    const classesData =
        processingMode === "defectml" ? defectClasses : toothClasses;

    for (let i = 0; i < labels.length; i++) {
        const label = labels[i];
        const color = new THREE.Color(getColorForLabel(label, processingMode));
        colors.setXYZ(i, color.r, color.g, color.b);

        // Calculate average position for each class
        if (!labelPositions[label]) {
            labelPositions[label] = { sum: new THREE.Vector3(), count: 0 };
        }
        const vertex = new THREE.Vector3().fromBufferAttribute(
            geometry.attributes.position,
            i
        );
        labelPositions[label].sum.add(vertex);
        labelPositions[label].count++;
    }

    colors.needsUpdate = true;

    // Create and position class labels
    Object.entries(labelPositions).forEach(([label, data]) => {
        const avgPosition = data.sum.divideScalar(data.count);
        const classData = classesData.find((c) => c.id === parseInt(label));
        if (classData) {
            const labelDiv = document.createElement("div");
            labelDiv.className = "tooth-label";
            labelDiv.textContent = classData.name;
            labelDiv.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
            labelDiv.style.color = "white";
            labelDiv.style.padding = "2px 5px";
            labelDiv.style.borderRadius = "3px";
            const labelObject = new CSS2DObject(labelDiv);
            labelObject.position.copy(avgPosition);
            globalState.scene.add(labelObject);
            globalState.classLabels.push(labelObject);
        } else {
            console.warn("No class data found for label:", label);
        }
    });

    // After applying colors, show the download button
    showDownloadButton();
}

function getColorForLabel(label, processingMode) {
    const classesData =
        processingMode === "defectml" ? defectClasses : toothClasses;
    const classData = classesData.find((c) => c.id === parseInt(label));
    return classData ? classData.color : "#FFFFFF";
}

function addBoundaryPointsAndLines(
    boundaryPointsData,
    simplifiedBoundaryPointsData
) {
    // Remove existing boundary points and lines
    if (globalState.boundaryPoints)
        globalState.boundaryScene.remove(globalState.boundaryPoints);
    if (globalState.boundaryLines)
        globalState.boundaryScene.remove(globalState.boundaryLines);
    if (globalState.simplifiedBoundaryPoints)
        globalState.simplifiedBoundaryScene.remove(
            globalState.simplifiedBoundaryPoints
        );
    if (globalState.simplifiedBoundaryLines)
        globalState.simplifiedBoundaryScene.remove(
            globalState.simplifiedBoundaryLines
        );

    globalState.boundaryPoints = new THREE.Group();
    globalState.boundaryLines = new THREE.Group();
    globalState.simplifiedBoundaryPoints = new THREE.Group();
    globalState.simplifiedBoundaryLines = new THREE.Group();
    const sphereGeometry = new THREE.SphereGeometry(0.2, 32, 32);

    function drawLines(points, lines, color) {
        if (!Array.isArray(points) || points.length === 0) {
            console.warn("Invalid points array for drawing lines:", points);
            return;
        }

        const lineMaterial = new THREE.LineBasicMaterial({
            color: color,
            linewidth: 2
        });
        for (let i = 0; i < points.length; i++) {
            // Validate point array structure
            if (!Array.isArray(points[i]) || points[i].length < 3) {
                console.warn(`Invalid point at index ${i}:`, points[i]);
                continue;
            }

            // Validate next point
            const nextIdx = (i + 1) % points.length;
            if (!Array.isArray(points[nextIdx]) || points[nextIdx].length < 3) {
                console.warn(
                    `Invalid next point at index ${nextIdx}:`,
                    points[nextIdx]
                );
                continue;
            }

            const start = new THREE.Vector3(
                points[i][0],
                points[i][1],
                points[i][2]
            );
            const end = new THREE.Vector3(
                points[nextIdx][0],
                points[nextIdx][1],
                points[nextIdx][2]
            );

            // Skip if any coordinates are not finite numbers
            if (
                !isFinite(start.x) ||
                !isFinite(start.y) ||
                !isFinite(start.z) ||
                !isFinite(end.x) ||
                !isFinite(end.y) ||
                !isFinite(end.z)
            ) {
                console.warn("Non-finite coordinates detected:", {
                    start,
                    end
                });
                continue;
            }

            if (start.distanceTo(end) < 3) {
                const lineGeometry = new THREE.BufferGeometry().setFromPoints([
                    start,
                    end
                ]);
                const line = new THREE.Line(lineGeometry, lineMaterial);
                lines.add(line);
            }
        }
    }

    try {
        if (boundaryPointsData && typeof boundaryPointsData === "object") {
            Object.entries(boundaryPointsData).forEach(
                ([toothClass, points]) => {
                    if (Array.isArray(points) && points.length > 0) {
                        const color = new THREE.Color(
                            getColorForLabel(parseInt(toothClass), "toothml")
                        );
                        const sphereMaterial = new THREE.MeshBasicMaterial({
                            color: color
                        });

                        points.forEach((point) => {
                            if (!Array.isArray(point) || point.length < 3) {
                                console.warn("Invalid point:", point);
                                return;
                            }

                            if (
                                !isFinite(point[0]) ||
                                !isFinite(point[1]) ||
                                !isFinite(point[2])
                            ) {
                                console.warn(
                                    "Non-finite point coordinates:",
                                    point
                                );
                                return;
                            }

                            const sphere = new THREE.Mesh(
                                sphereGeometry,
                                sphereMaterial
                            );
                            sphere.position.set(point[0], point[1], point[2]);
                            globalState.boundaryPoints.add(sphere);
                        });

                        drawLines(points, globalState.boundaryLines, color);
                    } else {
                        console.warn(
                            `No valid points array for tooth class ${toothClass}:`,
                            points
                        );
                    }
                }
            );
        } else {
            console.warn(
                "Invalid or missing boundary points data:",
                boundaryPointsData
            );
        }

        if (
            simplifiedBoundaryPointsData &&
            typeof simplifiedBoundaryPointsData === "object"
        ) {
            Object.entries(simplifiedBoundaryPointsData).forEach(
                ([toothClass, points]) => {
                    if (Array.isArray(points) && points.length > 0) {
                        const color = new THREE.Color(
                            getColorForLabel(parseInt(toothClass))
                        );
                        const sphereMaterial = new THREE.MeshBasicMaterial({
                            color: color
                        });

                        points.forEach((point) => {
                            if (!Array.isArray(point) || point.length < 3) {
                                console.warn(
                                    "Invalid simplified point:",
                                    point
                                );
                                return;
                            }

                            if (
                                !isFinite(point[0]) ||
                                !isFinite(point[1]) ||
                                !isFinite(point[2])
                            ) {
                                console.warn(
                                    "Non-finite simplified point coordinates:",
                                    point
                                );
                                return;
                            }

                            const sphere = new THREE.Mesh(
                                sphereGeometry,
                                sphereMaterial
                            );
                            sphere.position.set(point[0], point[1], point[2]);
                            globalState.simplifiedBoundaryPoints.add(sphere);
                        });

                        drawLines(
                            points,
                            globalState.simplifiedBoundaryLines,
                            color
                        );
                    } else {
                        console.warn(
                            `No valid simplified points array for tooth class ${toothClass}:`,
                            points
                        );
                    }
                }
            );
        } else {
            console.warn(
                "Invalid or missing simplified boundary points data:",
                simplifiedBoundaryPointsData
            );
        }

        globalState.boundaryScene.add(globalState.boundaryPoints);
        globalState.boundaryScene.add(globalState.boundaryLines);
        globalState.simplifiedBoundaryScene.add(
            globalState.simplifiedBoundaryPoints
        );
        globalState.simplifiedBoundaryScene.add(
            globalState.simplifiedBoundaryLines
        );
    } catch (error) {
        console.error("Error adding boundary points and lines:", error);
    }
}

function updateMeshVisualization() {
    if (!globalState.mesh) return;

    // Remove existing visualizations
    globalState.scene.remove(globalState.scene.getObjectByName("wireframe"));
    globalState.scene.remove(globalState.scene.getObjectByName("pointcloud"));

    if (globalState.showWireframe) {
        const wireframe = new THREE.LineSegments(
            new THREE.WireframeGeometry(globalState.mesh.geometry),
            globalState.wireframeMaterial
        );
        wireframe.name = "wireframe";
        globalState.scene.add(wireframe);
    }

    if (globalState.showPointCloud) {
        const pointCloud = new THREE.Points(
            globalState.mesh.geometry,
            globalState.pointsMaterial
        );
        pointCloud.name = "pointcloud";
        globalState.scene.add(pointCloud);
    }

    globalState.mesh.visible = !(
        globalState.showWireframe || globalState.showPointCloud
    );
}

function updateLighting() {
    const lightingAmount = 1.5; // Fixed lighting amount
    globalState.ambientLight.intensity = 0.5 * lightingAmount;
    globalState.directionalLight1.intensity = 0.5 * lightingAmount;
    globalState.directionalLight2.intensity = 0.5 * lightingAmount;
}

/**
 * Update brightness for all scenes and views
 */
function updateBrightness() {
    const brightnessMultiplier = globalState.brightnessAmount;

    // Update main scene lighting
    if (globalState.ambientLight) {
        globalState.ambientLight.intensity = 0.3 * brightnessMultiplier;
    }
    if (globalState.directionalLight1) {
        globalState.directionalLight1.intensity = 0.7 * brightnessMultiplier;
    }
    if (globalState.directionalLight2) {
        globalState.directionalLight2.intensity = 0.5 * brightnessMultiplier;
    }

    // Update boundary scene lighting
    if (globalState.boundaryScene) {
        globalState.boundaryScene.children.forEach((child) => {
            if (child instanceof THREE.AmbientLight) {
                child.intensity = 0.3 * brightnessMultiplier;
            } else if (child instanceof THREE.DirectionalLight) {
                if (child.position.x > 0) {
                    child.intensity = 0.7 * brightnessMultiplier;
                } else {
                    child.intensity = 0.5 * brightnessMultiplier;
                }
            }
        });
    }

    // Update simplified boundary scene lighting
    if (globalState.simplifiedBoundaryScene) {
        globalState.simplifiedBoundaryScene.children.forEach((child) => {
            if (child instanceof THREE.AmbientLight) {
                child.intensity = 0.3 * brightnessMultiplier;
            } else if (child instanceof THREE.DirectionalLight) {
                if (child.position.x > 0) {
                    child.intensity = 0.7 * brightnessMultiplier;
                } else {
                    child.intensity = 0.5 * brightnessMultiplier;
                }
            }
        });
    }

    // Update multi-view scenes lighting
    if (globalState.multiViewScenes) {
        Object.values(globalState.multiViewScenes).forEach((scene) => {
            if (scene) {
                scene.children.forEach((child) => {
                    if (child instanceof THREE.AmbientLight) {
                        child.intensity = 0.4 * brightnessMultiplier;
                    } else if (child instanceof THREE.DirectionalLight) {
                        if (child.position.x > 0) {
                            child.intensity = 0.6 * brightnessMultiplier;
                        } else {
                            child.intensity = 0.4 * brightnessMultiplier;
                        }
                    }
                });
            }
        });
    }
}

/**
 * Helper function to hide all boundary views
 */
function hideAllBoundaryViews() {
    if (
        globalState.boundaryRenderer &&
        globalState.boundaryRenderer.domElement
    ) {
        globalState.boundaryRenderer.domElement.style.display = "none";
    }
    if (
        globalState.boundaryLabelRenderer &&
        globalState.boundaryLabelRenderer.domElement
    ) {
        globalState.boundaryLabelRenderer.domElement.style.display = "none";
    }
    if (
        globalState.simplifiedBoundaryRenderer &&
        globalState.simplifiedBoundaryRenderer.domElement
    ) {
        globalState.simplifiedBoundaryRenderer.domElement.style.display =
            "none";
    }
    if (
        globalState.simplifiedBoundaryLabelRenderer &&
        globalState.simplifiedBoundaryLabelRenderer.domElement
    ) {
        globalState.simplifiedBoundaryLabelRenderer.domElement.style.display =
            "none";
    }
}

/**
 * Helper function to show all boundary views
 */
function showAllBoundaryViews() {
    if (
        globalState.boundaryRenderer &&
        globalState.boundaryRenderer.domElement
    ) {
        globalState.boundaryRenderer.domElement.style.display = "block";
    }
    if (
        globalState.boundaryLabelRenderer &&
        globalState.boundaryLabelRenderer.domElement
    ) {
        globalState.boundaryLabelRenderer.domElement.style.display = "block";
    }
    if (
        globalState.simplifiedBoundaryRenderer &&
        globalState.simplifiedBoundaryRenderer.domElement
    ) {
        globalState.simplifiedBoundaryRenderer.domElement.style.display =
            "block";
    }
    if (
        globalState.simplifiedBoundaryLabelRenderer &&
        globalState.simplifiedBoundaryLabelRenderer.domElement
    ) {
        globalState.simplifiedBoundaryLabelRenderer.domElement.style.display =
            "block";
    }
}

/**
 * Helper function to hide view separators
 */
function hideViewSeparators() {
    document.querySelectorAll(".view-separator").forEach((separator) => {
        separator.style.display = "none";
    });
}

/**
 * Helper function to show view separators
 */
function showViewSeparators() {
    document.querySelectorAll(".view-separator").forEach((separator) => {
        separator.style.display = "block";
    });
}

/**
 * Setup teeth separation multi-view layout for 2x2 grid
 */
function setupTeethSeparationMultiViewLayout() {
    // First ensure we start with a clean slate
    resetMultiViewContainer();

    // Make sure multi-view container is visible and configure for 2x2 grid
    const multiViewContainer = document.getElementById("multi-view-container");
    if (multiViewContainer) {
        // Force clear any existing styles first
        multiViewContainer.removeAttribute("style");

        // Now set the proper 2x2 grid layout
        multiViewContainer.style.display = "grid";
        multiViewContainer.style.gridTemplateColumns = "1fr 1fr";
        multiViewContainer.style.gridTemplateRows = "1fr 1fr";
        multiViewContainer.style.gap = "2px";
        multiViewContainer.style.width = "100%";
        multiViewContainer.style.height = "100%";
        multiViewContainer.style.position = "absolute";
        multiViewContainer.style.top = "0";
        multiViewContainer.style.left = "0";
    }

    // Configure ALL FOUR view panels for 2x2 grid
    const viewPanels = [
        "original-view",
        "segmented-view",
        "analysis-view",
        "final-view"
    ];
    viewPanels.forEach((panelId) => {
        const panel = document.getElementById(panelId);
        if (panel) {
            // Force clear existing styles
            panel.removeAttribute("style");

            // Set proper styles for 2x2 grid
            panel.style.width = "100%";
            panel.style.height = "100%";
            panel.style.display = "block";
            panel.style.position = "relative";
            panel.style.overflow = "hidden";
        }
    });

    // Calculate view dimensions for each quadrant
    const viewWidth = (window.innerWidth - 2) / 2; // Account for 2px gap
    const viewHeight = (window.innerHeight - 2) / 2; // Account for 2px gap

    // Configure renderers for all views
    Object.keys(globalState.multiViewRenderers).forEach((view) => {
        if (
            globalState.multiViewRenderers[view] &&
            globalState.multiViewCameras[view]
        ) {
            // Use full quadrant proportions
            const aspect = viewWidth / viewHeight;
            updateOrthographicCamera(
                globalState.multiViewCameras[view],
                aspect
            );
            globalState.multiViewRenderers[view].setSize(viewWidth, viewHeight);

            // Also resize label renderers
            if (globalState.multiViewLabelRenderers[view]) {
                globalState.multiViewLabelRenderers[view].setSize(
                    viewWidth,
                    viewHeight
                );
            }
        }
    });

    // Make sure main view is hidden during multi-view
    globalState.renderer.domElement.style.display = "none";
    globalState.labelRenderer.domElement.style.display = "none";

    // Hide boundary views during reconstruction
    hideAllBoundaryViews();
    hideViewSeparators();
}

function onWindowResize() {
    const processingMode =
        document.getElementById("processing-mode-dropdown")?.value ||
        "arrangement";

    // Get the main viewer container dimensions
    const mainViewer = document.getElementById("main-viewer");
    if (!mainViewer) {
        console.warn("Main viewer container not found");
        return;
    }

    const viewerRect = mainViewer.getBoundingClientRect();
    const viewerWidth = viewerRect.width;
    const viewerHeight = viewerRect.height;

    // Check if multi-view container is visible (arrangement dual view active)
    const multiViewContainer = document.getElementById("multi-view-container");
    const overlayViewContainer = document.getElementById(
        "overlay-view-container"
    );
    const isArrangementDualView =
        multiViewContainer &&
        multiViewContainer.style.display !== "none" &&
        processingMode === "arrangement";
    const isArrangementOverlayView =
        overlayViewContainer &&
        overlayViewContainer.style.display !== "none" &&
        processingMode === "arrangement";

    if (isArrangementOverlayView) {
        // Handle arrangement overlay view layout - single full view
        if (globalState.overlayRenderer && globalState.overlayCamera) {
            const aspect = viewerWidth / viewerHeight;
            updateOrthographicCamera(globalState.overlayCamera, aspect);
            globalState.overlayRenderer.setSize(viewerWidth, viewerHeight);
            if (globalState.overlayLabelRenderer) {
                globalState.overlayLabelRenderer.setSize(
                    viewerWidth,
                    viewerHeight
                );
            }
        }
    } else if (isArrangementDualView) {
        // Handle arrangement dual view layout - 1x2 side-by-side
        setupArrangementMultiViewLayout();
    } else if (
        processingMode === "defectml" ||
        processingMode === "keypointml" ||
        (processingMode === "teethseparation" &&
            globalState.separationPhase === 0) ||
        (processingMode === "arrangement" && !isArrangementDualView)
    ) {
        // Full viewer single view for:
        // - defect or keypoint detection
        // - initial phase of teeth separation (phase 0)
        // - arrangement loading/preview mode
        const aspect = viewerWidth / viewerHeight;
        updateOrthographicCamera(globalState.camera, aspect);
        globalState.renderer.setSize(viewerWidth, viewerHeight);
        globalState.labelRenderer.setSize(viewerWidth, viewerHeight);

        // Make sure main view is visible
        globalState.renderer.domElement.style.display = "block";
        globalState.labelRenderer.domElement.style.display = "block";

        // Hide boundary views and multi-view
        hideAllBoundaryViews();
        hideViewSeparators();
        if (multiViewContainer) {
            multiViewContainer.style.display = "none";
        }
    } else if (processingMode === "teethseparation") {
        // Handle teeth separation mode based on phase and multi-view status
        if (globalState.renderMultiView) {
            // Phase 2+ with multi-view layout - use full screen with 2x2 grid
            setupTeethSeparationMultiViewLayout();
        } else if (globalState.separationPhase === 1) {
            // Phase 1 (segmentation) - use full view

            hideAllBoundaryViews();
            hideViewSeparators();

            // Make sure main view is visible and sized correctly
            globalState.renderer.domElement.style.display = "block";
            globalState.labelRenderer.domElement.style.display = "block";

            // Adjust main view to use full viewer
            const aspect = viewerWidth / viewerHeight;
            updateOrthographicCamera(globalState.camera, aspect);
            globalState.renderer.setSize(viewerWidth, viewerHeight);
            globalState.labelRenderer.setSize(viewerWidth, viewerHeight);

            // Hide multi-view container if it exists
            const multiViewContainer = document.getElementById(
                "multi-view-container"
            );
            if (multiViewContainer) {
                multiViewContainer.style.display = "none";
            }

            // Still update boundary camera settings for when they might be shown again
            const boundaryAspect =
                window.innerWidth / 2 / (window.innerHeight / 2);
            updateOrthographicCamera(
                globalState.boundaryCamera,
                boundaryAspect
            );
            globalState.boundaryRenderer.setSize(
                window.innerWidth / 2,
                window.innerHeight / 2
            );
            globalState.boundaryLabelRenderer.setSize(
                window.innerWidth / 2,
                window.innerHeight / 2
            );

            const simplifiedBoundaryAspect =
                window.innerWidth / 2 / (window.innerHeight / 2);
            updateOrthographicCamera(
                globalState.simplifiedBoundaryCamera,
                simplifiedBoundaryAspect
            );
            globalState.simplifiedBoundaryRenderer.setSize(
                window.innerWidth / 2,
                window.innerHeight / 2
            );
            globalState.simplifiedBoundaryLabelRenderer.setSize(
                window.innerWidth / 2,
                window.innerHeight / 2
            );
        } else {
            // Teeth separation phase 0 or other phases - single view

            // Make sure main view is visible
            globalState.renderer.domElement.style.display = "block";
            globalState.labelRenderer.domElement.style.display = "block";

            // Full window single view
            const aspect = window.innerWidth / window.innerHeight;
            updateOrthographicCamera(globalState.camera, aspect);
            globalState.renderer.setSize(window.innerWidth, window.innerHeight);
            globalState.labelRenderer.setSize(
                window.innerWidth,
                window.innerHeight
            );

            // Hide multi-view and boundary views
            const multiViewContainer = document.getElementById(
                "multi-view-container"
            );
            if (multiViewContainer) {
                multiViewContainer.style.display = "none";
            }
            hideAllBoundaryViews();
            hideViewSeparators();
        }
    } else {
        // Default split view layout for standard segmentation
        showAllBoundaryViews();
        showViewSeparators();

        const aspect = window.innerWidth / 2 / window.innerHeight;
        updateOrthographicCamera(globalState.camera, aspect);
        globalState.renderer.setSize(window.innerWidth / 2, window.innerHeight);
        globalState.labelRenderer.setSize(
            window.innerWidth / 2,
            window.innerHeight
        );

        const boundaryAspect = window.innerWidth / 2 / (window.innerHeight / 2);
        updateOrthographicCamera(globalState.boundaryCamera, boundaryAspect);
        globalState.boundaryRenderer.setSize(
            window.innerWidth / 2,
            window.innerHeight / 2
        );
        globalState.boundaryLabelRenderer.setSize(
            window.innerWidth / 2,
            window.innerHeight / 2
        );

        const simplifiedBoundaryAspect =
            window.innerWidth / 2 / (window.innerHeight / 2);
        updateOrthographicCamera(
            globalState.simplifiedBoundaryCamera,
            simplifiedBoundaryAspect
        );
        globalState.simplifiedBoundaryRenderer.setSize(
            window.innerWidth / 2,
            window.innerHeight / 2
        );
        globalState.simplifiedBoundaryLabelRenderer.setSize(
            window.innerWidth / 2,
            window.innerHeight / 2
        );

        // Hide multi-view for standard modes
        if (multiViewContainer) {
            multiViewContainer.style.display = "none";
        }
    }
}

function animate() {
    requestAnimationFrame(animate);

    globalState.controls.update();
    globalState.boundaryControls.update();
    globalState.simplifiedBoundaryControls.update();

    // Render main view
    globalState.renderer.render(globalState.scene, globalState.camera);
    globalState.labelRenderer.render(globalState.scene, globalState.camera);

    // Only render boundary views if not in teethseparation mode or if we're not in phase 1
    const shouldRenderBoundaryViews =
        globalState.processingMode !== "teethseparation" ||
        (globalState.processingMode === "teethseparation" &&
            globalState.separationPhase !== 1);

    if (shouldRenderBoundaryViews) {
        globalState.boundaryRenderer.render(
            globalState.boundaryScene,
            globalState.boundaryCamera
        );
        globalState.boundaryLabelRenderer.render(
            globalState.boundaryScene,
            globalState.boundaryCamera
        );
        globalState.simplifiedBoundaryRenderer.render(
            globalState.simplifiedBoundaryScene,
            globalState.simplifiedBoundaryCamera
        );
        globalState.simplifiedBoundaryLabelRenderer.render(
            globalState.simplifiedBoundaryScene,
            globalState.simplifiedBoundaryCamera
        );
    }

    // Render multi-view if active
    if (globalState.renderMultiView) {
        // Check if this is the first time multi-view is being rendered and setup layout
        if (!globalState.multiViewLayoutInitialized) {
            // Check processing mode to determine layout type
            const processingMode =
                document.getElementById("processing-mode-dropdown")?.value ||
                "arrangement";

            if (processingMode === "arrangement") {
                setupArrangementMultiViewLayout();
            } else {
                setupTeethSeparationMultiViewLayout();
            }
            globalState.multiViewLayoutInitialized = true;

            // Force a layout refresh after a brief delay to ensure changes take effect
            setTimeout(() => {
                if (globalState.renderMultiView) {
                    if (processingMode === "arrangement") {
                        setupArrangementMultiViewLayout();
                    } else {
                        setupTeethSeparationMultiViewLayout();
                    }
                }
            }, 100);
        }

        Object.keys(globalState.multiViewRenderers).forEach((view) => {
            if (
                globalState.multiViewRenderers[view] &&
                globalState.multiViewScenes[view]
            ) {
                globalState.multiViewControls[view].update();
                globalState.multiViewRenderers[view].render(
                    globalState.multiViewScenes[view],
                    globalState.multiViewCameras[view]
                );

                // Render CSS2D labels
                if (globalState.multiViewLabelRenderers[view]) {
                    globalState.multiViewLabelRenderers[view].render(
                        globalState.multiViewScenes[view],
                        globalState.multiViewCameras[view]
                    );
                }
            }
        });
    }

    // Render overlay view if active
    if (
        globalState.overlayViewMode === "overlay" &&
        globalState.overlayRenderer &&
        globalState.overlayScene
    ) {
        globalState.overlayControls.update();
        globalState.overlayRenderer.render(
            globalState.overlayScene,
            globalState.overlayCamera
        );
        if (globalState.overlayLabelRenderer) {
            globalState.overlayLabelRenderer.render(
                globalState.overlayScene,
                globalState.overlayCamera
            );
        }
    }
}

// Function to proceed to the next phase of teeth separation
function proceedToNextPhase() {
    const nextPhaseButton = document.getElementById("next-phase-button");

    if (globalState.separationPhase === 1) {
        // Moving from segmentation to separation
        globalState.separationPhase = 2;
        nextPhaseButton.style.display = "none";

        // Store the segmented mesh data
        globalState.separationData.segmentedMesh = globalState.mesh.clone();

        // Begin teeth reconstruction processing
        processSeparation();
    } else if (nextPhaseButton.textContent === "Try Again") {
        // Handle the "Try Again" case
        nextPhaseButton.style.display = "none";

        // Reset any failed state and try the separation again
        processSeparation();
    }
}

/**
 * Initialize multi-view renderers for arrangement display
 */
function initializeMultiViewRenderers() {
    const views = ["original", "segmented", "analysis", "final"];

    views.forEach((view) => {
        // Create scene
        if (!globalState.multiViewScenes[view]) {
            globalState.multiViewScenes[view] = new THREE.Scene();
            globalState.multiViewScenes[view].background =
                createGradientTexture();
        }

        // Create camera
        if (!globalState.multiViewCameras[view]) {
            const multiViewAspect =
                window.innerWidth / 2 / (window.innerHeight / 2);
            globalState.multiViewCameras[view] =
                createOrthographicCamera(multiViewAspect);
            globalState.multiViewCameras[view].position.set(0, 0, 60);
        }

        // Create renderer
        if (!globalState.multiViewRenderers[view]) {
            globalState.multiViewRenderers[view] = new THREE.WebGLRenderer({
                antialias: true
            });
            globalState.multiViewRenderers[view].setSize(
                window.innerWidth / 2,
                window.innerHeight / 2
            );

            const viewElement = document.getElementById(`${view}-view`);
            if (viewElement) {
                viewElement.appendChild(
                    globalState.multiViewRenderers[view].domElement
                );
            }
        }

        // Create CSS2D label renderer
        if (!globalState.multiViewLabelRenderers[view]) {
            globalState.multiViewLabelRenderers[view] = new CSS2DRenderer();
            globalState.multiViewLabelRenderers[view].setSize(
                window.innerWidth / 2,
                window.innerHeight / 2
            );
            globalState.multiViewLabelRenderers[
                view
            ].domElement.style.position = "absolute";
            globalState.multiViewLabelRenderers[view].domElement.style.top =
                "0";
            globalState.multiViewLabelRenderers[view].domElement.style.left =
                "0";
            globalState.multiViewLabelRenderers[
                view
            ].domElement.style.pointerEvents = "none";

            const viewElement = document.getElementById(`${view}-view`);
            if (viewElement) {
                viewElement.appendChild(
                    globalState.multiViewLabelRenderers[view].domElement
                );
            }
        }

        // Create controls
        if (!globalState.multiViewControls[view]) {
            globalState.multiViewControls[view] = new OrbitControls(
                globalState.multiViewCameras[view],
                globalState.multiViewRenderers[view].domElement
            );
            globalState.multiViewControls[view].enableDamping = true;
            globalState.multiViewControls[view].dampingFactor = 0.5;
        }

        // Add lights to the scene - ALWAYS ensure proper lighting
        // Remove existing lights first to avoid duplicates
        const existingLights = globalState.multiViewScenes[
            view
        ].children.filter((child) => child.isLight);
        existingLights.forEach((light) =>
            globalState.multiViewScenes[view].remove(light)
        );

        // Add comprehensive lighting setup with brightness setting
        const ambientLight = new THREE.AmbientLight(
            0xffffff,
            0.4 * globalState.brightnessAmount
        );
        globalState.multiViewScenes[view].add(ambientLight);

        const directionalLight1 = new THREE.DirectionalLight(
            0xffffff,
            0.6 * globalState.brightnessAmount
        );
        directionalLight1.position.set(1, 1, 1);
        globalState.multiViewScenes[view].add(directionalLight1);

        const directionalLight2 = new THREE.DirectionalLight(
            0xffffff,
            0.4 * globalState.brightnessAmount
        );
        directionalLight2.position.set(-1, -1, -1);
        globalState.multiViewScenes[view].add(directionalLight2);

        console.log(`💡 Lighting setup completed for ${view} scene`);
    });

    // Link camera controls for synchronized viewing with loop prevention
    let multiViewSyncing = false;

    views.forEach((view) => {
        globalState.multiViewControls[view].addEventListener("change", () => {
            // Prevent recursive calls
            if (multiViewSyncing) return;

            multiViewSyncing = true;

            views.forEach((otherView) => {
                if (
                    otherView !== view &&
                    globalState.multiViewCameras[otherView] &&
                    globalState.multiViewControls[otherView]
                ) {
                    globalState.multiViewCameras[otherView].position.copy(
                        globalState.multiViewCameras[view].position
                    );
                    globalState.multiViewCameras[otherView].rotation.copy(
                        globalState.multiViewCameras[view].rotation
                    );
                    globalState.multiViewControls[otherView].target.copy(
                        globalState.multiViewControls[view].target
                    );
                    globalState.multiViewControls[otherView].update();
                }
            });

            multiViewSyncing = false;
        });
    });

    // Enable multi-view rendering in the animation loop
    globalState.renderMultiView = true;
}

/**
 * Setup camera synchronization specifically for arrangement views (original and segmented)
 * This provides better performance than syncing all 4 views when only 2 are active
 */
function setupArrangementCameraSynchronization() {
    const arrangementViews = ["original", "segmented"];

    // Remove any existing event listeners to avoid duplicates
    arrangementViews.forEach((view) => {
        if (globalState.multiViewControls[view]._arrangementSyncListener) {
            globalState.multiViewControls[view].removeEventListener(
                "change",
                globalState.multiViewControls[view]._arrangementSyncListener
            );
        }
    });

    // Flag to prevent infinite loops during synchronization
    let syncing = false;

    // Setup synchronization between only the arrangement views
    arrangementViews.forEach((view) => {
        const syncListener = () => {
            // Prevent recursive calls
            if (syncing) return;

            const otherView = view === "original" ? "segmented" : "original";

            if (
                globalState.multiViewCameras[otherView] &&
                globalState.multiViewControls[otherView]
            ) {
                syncing = true;

                // Synchronize camera position, rotation, and target
                globalState.multiViewCameras[otherView].position.copy(
                    globalState.multiViewCameras[view].position
                );
                globalState.multiViewCameras[otherView].rotation.copy(
                    globalState.multiViewCameras[view].rotation
                );
                globalState.multiViewControls[otherView].target.copy(
                    globalState.multiViewControls[view].target
                );

                // Update the other view's controls without triggering events
                globalState.multiViewControls[otherView].update();

                syncing = false;
            }
        };

        // Store the listener reference for cleanup
        globalState.multiViewControls[view]._arrangementSyncListener =
            syncListener;
        globalState.multiViewControls[view].addEventListener(
            "change",
            syncListener
        );
    });

    console.log("🔗 Arrangement camera synchronization setup complete");
}

/**
 * Display arrangement results with side-by-side visualization using InfoJson format
 * @param {Object} arrangementResult - The arrangement result data
 */
async function displayArrangementResults(arrangementResult) {
    if (!arrangementResult) {
        console.warn("No arrangement result data");
        showToast("No arrangement result data", "exclamation-triangle");
        return;
    }

    // Notes the pipeline wrote for the technician about the result they are about to
    // see. Shown before the meshes render, so an unexpected arch is explained rather
    // than left to be guessed at, and held longer than an ordinary toast because it
    // asks the reader to reconsider a choice.
    if (arrangementResult.warnings?.archform) {
        showToast(
            arrangementResult.warnings.archform,
            "exclamation-triangle",
            12000
        );
    }

    // Check if we have InfoJson data
    if (!arrangementResult.info_json || !arrangementResult.info_json.Objects) {
        console.warn("No InfoJson data found in arrangement result");
        showToast(
            "No InfoJson data found in arrangement result",
            "exclamation-triangle"
        );
        return;
    }

    console.log("🔄 Starting arrangement results display...");

    // Parse original files and create arrangement objects using InfoJson FIRST
    const { originalObjects, transformedObjects } = await parseArrangementFiles(
        arrangementResult.info_json
    );

    console.log(
        `📊 Parsed objects - Original: ${originalObjects.length}, Transformed: ${transformedObjects.length}`
    );

    // Ensure we have objects to display before setting up views
    if (originalObjects.length === 0 && transformedObjects.length === 0) {
        console.warn("⚠️ No objects found to display");
        showToast("No objects found to display", "exclamation-triangle");
        return;
    }

    // Ensure multi-view container exists
    const multiViewContainer = document.getElementById("multi-view-container");
    if (!multiViewContainer) {
        console.error("❌ Multi-view container not found!");
        showToast(
            "Display error: Multi-view container missing",
            "exclamation-triangle"
        );
        return;
    }

    // Hide the original single view and show multi-view container
    globalState.renderer.domElement.style.display = "none";
    globalState.labelRenderer.domElement.style.display = "none";
    console.log("🖥️ Setting up multi-view display...");
    multiViewContainer.style.display = "block";

    // Initialize multi-view renderers if not already done
    if (!globalState.multiViewRenderers.original) {
        console.log("🔧 Initializing multi-view renderers...");
        initializeMultiViewRenderers();
    }

    // Clear existing scenes
    clearMultiViewScenes();

    // Configure for 1x2 side-by-side layout
    setupArrangementMultiViewLayout();

    // Check if we have both original and transformed objects for dual view
    if (originalObjects.length > 0 && transformedObjects.length > 0) {
        // Store objects in global state for view mode switching
        globalState.arrangementOriginalObjects = originalObjects;
        globalState.arrangementTransformedObjects = transformedObjects;

        // Show the view mode toggle
        const viewModeContainer = document.getElementById(
            "arrangement-view-mode-container"
        );
        if (viewModeContainer) {
            viewModeContainer.style.display = "block";
        }

        // Display original objects (left side) with jaw toggles
        console.log("📦 Displaying original objects...");
        displayArrangementObjectsInScene(
            originalObjects,
            globalState.multiViewScenes.original,
            "Original Objects",
            true // enable jaw toggles
        );

        // Display transformed objects (right side) - also enable jaw toggles for userData
        console.log("📦 Displaying arranged objects...");
        displayArrangementObjectsInScene(
            transformedObjects,
            globalState.multiViewScenes.segmented,
            "Arranged Objects",
            true // enable jaw toggles so userData.group is set for visibility controls
        );

        // Note: Jaw toggle controls removed - visibility controls are now in the left sidebar
        cleanupFloatingJawControls();

        // Auto-position cameras to show objects and ensure multi-view rendering
        autoPositionArrangementCameras();

        // Setup targeted camera synchronization for arrangement views
        setupArrangementCameraSynchronization();

        // Force multi-view layout initialization
        globalState.multiViewLayoutInitialized = false; // Reset to force re-initialization
        console.log(
            "🔄 Reset multi-view layout flag to force re-initialization"
        );

        // Force camera controls update to show objects properly
        setTimeout(() => {
            console.log("📹 Updating camera controls and layout...");
            if (globalState.multiViewControls.original) {
                globalState.multiViewControls.original.update();
            }
            if (globalState.multiViewControls.segmented) {
                globalState.multiViewControls.segmented.update();
            }
            // Force window resize to ensure proper layout
            onWindowResize();
        }, 100);

        console.log("✅ Arrangement dual-view setup complete");
        showToast(
            "Arrangement completed - Left: Original, Right: Arranged",
            "check-circle"
        );

        // Initialize feedback after successful arrangement
        setTimeout(() => {
            initializeFeedback("arrangement", arrangementResult);
        }, 1000);
    } else if (originalObjects.length > 0) {
        // No transformed objects, show original objects in main view instead
        console.log("📦 Displaying original objects in main view...");

        // Hide multi-view and show main view
        multiViewContainer.style.display = "none";
        globalState.renderer.domElement.style.display = "block";
        globalState.labelRenderer.domElement.style.display = "block";

        // Display original objects in main scene
        displayOriginalObjectsInMainScene(originalObjects);

        // Note: Jaw toggle controls removed - visibility controls are now in the left sidebar
        cleanupFloatingJawControls();

        // Force camera controls update
        setTimeout(() => {
            console.log("📹 Updating main view camera controls...");
            if (globalState.controls) {
                globalState.controls.update();
            }
            onWindowResize();
        }, 100);

        console.log("✅ Arrangement main-view setup complete");
        showToast(
            "Arrangement processing completed - showing original objects",
            "exclamation-triangle"
        );

        // Initialize feedback after arrangement completion
        setTimeout(() => {
            initializeFeedback("arrangement", arrangementResult);
        }, 1000);
    }

    // Store InfoJSON data for download
    globalState.lastArrangementInfoJson = arrangementResult.info_json;
    updateArrangementActionButtons();

    // Store displacement table if available
    if (arrangementResult.displacement_table) {
        globalState.displacementTable = arrangementResult.displacement_table;
        // Show displacement table button
        const displacementTableButton = document.getElementById(
            "displacement-table-button"
        );
        if (displacementTableButton) {
            displacementTableButton.style.display = "block";
        }
    } else {
        // Hide displacement table button if no table available
        const displacementTableButton = document.getElementById(
            "displacement-table-button"
        );
        if (displacementTableButton) {
            displacementTableButton.style.display = "none";
        }
        globalState.displacementTable = null;
    }

    // Hide postprocessing section since results are now displayed
    updatePostprocessingSectionVisibility();

    // Show the InfoJSON action buttons (Accept/Reject when the review gate is
    // enabled, otherwise a plain Download button).
    showInfoJsonActions();

    console.log("🎯 Arrangement results display completed successfully");
}

/**
 * Auto-position cameras for arrangement multi-view to properly frame objects
 */
function autoPositionArrangementCameras() {
    console.log("📹 Auto-positioning arrangement cameras...");

    // Ensure multi-view rendering is enabled
    globalState.renderMultiView = true;
    console.log("🎬 Multi-view rendering enabled");

    const scenes = [
        {
            key: "original",
            scene: globalState.multiViewScenes.original,
            camera: globalState.multiViewCameras.original
        },
        {
            key: "segmented",
            scene: globalState.multiViewScenes.segmented,
            camera: globalState.multiViewCameras.segmented
        }
    ];

    // Use a SINGLE shared reference frame so both panels are centered and
    // framed identically. This makes global displacement directly readable:
    // an unmoved tooth lands at the same screen position in both panels, and a
    // uniform shift of the arranged arch shows up as a visible offset (instead
    // of being hidden by each panel re-centering on its own arch).
    const computeVisibleBox = (scene) => {
        if (!scene) return null;
        const box = new THREE.Box3();
        let count = 0;
        scene.traverse((object) => {
            if (object.isMesh && object.visible) {
                const meshBox = new THREE.Box3().setFromObject(object);
                if (meshBox.min.x !== Infinity) {
                    box.union(meshBox);
                    count++;
                }
            }
        });
        return count > 0 ? box : null;
    };

    const originalBox = computeVisibleBox(globalState.multiViewScenes.original);
    const arrangedBox = computeVisibleBox(
        globalState.multiViewScenes.segmented
    );
    const referenceBox = originalBox || arrangedBox;

    if (!referenceBox) {
        console.warn("⚠️ No visible meshes found in arrangement scenes");
        return;
    }

    // Shared pivot = Original arch center (fall back to Arranged if Original is
    // empty), so the Arranged panel is framed relative to where the original
    // teeth sit and any global shift is visible.
    const center = referenceBox.getCenter(new THREE.Vector3());

    // Framing distance from the union of both arches so neither panel clips.
    const unionBox = new THREE.Box3();
    if (originalBox) unionBox.union(originalBox);
    if (arrangedBox) unionBox.union(arrangedBox);
    const size = unionBox.getSize(new THREE.Vector3());
    const maxDimension = Math.max(size.x, size.y, size.z);
    const distance = maxDimension * 1.5;

    console.log(
        `📏 Shared arrangement pivot - Center: (${center.x.toFixed(1)}, ` +
            `${center.y.toFixed(1)}, ${center.z.toFixed(1)}), ` +
            `frame size: ${maxDimension.toFixed(1)}`
    );

    scenes.forEach(({ key, scene, camera }) => {
        if (!scene || !camera) {
            console.warn(`⚠️ Missing scene or camera for ${key} view`);
            return;
        }

        // Identical center + distance for every panel.
        camera.position.set(center.x, center.y, center.z + distance);
        camera.lookAt(center.x, center.y, center.z);
        camera.updateProjectionMatrix();

        if (globalState.multiViewControls[key]) {
            globalState.multiViewControls[key].target.copy(center);
            globalState.multiViewControls[key].update();
        }

        console.log(
            `📹 ${key} camera positioned at shared center, distance ${distance.toFixed(
                1
            )}`
        );
    });

    console.log("✅ Camera auto-positioning completed (shared center)");
}

/**
 * Reset multi-view container to default state
 */
function resetMultiViewContainer() {
    const multiViewContainer = document.getElementById("multi-view-container");
    if (multiViewContainer) {
        // Reset to default CSS properties
        multiViewContainer.style.display = "none";
        multiViewContainer.style.gridTemplateColumns = "";
        multiViewContainer.style.gridTemplateRows = "";
        multiViewContainer.style.gap = "";
        multiViewContainer.style.width = "";
        multiViewContainer.style.height = "";
        multiViewContainer.style.position = "";
        multiViewContainer.style.top = "";
        multiViewContainer.style.left = "";

        // Force remove any inline grid styles that might persist
        multiViewContainer.removeAttribute("style");
        multiViewContainer.style.display = "none";
    }

    // Reset all view panels to default state and ensure they're hidden
    const viewPanels = [
        "original-view",
        "segmented-view",
        "analysis-view",
        "final-view"
    ];
    viewPanels.forEach((panelId) => {
        const panel = document.getElementById(panelId);
        if (panel) {
            // Force remove all inline styles
            panel.removeAttribute("style");
            panel.style.display = "none";
        }
    });

    // Clear any cached renderers that might have wrong sizes
    if (globalState.multiViewRenderers) {
        Object.keys(globalState.multiViewRenderers).forEach((view) => {
            if (globalState.multiViewRenderers[view]) {
                // Force a resize to clear any cached dimensions
                globalState.multiViewRenderers[view].setSize(1, 1);
            }
        });
    }
}

/**
 * Setup arrangement multi-view layout for 1x2 side-by-side views
 */
function setupArrangementMultiViewLayout() {
    // Make sure multi-view container is visible and configure for 1x2 grid
    const multiViewContainer = document.getElementById("multi-view-container");
    if (multiViewContainer) {
        multiViewContainer.style.display = "grid";
        multiViewContainer.style.gridTemplateColumns = "1fr 1fr"; // 2 equal columns
        multiViewContainer.style.gridTemplateRows = "1fr"; // 1 row
        multiViewContainer.style.gap = "2px";
        multiViewContainer.style.width = "100%";
        multiViewContainer.style.height = "100%";
        multiViewContainer.style.position = "absolute";
        multiViewContainer.style.top = "0";
        multiViewContainer.style.left = "0";
    }

    // Hide unused views (only use original and segmented for arrangement)
    const analysisView = document.getElementById("analysis-view");
    const finalView = document.getElementById("final-view");
    if (analysisView) analysisView.style.display = "none";
    if (finalView) finalView.style.display = "none";

    // Show and configure the two main views
    const originalView = document.getElementById("original-view");
    const segmentedView = document.getElementById("segmented-view");

    if (originalView) {
        originalView.style.display = "block";
        originalView.style.width = "100%";
        originalView.style.height = "100%";
        originalView.style.position = "relative";
    }

    if (segmentedView) {
        segmentedView.style.display = "block";
        segmentedView.style.width = "100%";
        segmentedView.style.height = "100%";
        segmentedView.style.position = "relative";
    }

    // Calculate view dimensions for each half
    const viewWidth = (window.innerWidth - 2) / 2; // Account for 2px gap
    const viewHeight = window.innerHeight;

    // Resize the renderers for 50/50 split
    if (
        globalState.multiViewRenderers.original &&
        globalState.multiViewCameras.original
    ) {
        const aspect = viewWidth / viewHeight;
        updateOrthographicCamera(globalState.multiViewCameras.original, aspect);
        globalState.multiViewRenderers.original.setSize(viewWidth, viewHeight);

        if (globalState.multiViewLabelRenderers.original) {
            globalState.multiViewLabelRenderers.original.setSize(
                viewWidth,
                viewHeight
            );
        }
    }

    if (
        globalState.multiViewRenderers.segmented &&
        globalState.multiViewCameras.segmented
    ) {
        const aspect = viewWidth / viewHeight;
        updateOrthographicCamera(
            globalState.multiViewCameras.segmented,
            aspect
        );
        globalState.multiViewRenderers.segmented.setSize(viewWidth, viewHeight);

        if (globalState.multiViewLabelRenderers.segmented) {
            globalState.multiViewLabelRenderers.segmented.setSize(
                viewWidth,
                viewHeight
            );
        }
    }

    // Make sure main view is hidden during multi-view
    globalState.renderer.domElement.style.display = "none";
    globalState.labelRenderer.domElement.style.display = "none";

    // Hide boundary views during arrangement
    hideAllBoundaryViews();
    hideViewSeparators();
}

/**
 * Switch between dual view and overlay view modes for arrangement
 * @param {string} mode - "dual" or "overlay"
 */
function switchArrangementViewMode(mode) {
    console.log(`🔄 Switching arrangement view mode to: ${mode}`);
    globalState.overlayViewMode = mode;

    const multiViewContainer = document.getElementById("multi-view-container");
    const overlayViewContainer = document.getElementById(
        "overlay-view-container"
    );

    if (mode === "overlay") {
        // Hide dual view, show overlay view
        if (multiViewContainer) {
            multiViewContainer.style.display = "none";
        }
        if (overlayViewContainer) {
            overlayViewContainer.style.display = "block";
        }

        // Initialize overlay view if needed
        if (!globalState.overlayRenderer) {
            initializeOverlayView();
        }

        // Display objects in overlay mode
        displayArrangementOverlay();
    } else {
        // Show dual view, hide overlay view
        if (multiViewContainer) {
            multiViewContainer.style.display = "block";
        }
        if (overlayViewContainer) {
            overlayViewContainer.style.display = "none";
        }

        // Hide opacity controls in dual mode
        const opacityControls = document.getElementById(
            "overlay-opacity-controls"
        );
        if (opacityControls) {
            opacityControls.style.display = "none";
        }

        // Re-setup dual view layout
        setupArrangementMultiViewLayout();
    }

    // Force window resize to update layout
    setTimeout(() => {
        onWindowResize();
    }, 100);

    showToast(
        `Switched to ${mode === "overlay" ? "Overlay" : "Dual"} view`,
        "check-circle"
    );
}

/**
 * Initialize the overlay view renderer and scene
 */
function initializeOverlayView() {
    console.log("🔧 Initializing overlay view...");

    const overlayViewElement = document.getElementById("overlay-view");
    if (!overlayViewElement) {
        console.error("❌ Overlay view element not found!");
        return;
    }

    // Force full width on the overlay view element
    overlayViewElement.style.width = "100%";
    overlayViewElement.style.display = "block";
    overlayViewElement.style.borderRight = "none";

    // Create scene
    globalState.overlayScene = new THREE.Scene();
    globalState.overlayScene.background = createGradientTexture();

    // Create camera
    const aspect = window.innerWidth / window.innerHeight;
    globalState.overlayCamera = createOrthographicCamera(aspect);
    globalState.overlayCamera.position.set(0, 0, 50);

    // Create renderer
    globalState.overlayRenderer = new THREE.WebGLRenderer({ antialias: true });
    globalState.overlayRenderer.setSize(window.innerWidth, window.innerHeight);
    globalState.overlayRenderer.setPixelRatio(window.devicePixelRatio);
    globalState.overlayRenderer.domElement.style.position = "absolute";
    globalState.overlayRenderer.domElement.style.top = "0";
    globalState.overlayRenderer.domElement.style.left = "0";
    globalState.overlayRenderer.domElement.style.zIndex = "1";
    overlayViewElement.appendChild(globalState.overlayRenderer.domElement);

    // Create label renderer
    globalState.overlayLabelRenderer = new CSS2DRenderer();
    globalState.overlayLabelRenderer.setSize(
        window.innerWidth,
        window.innerHeight
    );
    globalState.overlayLabelRenderer.domElement.style.position = "absolute";
    globalState.overlayLabelRenderer.domElement.style.top = "0";
    globalState.overlayLabelRenderer.domElement.style.left = "0";
    globalState.overlayLabelRenderer.domElement.style.zIndex = "2";
    globalState.overlayLabelRenderer.domElement.style.pointerEvents = "none";
    overlayViewElement.appendChild(globalState.overlayLabelRenderer.domElement);

    // Add lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    globalState.overlayScene.add(ambientLight);

    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight1.position.set(1, 1, 1);
    globalState.overlayScene.add(directionalLight1);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.3);
    directionalLight2.position.set(-1, -1, -1);
    globalState.overlayScene.add(directionalLight2);

    // Create controls
    globalState.overlayControls = new OrbitControls(
        globalState.overlayCamera,
        globalState.overlayRenderer.domElement
    );
    globalState.overlayControls.enableDamping = true;
    globalState.overlayControls.dampingFactor = 0.05;

    console.log("✅ Overlay view initialized");
}

/**
 * Display arrangement objects in overlay mode (pre as gray, post as blue)
 */
function displayArrangementOverlay() {
    if (!globalState.overlayScene) {
        console.error("❌ Overlay scene not initialized!");
        return;
    }

    console.log("🎨 Displaying arrangement overlay...");

    // Clear existing objects
    while (globalState.overlayScene.children.length > 0) {
        const child = globalState.overlayScene.children[0];
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
            if (Array.isArray(child.material)) {
                child.material.forEach((mat) => mat.dispose());
            } else {
                child.material.dispose();
            }
        }
        globalState.overlayScene.remove(child);
    }

    // Re-add lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    globalState.overlayScene.add(ambientLight);

    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight1.position.set(1, 1, 1);
    globalState.overlayScene.add(directionalLight1);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.3);
    directionalLight2.position.set(-1, -1, -1);
    globalState.overlayScene.add(directionalLight2);

    // Initialize arrays to track meshes by layer
    globalState.overlayOriginalMeshes = [];
    globalState.overlayTransformedMeshes = [];

    // Display original objects in gray
    if (globalState.arrangementOriginalObjects) {
        console.log("📦 Adding original objects (gray)...");
        globalState.arrangementOriginalObjects.forEach((objData) => {
            if (
                !objData?.vertices ||
                !Array.isArray(objData.vertices) ||
                objData.vertices.length === 0
            ) {
                console.warn("⚠️ Skipping object with no vertices:", objData);
                return;
            }

            // Create geometry from vertices
            const vertices = new Float32Array(objData.vertices.flat());
            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute(
                "position",
                new THREE.BufferAttribute(vertices, 3)
            );

            // Add faces if available
            if (
                objData.faces &&
                Array.isArray(objData.faces) &&
                objData.faces.length > 0
            ) {
                const faces = objData.faces.flat();
                geometry.setIndex(faces);
                geometry.computeVertexNormals();
            }

            // Apply gray material with transparency
            const grayMaterial = new THREE.MeshPhongMaterial({
                color: 0x808080,
                transparent: true,
                opacity: 0.5,
                side: THREE.DoubleSide
            });

            const mesh = new THREE.Mesh(geometry, grayMaterial);

            // Set mesh name for getObjectType to work
            mesh.name = objData.name || "";

            // Set user data for visibility controls
            mesh.userData = {
                type: objData.type,
                group: objData.group,
                teethID: objData.teethID,
                descr: objData.descr,
                originalType: objData.originalType,
                layer: "original"
            };

            // Apply initial visibility based on current toggle states
            mesh.visible = getInitialObjectVisibility(mesh);

            globalState.overlayScene.add(mesh);
            globalState.overlayOriginalMeshes.push(mesh);
        });
    }

    // Display transformed objects in blue
    if (globalState.arrangementTransformedObjects) {
        console.log("📦 Adding transformed objects (blue)...");
        globalState.arrangementTransformedObjects.forEach((objData) => {
            if (
                !objData?.vertices ||
                !Array.isArray(objData.vertices) ||
                objData.vertices.length === 0
            ) {
                console.warn("⚠️ Skipping object with no vertices:", objData);
                return;
            }

            // Create geometry from vertices
            const vertices = new Float32Array(objData.vertices.flat());
            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute(
                "position",
                new THREE.BufferAttribute(vertices, 3)
            );

            // Add faces if available
            if (
                objData.faces &&
                Array.isArray(objData.faces) &&
                objData.faces.length > 0
            ) {
                const faces = objData.faces.flat();
                geometry.setIndex(faces);
                geometry.computeVertexNormals();
            }

            // Apply blue material with transparency enabled
            const blueMaterial = new THREE.MeshPhongMaterial({
                color: 0x4a90e2,
                transparent: true,
                opacity: 1.0,
                side: THREE.DoubleSide
            });

            const mesh = new THREE.Mesh(geometry, blueMaterial);

            // Set mesh name for getObjectType to work
            mesh.name = objData.name || "";

            // Set user data for visibility controls
            mesh.userData = {
                type: objData.type,
                group: objData.group,
                teethID: objData.teethID,
                descr: objData.descr,
                originalType: objData.originalType,
                layer: "transformed"
            };

            // Apply initial visibility based on current toggle states
            mesh.visible = getInitialObjectVisibility(mesh);

            globalState.overlayScene.add(mesh);
            globalState.overlayTransformedMeshes.push(mesh);
        });
    }

    // Auto-position camera
    autoPositionOverlayCamera();

    // Ensure opacity controls are visible
    ensureOpacityControlsVisible();

    console.log("✅ Overlay display complete");
}

/**
 * Ensure opacity controls are visible and properly positioned
 */
function ensureOpacityControlsVisible() {
    if (document.getElementById("overlay-opacity-controls")) return;

    // Ensure overlay container exists
    let container = document.getElementById("overlay-view-container");
    if (!container) {
        const mainViewer = document.getElementById("main-viewer");
        if (!mainViewer) return;

        container = document.createElement("div");
        container.id = "overlay-view-container";
        container.style.cssText =
            "display: none; width: 100%; height: 100%; position: absolute; top: 0; left: 0;";

        const view = document.createElement("div");
        view.className = "view-panel";
        view.id = "overlay-view";
        view.style.cssText =
            "width: 100% !important; display: block !important; border-right: none !important;";

        container.appendChild(view);
        mainViewer.appendChild(container);
    }

    // Create opacity controls
    const controls = document.createElement("div");
    controls.id = "overlay-opacity-controls";
    controls.style.cssText =
        "position: absolute; top: 20px; right: 20px; background: rgba(0, 0, 0, 0.8); padding: 15px; border-radius: 8px; color: white; min-width: 220px; z-index: 10000; pointer-events: auto;";
    controls.innerHTML = `
        <div style="margin-bottom: 15px;">
            <label style="display: flex; align-items: center; margin-bottom: 5px; font-size: 12px; font-weight: 600;">
                <span style="display: inline-block; width: 16px; height: 16px; background: #808080; border-radius: 3px; margin-right: 8px;"></span>
                Pre-Arrangement
            </label>
            <input type="range" id="original-opacity-slider" min="0" max="100" value="50" style="width: 100%; accent-color: #808080;">
            <span id="original-opacity-value" style="font-size: 11px;">50%</span>
        </div>
        <div>
            <label style="display: flex; align-items: center; margin-bottom: 5px; font-size: 12px; font-weight: 600;">
                <span style="display: inline-block; width: 16px; height: 16px; background: #4a90e2; border-radius: 3px; margin-right: 8px;"></span>
                Post-Arrangement
            </label>
            <input type="range" id="transformed-opacity-slider" min="0" max="100" value="100" style="width: 100%; accent-color: #4a90e2;">
            <span id="transformed-opacity-value" style="font-size: 11px;">100%</span>
        </div>
    `;
    container.appendChild(controls);

    // Setup slider event listeners
    const setupSlider = (sliderId, valueId, meshArray) => {
        const slider = document.getElementById(sliderId);
        const valueSpan = document.getElementById(valueId);
        if (slider) {
            slider.oninput = (e) => {
                const opacity = e.target.value / 100;
                valueSpan.textContent = `${e.target.value}%`;
                meshArray?.forEach((mesh) => {
                    if (mesh.material) mesh.material.opacity = opacity;
                });
            };
        }
    };

    setupSlider(
        "original-opacity-slider",
        "original-opacity-value",
        globalState.overlayOriginalMeshes
    );
    setupSlider(
        "transformed-opacity-slider",
        "transformed-opacity-value",
        globalState.overlayTransformedMeshes
    );
}

/**
 * Auto-position camera for overlay view to properly frame objects
 */
function autoPositionOverlayCamera() {
    if (!globalState.overlayScene || !globalState.overlayCamera) return;

    console.log("📹 Auto-positioning overlay camera...");

    // Calculate bounding box of all objects
    const box = new THREE.Box3();
    globalState.overlayScene.traverse((object) => {
        if (object.isMesh) {
            box.expandByObject(object);
        }
    });

    if (box.isEmpty()) {
        console.warn("⚠️ No objects to frame in overlay view");
        return;
    }

    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);

    // Position camera to view from front
    globalState.overlayCamera.position.set(
        center.x,
        center.y,
        center.z + maxDim * 2
    );
    globalState.overlayControls.target.copy(center);
    globalState.overlayControls.update();

    console.log("✅ Overlay camera positioned");
}

/**
 * Add a view label to distinguish between original and transformed views
 * @param {HTMLElement} viewElement - The view container element
 * @param {string} labelText - The label text to display
 * @param {string} side - "left" or "right" for positioning
 */
function addViewLabel(viewElement, labelText, side) {
    if (!viewElement) return;

    // Remove existing label if any
    const existingLabel = viewElement.querySelector(".view-label");
    if (existingLabel) {
        existingLabel.remove();
    }

    // Create new label
    const label = document.createElement("div");
    label.className = "view-label";
    label.textContent = labelText;
    label.style.cssText = `
        position: absolute;
        top: 10px;
        ${side === "left" ? "left" : "right"}: 10px;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 8px 12px;
        border-radius: 6px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
        font-size: 14px;
        font-weight: bold;
        z-index: 1002;
        pointer-events: none;
        border: 1px solid rgba(255, 255, 255, 0.2);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    `;

    viewElement.appendChild(label);
}

function getBracketColor() {
    return [170, 203, 255]; // Light blue
}

/**
 * Parse arrangement files and apply InfoJSON transformations
 * @param {Object} infoJson - InfoJSON data containing transformation information
 * @param {Object} options - Configuration options
 * @param {Object} options.feedback - Feedback data (if parsing from feedback)
 * @param {boolean} options.fromFeedback - Whether parsing from feedback or UI inputs
 * @returns {Object} Object containing originalObjects and transformedObjects arrays
 */
async function parseArrangementFiles(infoJson, options = {}) {
    const { feedback, fromFeedback = false } = options;
    const originalObjects = [];
    const transformedObjects = [];

    let lowerJawFile, upperJawFile;

    if (fromFeedback) {
        // Get the mesh files from feedback
        if (!feedback?.meshFiles || feedback.meshFiles.length === 0) {
            console.warn(
                "No mesh files found in feedback data, trying to reconstruct from file data"
            );

            // Try to reconstruct mesh files from feedback file data as fallback
            if (feedback?.files) {
                const meshFiles = [];
                if (feedback.files.lowerJawFile?.file) {
                    meshFiles.push(feedback.files.lowerJawFile.file);
                }
                if (feedback.files.upperJawFile?.file) {
                    meshFiles.push(feedback.files.upperJawFile.file);
                }

                if (meshFiles.length > 0) {
                    console.log(
                        `🔄 Reconstructed ${meshFiles.length} mesh files from feedback file data`
                    );
                    feedback.meshFiles = meshFiles;
                } else {
                    console.warn(
                        "⚠️ No mesh files found in feedback data or file data"
                    );
                    return { originalObjects, transformedObjects };
                }
            } else {
                console.warn("⚠️ No mesh files or file data found in feedback");
                return { originalObjects, transformedObjects };
            }
        }

        // Try to identify lower and upper jaw files from the mesh files
        feedback.meshFiles.forEach((file) => {
            const fileName = file.name.toLowerCase();
            if (fileName.includes("lower") || fileName.includes("mandibular")) {
                lowerJawFile = file;
            } else if (
                fileName.includes("upper") ||
                fileName.includes("maxillary")
            ) {
                upperJawFile = file;
            }
        });

        // If we can't identify by filename, use the first files as defaults
        if (!lowerJawFile && !upperJawFile) {
            console.log(
                "📝 Could not identify jaw files by name, using available files"
            );
            if (feedback.meshFiles.length >= 1)
                lowerJawFile = feedback.meshFiles[0];
            if (feedback.meshFiles.length >= 2)
                upperJawFile = feedback.meshFiles[1];
        }

        if (!lowerJawFile && !upperJawFile) {
            console.warn("No jaw files available for parsing");
            return { originalObjects, transformedObjects };
        }

        console.log(`📁 Identified jaw files:`, {
            lower: lowerJawFile?.name || "None",
            upper: upperJawFile?.name || "None"
        });
    } else {
        // Get files from UI inputs (file upload or Azure paths/folder)
        if (isAzureInputMode()) {
            // For Azure mode, get the paths and download the files
            const lowerJawAzurePath = document
                .getElementById("lower-jaw-azure-path")
                .value.trim();
            const upperJawAzurePath = document
                .getElementById("upper-jaw-azure-path")
                .value.trim();

            if (!lowerJawAzurePath || !upperJawAzurePath) {
                console.warn(
                    "Azure paths not found for arrangement InfoJSON processing"
                );
                return { originalObjects, transformedObjects };
            }

            try {
                console.log(
                    "📥 Downloading arrangement files for InfoJSON processing..."
                );

                // Download both files from Azure
                const [downloadedLowerJaw, downloadedUpperJaw] =
                    await Promise.all([
                        ApiService.downloadAzureFile(lowerJawAzurePath),
                        ApiService.downloadAzureFile(upperJawAzurePath)
                    ]);

                lowerJawFile = downloadedLowerJaw;
                upperJawFile = downloadedUpperJaw;

                console.log(
                    "📥 Successfully downloaded arrangement files for InfoJSON processing"
                );
            } catch (error) {
                console.error(
                    "Error downloading Azure files for InfoJSON processing:",
                    error
                );
                return { originalObjects, transformedObjects };
            }
        } else {
            // For file mode, get files from inputs
            lowerJawFile = document.getElementById("lower-jaw-file").files[0];
            upperJawFile = document.getElementById("upper-jaw-file").files[0];

            if (!lowerJawFile || !upperJawFile) {
                console.warn("Original uploaded files not found");
                return { originalObjects, transformedObjects };
            }
        }
    }

    try {
        // Parse the available OBJ files
        const parsePromises = [];
        const sourceJaws = [];

        if (lowerJawFile) {
            parsePromises.push(parseObjFile(lowerJawFile));
            sourceJaws.push("lower");
        }
        if (upperJawFile) {
            parsePromises.push(parseObjFile(upperJawFile));
            sourceJaws.push("upper");
        }

        if (parsePromises.length === 0) {
            console.warn("No jaw files to parse");
            return { originalObjects, transformedObjects };
        }

        console.log(`🔄 Parsing ${parsePromises.length} jaw files...`);
        const results = await Promise.all(parsePromises);

        // Combine all objects from parsed files
        let allOriginalObjects = [];
        results.forEach((result, index) => {
            const sourceJaw = sourceJaws[index];
            const objects = result.objects || [];
            allOriginalObjects = [
                ...allOriginalObjects,
                ...objects.map((obj) => ({ ...obj, sourceJaw: sourceJaw }))
            ];
        });

        console.log(
            `🦷 Found ${allOriginalObjects.length} total objects from ${results.length} jaw files`
        );

        // Check if InfoJSON exists and has Objects
        if (!infoJson || !infoJson.Objects || infoJson.Objects.length === 0) {
            console.log(
                "⚠️ No InfoJSON provided, creating objects directly from OBJ files (no transformations)"
            );

            // When no InfoJSON is provided, just display the OBJ file contents as-is
            // Both original and transformed will be the same (identity transforms)
            allOriginalObjects.forEach((obj) => {
                // Determine object type based on metadata
                let objType = null;
                let objDescr = obj.metadata?.Type;
                let teethID = obj.teethID;

                // Try to extract teethID from name if not set
                if (teethID === null && obj.name) {
                    const nameMatch = obj.name.match(/^(\d+):/);
                    if (nameMatch) {
                        teethID = parseInt(nameMatch[1]);
                    }
                }

                // Create a temp object to test with getObjectType
                const tempTestObj = {
                    name: obj.name || "",
                    userData: {
                        type: null,
                        descr: obj.metadata?.Type,
                        teethID: teethID,
                        originalType: obj.metadata?.Type
                    }
                };

                const detectedType = getObjectType(tempTestObj);
                const isExplicitBracket = obj.metadata?.Type === "BrktAttm";
                const isImplicitBracket =
                    teethID !== null && obj.name && isBracketByName(obj.name);
                const isBracket = isExplicitBracket || isImplicitBracket;

                if (detectedType === "gingiva") {
                    objType = "gingiva";
                    objDescr = "Gingiva";
                } else if (detectedType === "tooth") {
                    objType = "tooth";
                    objDescr = "Tooth";
                } else if (isBracket) {
                    objType = 107;
                    objDescr = "BrktAttm";
                    console.log(
                        `✅ Identified bracket: ${obj.name} (teethID: ${teethID})`
                    );
                }

                // Determine group (0=upper, 1=lower)
                const group = obj.sourceJaw === "lower" ? 1 : 0;

                const objData = {
                    name: obj.name,
                    vertices: obj.vertices,
                    faces: obj.faces || [],
                    group: group,
                    type: objType,
                    descr: objDescr,
                    color: [255, 255, 255], // Default white
                    transform: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1], // Identity
                    teethID: teethID,
                    originalType: obj.metadata?.Type
                };

                // Add to both arrays (no transformation)
                originalObjects.push(objData);
                transformedObjects.push({ ...objData });
            });

            console.log(
                `📊 Created ${
                    originalObjects.length
                } objects without InfoJSON (${
                    originalObjects.filter((o) => o.type === 107).length
                } brackets)`
            );

            return { originalObjects, transformedObjects };
        }

        // InfoJSON exists - process with transformations
        console.log(
            `📋 Processing ${infoJson.Objects.length} objects from InfoJSON`
        );

        // Track processed objects to prevent duplicates
        const processedObjNames = new Set();
        let skippedDuplicates = 0;

        // First pass: Build a map of Type 107 bracket attachments for duplicate detection
        const bracketAttachments = new Set();
        infoJson.Objects.forEach((objInfo) => {
            if (objInfo.Type === 107 && objInfo.Descr === "BrktAttm") {
                bracketAttachments.add(objInfo.Name);
            }
        });

        // Helper function to check if a Type 0 bracket has a corresponding Type 107 attachment
        const hasCorrespondingBracketAttachment = (type0Name) => {
            // Extract bracket part without tooth number prefix (e.g., "17: 4T-1 - Molar Tube" -> "4T-1 - Molar Tube")
            const bracketPart = type0Name.includes(":")
                ? type0Name.split(":").slice(1).join(":").trim()
                : type0Name;
            return bracketAttachments.has(bracketPart);
        };

        // Process each object in InfoJson
        infoJson.Objects.forEach((objInfo, index) => {
            // Create a unique key for duplicate detection
            let uniqueKey;
            if (objInfo.Type === 107 && objInfo.Descr === "BrktAttm") {
                // For bracket attachments, use Index + Name to ensure uniqueness
                // This handles cases where bracket names don't exactly match between InfoJSON and OBJ
                uniqueKey = `Index${objInfo.Index}_${objInfo.Name}_Type${objInfo.Type}`;
            } else {
                // For non-bracket objects, use Index + Name to handle duplicates
                uniqueKey = `Index${objInfo.Index}_${objInfo.Name}`;
            }

            // Skip duplicates based on unique key
            if (processedObjNames.has(uniqueKey)) {
                console.warn(
                    `⚠️ SKIPPING DUPLICATE InfoJSON object: "${objInfo.Name}" (key: ${uniqueKey})`
                );
                skippedDuplicates++;
                return;
            }
            processedObjNames.add(uniqueKey);

            // Skip Type 0 bracket objects ONLY if there's a corresponding Type 107 bracket attachment
            if (objInfo.Type === 0 && isBracketByName(objInfo.Name)) {
                if (hasCorrespondingBracketAttachment(objInfo.Name)) {
                    return; // Skip Type 0 bracket - has Type 107 counterpart
                }
                // Keep Type 0 bracket - no Type 107 counterpart found
            }

            // Skip metadata-only objects (planes) - they don't have geometry in OBJ files
            // Type 108 = Maxilla plane, Type 109 = Mandible plane
            if (objInfo.Type === 108 || objInfo.Type === 109) {
                console.log(
                    `📐 Skipping metadata plane: "${objInfo.Name}" (Type: ${objInfo.Type})`
                );
                return;
            }

            // Find matching original object using flexible matching strategy
            let originalObj = null;

            // Strategy 1: Exact name match
            originalObj = allOriginalObjects.find(
                (obj) => obj.name === objInfo.Name
            );

            // Strategy 2: For brackets, match by tooth ID (bracket model can vary)
            if (!originalObj && objInfo.Type === 107) {
                // Match bracket primarily by tooth ID, since bracket model numbers
                // can differ between InfoJSON and OBJ files (e.g., "2M-1" vs "3M-1")

                // Extract tooth ID from InfoJSON bracket name if TeethID is null
                let infoTeethId = objInfo.TeethID;
                if (
                    (infoTeethId === null || infoTeethId === undefined) &&
                    objInfo.Name.includes(":")
                ) {
                    const infoToothIdMatch = objInfo.Name.match(/^(\d+):/);
                    if (infoToothIdMatch) {
                        infoTeethId = parseInt(infoToothIdMatch[1]);
                        console.log(
                            `📎 Extracted TeethID ${infoTeethId} from bracket name: "${objInfo.Name}"`
                        );
                    }
                }

                originalObj = allOriginalObjects.find((obj) => {
                    // Extract tooth ID from OBJ name (e.g., "21: 1X-1 - Metal SL" -> 21)
                    let objToothId = null;
                    if (obj.name.includes(":")) {
                        const toothIdMatch = obj.name.match(/^(\d+):/);
                        if (toothIdMatch) {
                            objToothId = parseInt(toothIdMatch[1]);
                        }
                    }
                    // Fallback to teethID if available
                    if (objToothId === null && obj.teethID !== undefined) {
                        objToothId = obj.teethID;
                    }

                    // Match if tooth ID matches AND object is a bracket
                    const toothIdMatches = infoTeethId === objToothId;
                    const isBracket =
                        obj.metadata?.Type === "BrktAttm" ||
                        obj.teethID !== undefined ||
                        isBracketByName(obj.name);

                    if (toothIdMatches && isBracket) {
                        // Log if bracket type names differ (for debugging)
                        if (objInfo.Name && obj.name) {
                            const infoBracketType = objInfo.Name.includes(":")
                                ? objInfo.Name.split(":")
                                      .slice(1)
                                      .join(":")
                                      .trim()
                                : objInfo.Name;
                            const objBracketType = obj.name.includes(":")
                                ? obj.name.split(":").slice(1).join(":").trim()
                                : obj.name;

                            if (infoBracketType !== objBracketType) {
                                console.log(
                                    `📎 Matched bracket by tooth ID ${objToothId}: InfoJSON="${infoBracketType}" vs OBJ="${objBracketType}"`
                                );
                            }
                        }
                        return true;
                    }
                    return false;
                });
            }

            // Strategy 3: For non-bracket objects only, try fuzzy matching
            if (
                !originalObj &&
                !(objInfo.Type === 107 && objInfo.Descr === "BrktAttm") &&
                objInfo.Name // Ensure Name exists before trying to use it
            ) {
                // Only do fuzzy matching for non-bracket objects (teeth, gingiva, etc.)
                const infoNameLower = objInfo.Name.toLowerCase();
                originalObj = allOriginalObjects.find((obj) => {
                    const objNameLower = obj.name ? obj.name.toLowerCase() : "";

                    // Simple name similarity for non-bracket objects
                    return (
                        infoNameLower === objNameLower ||
                        infoNameLower.includes(objNameLower) ||
                        objNameLower.includes(infoNameLower)
                    );
                });
            }

            if (
                originalObj &&
                originalObj.vertices &&
                originalObj.vertices.length > 0
            ) {
                // Check if transform is identity (skip untransformed brackets in arranged view)
                const isIdentityTransform = objInfo.Transform.every(
                    (val, idx) => {
                        const expectedIdentity = [
                            1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1
                        ];
                        return Math.abs(val - expectedIdentity[idx]) < 0.0001;
                    }
                );

                // Determine proper color for brackets
                let objectColor = objInfo.Color || [255, 255, 255];
                // Generate color only if not provided or default white/gray
                if (
                    objInfo.Type === 107 &&
                    (!objInfo.Color ||
                        (objInfo.Color[0] === 255 &&
                            objInfo.Color[1] === 255 &&
                            objInfo.Color[2] === 255) ||
                        (objInfo.Color[0] === 0.8 &&
                            objInfo.Color[1] === 0.8 &&
                            objInfo.Color[2] === 0.8))
                ) {
                    objectColor = getBracketColor();
                }

                // Create original object (no transformation)
                originalObjects.push({
                    name: objInfo.Name,
                    vertices: originalObj.vertices,
                    faces: originalObj.faces || [],
                    group: objInfo.Group, // 0 = upper, 1 = lower
                    type: objInfo.Type, // Type 107 = brackets/attachments
                    descr: objInfo.Descr, // "BrktAttm" for brackets
                    color: objectColor,
                    transform: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1], // identity matrix
                    teethID: originalObj.teethID, // Extract teethID from original object
                    originalType: originalObj.metadata?.Type // Store original type from .obj file
                });

                // Create transformed object by applying InfoJson transformation
                const transformedVertices = applyTransformationMatrix(
                    originalObj.vertices,
                    objInfo.Transform
                );

                transformedObjects.push({
                    name: objInfo.Name,
                    vertices: transformedVertices,
                    faces: originalObj.faces || [],
                    group: objInfo.Group,
                    type: objInfo.Type, // Type 107 = brackets/attachments
                    descr: objInfo.Descr, // "BrktAttm" for brackets
                    color: objectColor, // Use same color logic as original
                    transform: objInfo.Transform,
                    teethID: originalObj.teethID, // Extract teethID from original object
                    originalType: originalObj.metadata?.Type, // Store original type from .obj file
                    isIdentityTransform: isIdentityTransform // Track identity transforms
                });
            } else {
                // Only warn for objects that should have geometry
                // Types that typically have geometry: teeth (11-48), soft tissue (227-228), brackets (107)
                const shouldHaveGeometry =
                    (objInfo.Type >= 11 && objInfo.Type <= 48) || // Teeth
                    objInfo.Type === 227 ||
                    objInfo.Type === 228 || // Soft tissue
                    objInfo.Type === 107; // Brackets

                if (shouldHaveGeometry) {
                    console.warn(
                        `⚠️ No geometry found in OBJ files for: "${objInfo.Name}" (Type: ${objInfo.Type}, Descr: ${objInfo.Descr})`
                    );
                } else {
                    console.log(
                        `📋 Skipping non-visual object: "${objInfo.Name}" (Type: ${objInfo.Type}, Descr: ${objInfo.Descr})`
                    );
                }
            }
        });

        // Check for brackets in OBJ files that are missing from InfoJSON
        // This handles cases where the Python backend fails to add brackets
        console.log(
            `🔍 Checking for brackets in OBJ files not included in InfoJSON...`
        );

        const infoJsonObjectNames = new Set(
            infoJson.Objects.map((obj) => obj.Name)
        );
        const missingBrackets = allOriginalObjects.filter((obj) => {
            // Check if this is a bracket that's not in InfoJSON
            const isInInfoJson = infoJsonObjectNames.has(obj.name);
            if (isInInfoJson) return false;

            // Check if it's a bracket
            const tempTestObj = {
                name: obj.name || "",
                userData: {
                    type: null,
                    descr: obj.metadata?.Type,
                    teethID: obj.teethID,
                    originalType: obj.metadata?.Type
                }
            };

            const detectedType = getObjectType(tempTestObj);
            const isExplicitBracket = obj.metadata?.Type === "BrktAttm";
            const isImplicitBracket =
                obj.teethID !== null && obj.name && isBracketByName(obj.name);
            return (
                isExplicitBracket ||
                isImplicitBracket ||
                detectedType === "bracket"
            );
        });

        if (missingBrackets.length > 0) {
            console.error(
                `❌ Found ${missingBrackets.length} brackets in OBJ files that are missing from InfoJSON!`
            );
            console.error(
                `This indicates the Python backend failed to add brackets. Please check server logs.`
            );
            console.error(
                `Missing bracket names: ${missingBrackets
                    .map((b) => b.name)
                    .join(", ")}`
            );
            // Don't add brackets on frontend - this should be fixed in the backend
        }

        return { originalObjects, transformedObjects };
    } catch (error) {
        console.error("Error parsing original files:", error);
        return { originalObjects, transformedObjects };
    }
}

/**
 * Parse an .obj file following the same logic as Python tm_objects_from_obj_file
 * @param {File} file - The .obj file to parse
 * @returns {Promise<Object>} Promise resolving to parsed OBJ data
 */
function parseObjFile(file) {
    return new Promise((resolve, reject) => {
        console.log(
            `🔍 Starting to parse OBJ file: ${file.name} (${file.size} bytes)`
        );
        const reader = new FileReader();

        reader.onload = function (event) {
            try {
                const objContent = event.target.result;
                console.log(
                    `🔍 OBJ file content loaded: ${objContent.length} characters`
                );

                if (!objContent || objContent.length === 0) {
                    throw new Error("OBJ file is empty");
                }

                const lines = objContent.split("\n");
                console.log(
                    `🔍 Processing ${lines.length} lines from OBJ file`
                );

                // Step 1: Read all vertices first (like Python version)
                const allVertices = [];

                // Step 2: Track objects and their faces/metadata
                const objectFaces = {}; // objectName -> list of faces
                const objectMetadata = {}; // objectName -> metadata dict

                let currentObjectName = null;
                let currentMetadata = {};

                lines.forEach((line, lineIndex) => {
                    line = line.trim();

                    if (line.startsWith("v ")) {
                        // Parse vertex coordinates (x, y, z only)
                        const coords = line.split(/\s+/);
                        if (coords.length >= 4) {
                            const vertex = [
                                parseFloat(coords[1]),
                                parseFloat(coords[2]),
                                parseFloat(coords[3])
                            ];
                            allVertices.push(vertex);
                        }
                    } else if (line.startsWith("g ") || line.startsWith("o ")) {
                        // Object/group definition - get the name
                        const objectName = line.substring(2).trim();
                        if (objectName) {
                            // Only use non-empty names
                            currentObjectName = objectName;
                            if (!objectFaces[currentObjectName]) {
                                objectFaces[currentObjectName] = [];
                                // Use existing metadata if already stored, otherwise use current_metadata
                                if (!objectMetadata[currentObjectName]) {
                                    objectMetadata[currentObjectName] = {
                                        ...currentMetadata
                                    };
                                }
                                // Reset current_metadata for next object
                                currentMetadata = {};
                            }
                        }
                    } else if (line.startsWith("f ") && currentObjectName) {
                        // Parse face indices (OBJ uses 1-based indexing)
                        const faceIndices = [];
                        const parts = line.split(/\s+/);

                        for (let i = 1; i < parts.length; i++) {
                            const vertexRef = parts[i];
                            // Handle different face formats: v, v/vt, v/vt/vn, v//vn
                            const vertexIdx =
                                parseInt(vertexRef.split("/")[0]) - 1; // Convert to 0-based
                            faceIndices.push(vertexIdx);
                        }

                        if (faceIndices.length >= 3) {
                            // Valid face
                            // For now, assume triangular faces (take first 3 vertices)
                            const face = [
                                faceIndices[0],
                                faceIndices[1],
                                faceIndices[2]
                            ];
                            objectFaces[currentObjectName].push(face);
                        }
                    } else if (line.startsWith("# Object")) {
                        // Handle metadata from comment headers
                        const lineContent = line.substring(2).trim(); // Remove "# " prefix

                        if (lineContent.includes(":")) {
                            if (
                                lineContent.split(":").length === 2 &&
                                !lineContent.includes(",")
                            ) {
                                // New format: "Object: Name"
                                const objectName = lineContent
                                    .split(":")[1]
                                    .trim();
                                currentMetadata = {};
                                // We'll assign this when we encounter g/o tag
                            } else {
                                // Original format: "Object0: Name, Type: TypeName"
                                const parts = lineContent.split(",");
                                const objectNamePart = parts[0];

                                // Extract object name - handle formats like "Object45: 36: 4Y-2 - Metal SL"
                                let objectName;
                                let teethID = null;

                                if (objectNamePart.includes(":")) {
                                    const nameParts = objectNamePart.split(":");
                                    if (nameParts.length >= 3) {
                                        // Format: "Object45: 36: 4Y-2 - Metal SL"
                                        const nameAndId = nameParts
                                            .slice(1)
                                            .join(":")
                                            .trim();

                                        // Try to extract teethID and name
                                        const teethMatch =
                                            nameAndId.match(/^(\d+):\s*(.+)$/);
                                        if (teethMatch) {
                                            teethID = parseInt(teethMatch[1]);
                                            objectName = teethMatch[2].trim();
                                        } else {
                                            objectName = nameAndId;
                                        }
                                    } else {
                                        // Format: "Object: Name"
                                        objectName = nameParts[1].trim();
                                    }
                                } else {
                                    objectName = objectNamePart.trim();
                                }

                                currentMetadata = {};
                                if (teethID !== null) {
                                    currentMetadata.teethID = teethID;
                                }

                                // Parse Type from remaining parts
                                for (let i = 1; i < parts.length; i++) {
                                    const part = parts[i].trim();
                                    if (part.includes("Type:")) {
                                        const type = part.split(":")[1].trim();
                                        currentMetadata.Type = type;
                                    }
                                }

                                // Store metadata for this specific object name
                                if (!objectMetadata[objectName]) {
                                    objectMetadata[objectName] = {
                                        ...currentMetadata
                                    };
                                }
                            }
                        }
                    } else if (
                        line.startsWith("# ") &&
                        line.includes(":") &&
                        !line.startsWith("# Object")
                    ) {
                        // Regular metadata: "# key: value"
                        const metadataLine = line.substring(2); // Remove "# "
                        const colonIndex = metadataLine.indexOf(":");
                        if (colonIndex > 0) {
                            const key = metadataLine
                                .substring(0, colonIndex)
                                .trim();
                            const value = metadataLine
                                .substring(colonIndex + 1)
                                .trim();
                            currentMetadata[key] = value;
                        }
                    }
                });

                // Step 3: Group vertices into objects based on face indices (like Python)
                const objects = [];

                for (const [objectName, faces] of Object.entries(objectFaces)) {
                    if (!faces || faces.length === 0) {
                        continue; // Skip objects with no faces
                    }

                    // Find all unique vertex indices used by this object
                    const uniqueVertexIndices = [...new Set(faces.flat())];

                    // Validate vertex indices
                    const validIndices = uniqueVertexIndices.filter(
                        (idx) => idx >= 0 && idx < allVertices.length
                    );

                    if (validIndices.length === 0) {
                        continue; // Skip objects with no valid vertices
                    }

                    // Extract vertices for this object
                    const objectVertices = validIndices.map(
                        (idx) => allVertices[idx]
                    );

                    // Remap face indices to local vertex indices
                    const vertexIndexMap = {};
                    validIndices.forEach((oldIdx, newIdx) => {
                        vertexIndexMap[oldIdx] = newIdx;
                    });

                    const remappedFaces = faces
                        .map((face) => face.map((idx) => vertexIndexMap[idx]))
                        .filter((face) =>
                            face.every((idx) => idx !== undefined)
                        );

                    // Create object with metadata
                    const metadata = objectMetadata[objectName] || {};
                    let teethID = metadata.teethID || null;

                    // If metadata doesn't have Type set, try to infer it from the object name
                    if (!metadata.Type) {
                        // Check if this is a bracket by name pattern: "tooth_number: bracket_description"
                        if (objectName.includes(":")) {
                            const nameMatch =
                                objectName.match(/^(\d+):\s*(.+)$/);
                            if (nameMatch) {
                                const potentialTeethID = parseInt(nameMatch[1]);
                                const bracketDesc = nameMatch[2].trim();

                                // Validate this looks like a bracket (not a tooth)
                                if (
                                    !objectName.startsWith("Tooth ") &&
                                    !objectName.includes("Soft Tissue") &&
                                    !objectName.includes("Plane") &&
                                    isBracketByName(objectName)
                                ) {
                                    // This is a bracket!
                                    metadata.Type = "BrktAttm";
                                    if (teethID === null) {
                                        teethID = potentialTeethID;
                                        metadata.teethID = teethID;
                                    }
                                    console.log(
                                        `🔧 Inferred bracket type for: ${objectName} (teethID: ${teethID})`
                                    );
                                }
                            }
                        }
                    }

                    const obj = {
                        id: `Object_${objects.length}`,
                        name: objectName,
                        vertices: objectVertices,
                        faces: remappedFaces,
                        metadata: metadata,
                        teethID: teethID
                    };

                    // Object created successfully
                    objects.push(obj);
                }

                if (objects.length === 0) {
                    console.log(
                        `⚠️ No objects found in file "${file.name}" - trying fallback parsing`
                    );
                    // Fallback: create a single object with all vertices/faces
                    if (allVertices.length > 0) {
                        objects.push({
                            id: "Object_0",
                            name: file.name.replace(".obj", ""),
                            vertices: allVertices,
                            faces: [], // No faces in fallback
                            metadata: {},
                            teethID: null
                        });
                    }
                }

                // Parsing completed successfully
                console.log(
                    `🔍 Parsing complete for ${file.name}: ${objects.length} objects found`
                );
                console.log(
                    "🔍 Objects summary:",
                    objects.map((obj) => ({
                        name: obj.name,
                        id: obj.id,
                        vertexCount: obj.vertices?.length || 0,
                        faceCount: obj.faces?.length || 0,
                        metadata: obj.metadata,
                        teethID: obj.teethID
                    }))
                );

                resolve({ objects });
            } catch (error) {
                console.error(
                    `❌ Error parsing OBJ file "${file.name}":`,
                    error
                );
                reject(error);
            }
        };

        reader.onerror = function () {
            reject(new Error(`Failed to read file: ${file.name}`));
        };

        reader.readAsText(file);
    });
}

/**
 * Clear all multi-view scenes
 */
function clearMultiViewScenes() {
    Object.values(globalState.multiViewScenes).forEach((scene) => {
        if (scene) {
            // Remove all meshes from scene
            const meshesToRemove = scene.children.filter(
                (child) => child.isMesh
            );
            meshesToRemove.forEach((mesh) => {
                scene.remove(mesh);
                if (mesh.geometry) mesh.geometry.dispose();
                if (mesh.material) {
                    if (Array.isArray(mesh.material)) {
                        mesh.material.forEach((mat) => mat.dispose());
                    } else {
                        mesh.material.dispose();
                    }
                }
            });

            // Remove all CSS2D labels from scene
            const labelsToRemove = scene.children.filter(
                (child) => child.isCSS2DObject
            );
            labelsToRemove.forEach((label) => {
                scene.remove(label);
            });
        }
    });
}

// Feedback Review Mode Functions
function switchApplicationMode(mode) {
    globalState.feedbackMode = mode === "feedback";

    // Clear any existing state
    clearAllFilesAndState();

    // Show/hide appropriate sections
    const processingModeSection = document.getElementById(
        "processing-mode-section"
    );
    const feedbackModeSection = document.getElementById(
        "feedback-mode-section"
    );
    const fileSelectionSection = document.getElementById(
        "file-selection-section"
    );
    const processActionsRow = document.querySelector(".process-actions-row");
    const currentMode =
        document.getElementById("processing-mode-dropdown")?.value ||
        "arrangement";

    if (globalState.feedbackMode) {
        // Switch to feedback review mode
        console.log("🔄 Switching to Feedback Review Mode");

        if (processingModeSection) processingModeSection.style.display = "none";
        if (feedbackModeSection) feedbackModeSection.style.display = "block";
        if (fileSelectionSection) fileSelectionSection.style.display = "none";
        if (processActionsRow) processActionsRow.style.display = "none";
        const refineBtnFeedback = document.getElementById("refine-button");
        if (refineBtnFeedback) refineBtnFeedback.style.display = "none";

        showToast("Switched to Feedback Review Mode", "info");

        // Clear processing state
        globalState.feedbackData = [];
        globalState.currentFeedbackIndex = 0;

        // Hide feedback details section initially
        const feedbackDetailsSection = document.getElementById(
            "feedback-details-section"
        );
        if (feedbackDetailsSection) {
            feedbackDetailsSection.style.display = "none";
        }

        // Show right sidebar in feedback mode but start collapsed
        const rightSidebar = document.getElementById("right-sidebar");
        const appContainer = document.querySelector(".app-container");
        const collapseRightBtn = document.getElementById("collapse-right");

        if (rightSidebar && appContainer) {
            rightSidebar.style.display = "block";
            appContainer.classList.remove("hide-right-sidebar");

            // Start collapsed by default
            rightSidebar.classList.add("right-sidebar-collapsed");
            appContainer.classList.add("right-collapsed");

            if (collapseRightBtn) {
                collapseRightBtn.innerHTML = "❮";
                collapseRightBtn.title = "Expand sidebar";
            }
        }
    } else {
        // Switch to processing mode
        console.log("🔄 Switching to Processing Mode");

        if (processingModeSection)
            processingModeSection.style.display = "block";
        if (feedbackModeSection) feedbackModeSection.style.display = "none";
        if (fileSelectionSection) fileSelectionSection.style.display = "block";
        if (processActionsRow) processActionsRow.style.display = "flex";

        // Hide feedback details section
        const feedbackDetailsSection = document.getElementById(
            "feedback-details-section"
        );
        if (feedbackDetailsSection) {
            feedbackDetailsSection.style.display = "none";
        }

        // Hide right sidebar in processing mode
        const rightSidebar = document.getElementById("right-sidebar");
        const appContainer = document.querySelector(".app-container");
        if (rightSidebar && appContainer) {
            rightSidebar.style.display = "none";
            appContainer.classList.add("hide-right-sidebar");
        }

        showToast("Switched to Processing Mode", "info");

        // Update file selection UI for current processing mode
        updateFileSelectionUI(currentMode);
        updateModeInstructions(currentMode);
    }

    // Re-assert the Processing Mode selector / divider visibility for the new
    // application mode so a disabled selector stays hidden after switching back
    // from Review, and the leading divider is correct in each mode.
    updateProcessingModeSelectorVisibility();

    // Update postprocessing section visibility
    updatePostprocessingSectionVisibility();
    updateModelOptionsSectionVisibility(currentMode);
    updateClearButtonFromState();
    updateArrangementActionButtons();

    // Trigger window resize to adjust layout
    setTimeout(() => {
        onWindowResize();
    }, 100);
}

async function handleFeedbackDirectorySelection() {
    try {
        if (!window.showDirectoryPicker) {
            showToast(
                "Directory picker not supported. Please use a modern browser (Chrome 86+, Edge 86+)",
                "error"
            );
            return;
        }

        console.log("📂 Selecting feedback directory...");
        const directoryHandle = await window.showDirectoryPicker();

        showToast("Loading feedback data...", "info");

        // Scan the directory recursively for feedback files
        const feedbackFiles = await scanFeedbackDirectory(directoryHandle);
        console.log(
            `📋 Found ${feedbackFiles.length} feedback files:`,
            feedbackFiles.map((f) => f.webkitRelativePath)
        );

        if (feedbackFiles.length === 0) {
            showToast(
                "No feedback files found in the selected directory",
                "warning"
            );
            return;
        }

        // Parse feedback files
        const feedbackData = await parseFeedbackFiles(feedbackFiles);
        console.log(`✅ Parsed ${feedbackData.length} feedback items`);

        // Store feedback data
        globalState.feedbackData = feedbackData;
        globalState.currentFeedbackIndex = 0;

        // Update UI
        updateFeedbackFolderStatus(directoryHandle.name, feedbackData.length);
        updateFeedbackNavigation();
        updateFeedbackCasesList(feedbackData);

        // Display first feedback item
        if (feedbackData.length > 0) {
            // Auto-switch to the processing mode of the first feedback item
            const firstFeedback = feedbackData[0];
            if (firstFeedback.processingMode && firstFeedback.valid) {
                switchToProcessingMode(firstFeedback.processingMode);
            }

            await displayFeedback(0);

            // Show feedback details section in right sidebar
            const feedbackDetailsSection = document.getElementById(
                "feedback-details-section"
            );
            if (feedbackDetailsSection) {
                feedbackDetailsSection.style.display = "block";
            }

            // Make sure right sidebar is expanded
            const rightSidebar = document.getElementById("right-sidebar");
            if (
                rightSidebar &&
                rightSidebar.classList.contains("right-sidebar-collapsed")
            ) {
                toggleRightSidebar();
            }
        }

        // Show statistics
        const stats = calculateFeedbackStatistics(feedbackData);
        console.log("📊 Feedback Statistics:", stats);

        showToast(
            `Loaded ${feedbackData.length} feedback items from ${directoryHandle.name}`,
            "check-circle"
        );
    } catch (error) {
        console.error("Error loading feedback directory:", error);
        showToast(
            `Error loading feedback directory: ${error.message}`,
            "error"
        );
    }
}

async function scanFeedbackDirectory(directoryHandle) {
    const feedbackFiles = [];

    const scanDirectory = async (dirHandle, basePath = "") => {
        for await (const [name, handle] of dirHandle.entries()) {
            const path = basePath ? `${basePath}/${name}` : name;

            if (handle.kind === "directory") {
                // Recursively scan subdirectories
                await scanDirectory(handle, path);
            } else if (handle.kind === "file") {
                // Check if this is a feedback-related file
                if (isFeedbackFile(name)) {
                    const file = await handle.getFile();

                    // Add webkitRelativePath property for compatibility with existing parser
                    Object.defineProperty(file, "webkitRelativePath", {
                        value: path,
                        writable: false
                    });

                    feedbackFiles.push(file);
                }
            }
        }
    };

    await scanDirectory(directoryHandle);
    return feedbackFiles;
}

function isFeedbackFile(filename) {
    const fileName = filename.toLowerCase();
    return (
        fileName === "feedback.json" ||
        fileName === "form_data.json" ||
        fileName === "feedback_complete.json" ||
        fileName.endsWith(".obj") ||
        fileName.endsWith(".stl") ||
        fileName.endsWith(".csv") || // Include CSV files too
        (fileName.endsWith(".json") &&
            (fileName.includes("result") ||
                fileName.includes("prediction") ||
                fileName.includes("inference")))
    );
}

function updateFeedbackFolderStatus(folderName, count) {
    const folderStatus = document.getElementById("feedback-folder-status");
    const folderNameElement = document.getElementById("feedback-folder-name");
    const feedbackCount = document.getElementById("feedback-count");

    if (folderStatus && folderNameElement && feedbackCount) {
        folderNameElement.textContent = `📁 ${folderName}`;
        feedbackCount.textContent = `${count} items loaded`;
        folderStatus.style.display = "block";
    }
}

function updateFeedbackNavigation() {
    const navigation = document.getElementById("feedback-navigation");
    const prevBtn = document.getElementById("prev-feedback-btn");
    const nextBtn = document.getElementById("next-feedback-btn");
    const currentIndex = document.getElementById("current-feedback-index");
    const totalCount = document.getElementById("total-feedback-count");

    if (globalState.feedbackData.length > 0) {
        if (navigation) navigation.style.display = "block";

        // Update counter
        if (currentIndex)
            currentIndex.textContent = globalState.currentFeedbackIndex + 1;
        if (totalCount)
            totalCount.textContent = globalState.feedbackData.length;

        // Update button states
        if (prevBtn) {
            prevBtn.disabled = globalState.currentFeedbackIndex === 0;
        }
        if (nextBtn) {
            nextBtn.disabled =
                globalState.currentFeedbackIndex ===
                globalState.feedbackData.length - 1;
        }
    } else {
        if (navigation) navigation.style.display = "none";
    }
}

function navigateFeedback(direction) {
    if (globalState.feedbackData.length === 0) return;

    const newIndex = globalState.currentFeedbackIndex + direction;
    if (newIndex >= 0 && newIndex < globalState.feedbackData.length) {
        globalState.currentFeedbackIndex = newIndex;
        displayFeedback(newIndex);
        updateFeedbackNavigation();
        updateFeedbackCasesListSelection();
    }
}

function updateFeedbackCasesList(feedbackData) {
    console.log(
        "📋 Updating feedback cases list with",
        feedbackData.length,
        "items"
    );

    // Create or update the feedback cases list container
    let casesContainer = document.getElementById("feedback-cases-list");
    if (!casesContainer) {
        // Create the cases list container if it doesn't exist
        const feedbackModeSection = document.getElementById(
            "feedback-mode-section"
        );
        if (!feedbackModeSection) return;

        casesContainer = document.createElement("div");
        casesContainer.id = "feedback-cases-list";
        casesContainer.className = "feedback-cases-list";
        casesContainer.innerHTML = `
            <div class="cases-header">
                <h4>Feedback Cases</h4>
                <div class="cases-count" id="feedback-cases-count">0 cases</div>
            </div>
            <div class="cases-list-container" id="cases-list-container">
                <!-- Cases will be populated here -->
            </div>
        `;
        feedbackModeSection.appendChild(casesContainer);
    }

    // Update cases count
    const casesCount = document.getElementById("feedback-cases-count");
    if (casesCount) {
        casesCount.textContent = `${feedbackData.length} cases`;
    }

    // Populate the cases list
    const listContainer = document.getElementById("cases-list-container");
    if (!listContainer) return;

    listContainer.innerHTML = "";

    feedbackData.forEach((feedback, index) => {
        const caseItem = document.createElement("div");
        caseItem.className = "feedback-case-item";
        if (index === globalState.currentFeedbackIndex) {
            caseItem.classList.add("active");
        }
        if (!feedback.valid) {
            caseItem.classList.add("invalid");
        }

        // Format processing mode
        const formattedMode = formatProcessingMode(feedback.processingMode);
        const modeClass = feedback.processingMode || "unknown";

        // Format date and time
        let dateTimeStr = "Unknown date";
        if (feedback.date) {
            const dateStr = feedback.date.toLocaleDateString();
            const timeStr = feedback.date.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit"
            });
            dateTimeStr = `${dateStr} at ${timeStr}`;
        }

        // Create star rating
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            const star = document.createElement("span");
            star.className = `star-mini ${
                i <= (feedback.rating || 0) ? "filled" : "empty"
            }`;
            star.textContent = "★";
            stars.push(star);
        }

        caseItem.innerHTML = `
            <div class="case-header">
                <div class="case-id" title="${
                    feedback.id
                }">${feedback.id.substring(0, 8)}...</div>
                <div class="case-rating"></div>
            </div>
            <div class="case-info">
                <div class="case-doctor">Dr. ${
                    feedback.doctorName || "Unknown"
                }</div>
                <div class="case-datetime">${dateTimeStr}</div>
                <div class="case-mode ${modeClass}">${formattedMode}</div>
                ${
                    !feedback.valid
                        ? `<div class="case-error">⚠ ${feedback.error}</div>`
                        : ""
                }
            </div>
        `;

        // Add the star rating
        const ratingContainer = caseItem.querySelector(".case-rating");
        stars.forEach((star) => ratingContainer.appendChild(star));

        // Add click handler to navigate to this case
        caseItem.addEventListener("click", () => {
            navigateToFeedbackCase(index);
        });

        listContainer.appendChild(caseItem);
    });

    // Show the cases list
    casesContainer.style.display = "block";
    console.log("✅ Feedback cases list updated successfully");
}

function navigateToFeedbackCase(index) {
    if (index >= 0 && index < globalState.feedbackData.length) {
        globalState.currentFeedbackIndex = index;
        displayFeedback(index);
        updateFeedbackNavigation();
        updateFeedbackCasesListSelection();
    }
}

function updateFeedbackCasesListSelection() {
    const caseItems = document.querySelectorAll(".feedback-case-item");
    caseItems.forEach((item, index) => {
        if (index === globalState.currentFeedbackIndex) {
            item.classList.add("active");
        } else {
            item.classList.remove("active");
        }
    });
}

async function displayFeedback(index) {
    if (index < 0 || index >= globalState.feedbackData.length) return;

    const feedback = globalState.feedbackData[index];

    try {
        // Clear previous visualization
        clearVisualization();

        // Reset view mode to dual when switching feedback items
        globalState.overlayViewMode = "dual";
        const overlayViewContainer = document.getElementById(
            "overlay-view-container"
        );
        if (overlayViewContainer) {
            overlayViewContainer.style.display = "none";
        }
        const multiViewContainer = document.getElementById(
            "multi-view-container"
        );
        if (multiViewContainer) {
            multiViewContainer.style.display = "none";
        }
        // Reset the toggle to dual view
        const viewModeToggle = document.getElementById(
            "arrangement-view-mode-toggle"
        );
        if (viewModeToggle) {
            const toggleOptions =
                viewModeToggle.querySelectorAll(".toggle-option");
            toggleOptions.forEach((opt) => {
                if (opt.getAttribute("data-mode") === "dual") {
                    opt.classList.add("active");
                } else {
                    opt.classList.remove("active");
                }
            });
        }

        // Check if this is a valid feedback item
        if (!feedback.valid) {
            showToast(
                `Invalid feedback item: ${feedback.error || "Unknown error"}`,
                "warning"
            );
            updateFeedbackDetailsPanel(feedback);
            return;
        }

        console.log(
            `🔍 Displaying feedback: ${feedback.id}, Mode: ${feedback.processingMode}`
        );

        // Auto-switch processing mode if needed
        if (feedback.processingMode && feedback.valid) {
            switchToProcessingMode(feedback.processingMode);
        }

        // Special handling for arrangement feedbacks
        if (
            feedback.processingMode === "arrangement" &&
            feedback.predictionData?.arrangement_result
        ) {
            console.log("🦷 Displaying arrangement feedback with dual view");
            await displayArrangementFeedback(feedback);
        } else {
            // Regular processing for other modes
            console.log(
                `📦 Processing regular mode: ${feedback.processingMode}`
            );
            console.log(
                `📁 Available mesh files: ${feedback.meshFiles?.length || 0}`,
                feedback.meshFiles?.map((f) => f.name) || []
            );

            // Load and display the mesh
            if (feedback.meshFiles && feedback.meshFiles.length > 0) {
                console.log(
                    `🔄 Loading mesh file: ${feedback.meshFiles[0].name}`
                );
                try {
                    await loadMeshFromFeedback(feedback.meshFiles[0]);
                    console.log(`✅ Mesh loaded successfully`);
                } catch (error) {
                    console.error(`❌ Error loading mesh:`, error);
                    showToast(`Error loading mesh: ${error.message}`, "error");
                }
            } else {
                console.warn(
                    `⚠️ No mesh files found in feedback data, trying to reconstruct from file data`
                );

                // Try to reconstruct mesh files from feedback file data as fallback
                let meshFileToLoad = null;
                if (feedback?.files?.inputFile?.file) {
                    meshFileToLoad = feedback.files.inputFile.file;
                    console.log(
                        `🔄 Using reconstructed mesh file: ${meshFileToLoad.name}`
                    );
                }

                if (meshFileToLoad) {
                    try {
                        await loadMeshFromFeedback(meshFileToLoad);
                        console.log(
                            `✅ Mesh loaded successfully from reconstructed file data`
                        );
                    } catch (error) {
                        console.error(
                            `❌ Error loading reconstructed mesh:`,
                            error
                        );
                        showToast(
                            `Error loading mesh: ${error.message}`,
                            "error"
                        );
                    }
                } else {
                    console.warn(
                        `⚠️ No mesh files or file data found in feedback`
                    );
                    showToast(
                        "No mesh files (.obj/.stl) found in this feedback item",
                        "warning"
                    );
                }
            }

            // Apply prediction results if available
            if (feedback.predictionData) {
                console.log(
                    `🎨 Applying prediction results for mode: ${feedback.processingMode}`
                );
                applyPredictionResults(
                    feedback.predictionData,
                    feedback.processingMode
                );
            } else {
                console.log(`ℹ️ No prediction data available`);
            }
        }

        // Update details panel
        updateFeedbackDetailsPanel(feedback);
    } catch (error) {
        console.error("Error displaying feedback:", error);
        showToast(`Error displaying feedback: ${error.message}`, "error");
    }
}

async function loadMeshFromFeedback(file) {
    console.log(
        `🔧 loadMeshFromFeedback: Starting to load ${file.name} (${file.size} bytes)`
    );

    const fileExtension = file.name.split(".").pop().toLowerCase();
    console.log(`📄 File extension: ${fileExtension}`);

    let tmpMesh;

    if (fileExtension === "obj") {
        console.log(`🔄 Loading OBJ file...`);
        tmpMesh = await loadObjFile(file);
    } else if (fileExtension === "stl") {
        console.log(`🔄 Loading STL file...`);
        tmpMesh = await loadStlFile(file);
    } else {
        throw new Error(`Unsupported file format: ${fileExtension}`);
    }

    console.log(`📋 Loaded mesh result:`, tmpMesh ? "Success" : "Failed");
    if (tmpMesh && tmpMesh.geometry) {
        console.log(
            `📐 Mesh geometry vertices: ${
                tmpMesh.geometry.attributes.position?.count || "Unknown"
            }`
        );
    }

    if (!tmpMesh) {
        throw new Error("Failed to load mesh");
    }

    const geometry = tmpMesh.geometry;

    globalState.mesh = new THREE.Mesh(
        geometry,
        new THREE.MeshStandardMaterial({
            side: THREE.DoubleSide,
            vertexColors: true,
            metalness: 0.3,
            roughness: 0.5
        })
    );

    // Initialize color attribute if not present
    if (!globalState.mesh.geometry.attributes.color) {
        const colors = new Float32Array(
            globalState.mesh.geometry.attributes.position.count * 3
        );
        colors.fill(1);
        globalState.mesh.geometry.setAttribute(
            "color",
            new THREE.BufferAttribute(colors, 3)
        );
    }

    // Remove any existing mesh
    const existingMesh = globalState.scene.getObjectByName("mesh");
    if (existingMesh) {
        console.log(`🗑️ Removing existing mesh from scene`);
        globalState.scene.remove(existingMesh);
    }

    globalState.mesh.name = "mesh";
    globalState.scene.add(globalState.mesh);

    console.log(
        `✅ Mesh added to scene with ${globalState.mesh.geometry.attributes.position.count} vertices`
    );
    console.log(
        `🎭 Scene now has ${globalState.scene.children.length} children`
    );

    // Frame the object in view
    frameObject();
}

function frameObject() {
    if (!globalState.mesh) {
        console.warn(`⚠️ frameObject: No mesh to frame`);
        return;
    }

    console.log(`📹 Framing object in view...`);

    // Calculate bounding box
    globalState.mesh.geometry.computeBoundingBox();
    const box = globalState.mesh.geometry.boundingBox;
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());

    console.log(
        `📦 Mesh bounding box - Center: (${center.x.toFixed(
            2
        )}, ${center.y.toFixed(2)}, ${center.z.toFixed(
            2
        )}), Size: (${size.x.toFixed(2)}, ${size.y.toFixed(
            2
        )}, ${size.z.toFixed(2)})`
    );

    // Position camera to frame the object
    const maxDimension = Math.max(size.x, size.y, size.z);
    const distance = maxDimension * 1.5;

    console.log(
        `📹 Positioning camera at distance ${distance.toFixed(2)} from center`
    );

    globalState.camera.position.set(center.x, center.y, center.z + distance);
    globalState.controls.target.copy(center);
    globalState.controls.update();

    console.log(`✅ Camera positioned successfully`);
}

async function displayArrangementFeedback(feedback) {
    console.log("🦷 Displaying arrangement feedback with dual jaw view");

    if (!feedback.predictionData?.arrangement_result) {
        console.warn("No arrangement result data");
        showToast("No arrangement result data available", "warning");
        return;
    }

    // Check if we have InfoJSON data (similar to main displayArrangementResults)
    const arrangementResult = feedback.predictionData.arrangement_result;
    if (!arrangementResult.info_json || !arrangementResult.info_json.Objects) {
        console.warn("No InfoJSON data found in arrangement feedback");
        showToast("No InfoJSON data found in arrangement feedback", "warning");
        return;
    }

    console.log("🔄 Starting arrangement feedback display...");

    // Parse feedback files and create arrangement objects using InfoJSON
    const { originalObjects, transformedObjects } = await parseArrangementFiles(
        arrangementResult.info_json,
        { feedback, fromFeedback: true }
    );

    console.log(
        `📊 Parsed feedback objects - Original: ${originalObjects.length}, Transformed: ${transformedObjects.length}`
    );

    // Ensure we have objects to display before setting up views
    if (originalObjects.length === 0 && transformedObjects.length === 0) {
        console.warn("⚠️ No objects found to display from feedback");
        showToast("No objects found to display from feedback", "warning");
        return;
    }

    // Ensure multi-view container exists
    const multiViewContainer = document.getElementById("multi-view-container");
    if (!multiViewContainer) {
        console.error("❌ Multi-view container not found!");
        showToast("Display error: Multi-view container missing", "error");
        return;
    }

    // Hide the original single view and show multi-view container
    globalState.renderer.domElement.style.display = "none";
    globalState.labelRenderer.domElement.style.display = "none";
    console.log("🖥️ Setting up multi-view display for feedback...");
    multiViewContainer.style.display = "block";

    // Ensure multi-view container is properly visible
    console.log(
        `📺 Multi-view container display: ${multiViewContainer.style.display}`
    );
    console.log(
        `📺 Multi-view container dimensions: ${multiViewContainer.offsetWidth}x${multiViewContainer.offsetHeight}`
    );

    // Check if the individual view panels exist
    const originalView = document.getElementById("original-view");
    const segmentedView = document.getElementById("segmented-view");
    console.log(
        `📺 Original view exists: ${!!originalView}, display: ${
            originalView?.style.display
        }`
    );
    console.log(
        `📺 Segmented view exists: ${!!segmentedView}, display: ${
            segmentedView?.style.display
        }`
    );

    // Force visibility of view panels
    if (originalView) {
        originalView.style.display = "block";
        originalView.style.visibility = "visible";
    }
    if (segmentedView) {
        segmentedView.style.display = "block";
        segmentedView.style.visibility = "visible";
    }

    // Initialize multi-view renderers if needed
    if (!globalState.multiViewRenderers.original) {
        console.log("🔧 Initializing multi-view renderers for feedback...");
        initializeMultiViewRenderers();
    }

    // Clear existing scenes and setup layout
    clearMultiViewScenes();
    setupArrangementMultiViewLayout();

    try {
        console.log(`🎬 About to display objects:`);
        console.log(`   - Original objects: ${originalObjects.length}`);
        console.log(`   - Transformed objects: ${transformedObjects.length}`);
        console.log(
            `   - Scene keys:`,
            Object.keys(globalState.multiViewScenes)
        );
        console.log(
            `   - Original scene exists:`,
            !!globalState.multiViewScenes.original
        );
        console.log(
            `   - Segmented scene exists:`,
            !!globalState.multiViewScenes.segmented
        );

        // Store objects in global state for view mode switching
        globalState.arrangementOriginalObjects = originalObjects;
        globalState.arrangementTransformedObjects = transformedObjects;

        // Show the view mode toggle
        const viewModeContainer = document.getElementById(
            "arrangement-view-mode-container"
        );
        if (viewModeContainer) {
            viewModeContainer.style.display = "block";
        }

        // Display objects in both views (same as main arrangement display)
        console.log(
            `🎨 Displaying original objects in "Original Position" scene...`
        );
        await displayArrangementObjectsInScene(
            originalObjects,
            globalState.multiViewScenes.original,
            "Original Position",
            true // Enable jaw toggles
        );

        console.log(
            `🎨 Displaying transformed objects in "Arranged Position" scene...`
        );
        await displayArrangementObjectsInScene(
            transformedObjects,
            globalState.multiViewScenes.segmented,
            "Arranged Position",
            true // Enable jaw toggles
        );

        // Auto-position cameras to show the arrangement
        console.log(`📹 Auto-positioning cameras for both scenes...`);
        autoPositionArrangementCameras();

        // Setup targeted camera synchronization for arrangement views
        setupArrangementCameraSynchronization();

        // Note: Jaw toggle controls removed - visibility controls are now in the left sidebar
        cleanupFloatingJawControls();
        console.log(`🦷 Jaw toggle controls skipped - using left sidebar...`);

        console.log(
            `✅ Arrangement feedback display setup completed successfully`
        );
        showToast(
            "Arrangement feedback displayed with dual jaw view",
            "check-circle"
        );
    } catch (error) {
        console.error("Error setting up arrangement feedback display:", error);
        showToast(`Error displaying arrangement: ${error.message}`, "error");
    }
}

function applyPredictionResults(predictionData, processingMode) {
    if (processingMode === "keypointml" && predictionData.keypoints) {
        addKeypoints(predictionData.keypoints);
    } else if (
        (processingMode === "teethml" || processingMode === "defectml") &&
        predictionData.labels
    ) {
        applyInferenceColors(predictionData);
    }
}

function updateFeedbackDetailsPanel(feedback) {
    // Update basic information
    const doctorName = document.getElementById("feedback-doctor-name");
    const processingMode = document.getElementById("feedback-processing-mode");
    const starRating = document.getElementById("feedback-star-rating");
    const feedbackDate = document.getElementById("feedback-date");

    if (doctorName) doctorName.textContent = feedback.doctorName || "-";
    if (processingMode)
        processingMode.textContent =
            formatProcessingMode(feedback.processingMode) || "-";
    if (feedbackDate) {
        if (feedback.date) {
            const dateStr = feedback.date.toLocaleDateString();
            const timeStr = feedback.date.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit"
            });
            feedbackDate.textContent = `${dateStr} at ${timeStr}`;
        } else {
            feedbackDate.textContent = "-";
        }
    }

    // Update star rating
    if (starRating) {
        starRating.innerHTML = "";
        if (feedback.rating) {
            for (let i = 1; i <= 5; i++) {
                const star = document.createElement("span");
                star.className = "star";
                star.textContent = "★";
                if (i <= feedback.rating) {
                    star.style.color = "#ffc107";
                } else {
                    star.classList.add("empty");
                    star.style.color = "#ddd";
                }
                starRating.appendChild(star);
            }
        } else {
            starRating.textContent = "-";
        }
    }

    // Update questions and responses
    updateFeedbackQuestions(feedback);

    // Update file information
    updateFeedbackFileList(feedback);

    // Update comments
    updateFeedbackComments(feedback);

    // Update system information
    updateFeedbackSystemInfo(feedback);

    // Update postprocessing information (for arrangement mode)
    updateFeedbackPostprocessingInfo(feedback);
}

function updateFeedbackFileList(feedback) {
    const fileList = document.getElementById("feedback-file-list");
    if (!fileList) return;

    fileList.innerHTML = "";

    const allFiles = [
        ...(feedback.meshFiles || []),
        ...(feedback.resultFiles || []),
        feedback.formDataFile
    ].filter(Boolean);

    allFiles.forEach((file) => {
        const fileItem = document.createElement("div");
        fileItem.className = "file-item";

        const fileName = document.createElement("div");
        fileName.className = "file-item-name";
        fileName.textContent = file.name;

        const fileDetails = document.createElement("div");
        fileDetails.className = "file-item-details";
        fileDetails.textContent = `${(file.size / 1024).toFixed(1)} KB`;

        fileItem.appendChild(fileName);
        fileItem.appendChild(fileDetails);
        fileList.appendChild(fileItem);
    });
}

function updateFeedbackQuestions(feedback) {
    const questionsSection = document.getElementById(
        "feedback-questions-section"
    );
    const questionsList = document.getElementById("feedback-questions-list");

    console.log(`📝 UpdateFeedbackQuestions called:`, {
        hasResponses: !!(
            feedback.responses && Object.keys(feedback.responses).length > 0
        ),
        responses: feedback.responses,
        processingMode: feedback.processingMode,
        hasConfig: !!feedbackManager.feedbackConfig,
        configQuestions:
            feedbackManager.feedbackConfig?.questions?.[feedback.processingMode]
    });

    if (feedback.responses && Object.keys(feedback.responses).length > 0) {
        if (questionsSection) questionsSection.style.display = "block";
        if (questionsList) {
            questionsList.innerHTML = "";

            // Get the questions config to maintain proper ordering and numbering
            let questionsConfig = [];
            if (feedbackManager.feedbackConfig && feedback.processingMode) {
                questionsConfig =
                    feedbackManager.feedbackConfig.questions[
                        feedback.processingMode
                    ] || [];
                console.log(
                    `📝 Using config questions for ${feedback.processingMode}:`,
                    questionsConfig
                );
            } else {
                console.warn(
                    `📝 No config available, feedback config:`,
                    feedbackManager.feedbackConfig,
                    `processing mode:`,
                    feedback.processingMode
                );
            }

            let questionsDisplayed = 0;

            if (questionsConfig.length > 0) {
                // Display questions in the same order as the config, with proper numbering
                questionsConfig.forEach((questionConfig, index) => {
                    const response = feedback.responses[questionConfig.id];
                    if (response !== undefined && response !== null) {
                        const questionItem = createQuestionElement(
                            `Question ${index + 1}:`,
                            questionConfig.question,
                            response,
                            questionConfig
                        );
                        questionsList.appendChild(questionItem);
                        questionsDisplayed++;
                    }
                });
            }

            // Fallback: Display all responses if no config or no questions were displayed
            if (questionsDisplayed === 0) {
                console.log(
                    `📝 No questions displayed from config, using fallback approach`
                );
                let questionIndex = 1;
                Object.entries(feedback.responses).forEach(
                    ([questionId, response]) => {
                        const questionItem = document.createElement("div");
                        questionItem.className = "question-item";

                        const questionText = document.createElement("div");
                        questionText.className = "question-text";

                        // Fallback formatting for questions not in config
                        let fallbackText = questionId
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (l) => l.toUpperCase());

                        questionText.innerHTML = `<strong>Question ${questionIndex}:</strong> ${fallbackText}`;
                        questionIndex++;

                        const questionResponse = document.createElement("div");
                        questionResponse.className = "question-response";

                        const indicator = document.createElement("span");
                        indicator.className = "response-indicator";

                        const responseText = document.createElement("span");
                        responseText.className = "response-text";

                        if (response === "yes" || response === true) {
                            indicator.classList.add("yes");
                            indicator.textContent = "✓";
                            responseText.textContent = "Yes";
                        } else {
                            indicator.classList.add("no");
                            indicator.textContent = "✗";
                            responseText.textContent = "No";
                        }

                        questionResponse.appendChild(indicator);
                        questionResponse.appendChild(responseText);

                        questionItem.appendChild(questionText);
                        questionItem.appendChild(questionResponse);
                        questionsList.appendChild(questionItem);
                        questionsDisplayed++;
                    }
                );
            }

            console.log(
                `📝 Displayed ${questionsDisplayed} questions for ${feedback.processingMode}`
            );
        }
    } else {
        if (questionsSection) questionsSection.style.display = "none";
        console.log(`📝 No responses found in feedback:`, feedback);
    }
}

// Helper function to create question elements that match the input form layout
function createQuestionElement(
    questionPrefix,
    questionText,
    response,
    questionConfig = null
) {
    const questionItem = document.createElement("div");

    // Determine question type from config or response format
    let questionType = "yes_no"; // default
    if (questionConfig && questionConfig.type) {
        questionType = questionConfig.type;
    } else if (
        typeof response === "object" &&
        response !== null &&
        response.rating !== undefined
    ) {
        questionType = "rating_with_comment";
    }

    // Set appropriate CSS class based on question type
    if (
        questionType === "rating_with_comment" ||
        questionType === "rating_and_comment"
    ) {
        questionItem.className = "rating-comment-question readonly";
    } else {
        questionItem.className = "yes-no-question readonly";
    }

    const questionTextEl = document.createElement("div");
    questionTextEl.className = "question-text";
    questionTextEl.innerHTML = `<strong>${questionPrefix}</strong> ${questionText}`;
    questionItem.appendChild(questionTextEl);

    if (
        (questionType === "rating_with_comment" ||
            questionType === "rating_and_comment") &&
        typeof response === "object" &&
        response !== null
    ) {
        // Create rating section matching input form layout
        const ratingSection = document.createElement("div");
        ratingSection.className = "rating-section readonly";

        const ratingLabel = document.createElement("div");
        ratingLabel.className = "rating-label";
        ratingLabel.textContent = "Rating:";

        const ratingContainer = document.createElement("div");
        ratingContainer.className = "star-rating readonly";

        // Create 5 stars showing the rating
        for (let i = 1; i <= 5; i++) {
            const star = document.createElement("span");
            star.className = "star readonly";
            star.textContent = "★";
            if (i <= (response.rating || 0)) {
                star.classList.add("active");
            }
            ratingContainer.appendChild(star);
        }

        ratingSection.appendChild(ratingLabel);
        ratingSection.appendChild(ratingContainer);
        questionItem.appendChild(ratingSection);

        // Add rating text
        const ratingText = document.createElement("div");
        ratingText.className = "rating-text readonly";
        ratingText.textContent = `${response.rating || 0}/5`;
        ratingText.style.marginTop = "0.2rem";
        ratingText.style.color = "#adb5bd";
        ratingText.style.fontSize = "0.75rem";
        questionItem.appendChild(ratingText);

        // Create comment section if there's a comment
        if (response.comment && response.comment.trim()) {
            const commentSection = document.createElement("div");
            commentSection.className = "comment-section readonly";

            const commentLabel = document.createElement("div");
            commentLabel.className = "comment-label";
            commentLabel.textContent = "Comments:";
            commentLabel.style.fontWeight = "600";
            commentLabel.style.color = "#e9ecef";
            commentLabel.style.fontSize = "0.9rem";
            commentLabel.style.marginTop = "1rem";
            commentLabel.style.marginBottom = "0.5rem";

            const commentDisplay = document.createElement("div");
            commentDisplay.className = "comment-display readonly";
            commentDisplay.textContent = response.comment;
            commentDisplay.style.padding = "10px 12px";
            commentDisplay.style.background = "#2d2d2d";
            commentDisplay.style.border = "1px solid #555";
            commentDisplay.style.borderRadius = "6px";
            commentDisplay.style.color = "#e9ecef";
            commentDisplay.style.fontSize = "0.9rem";
            commentDisplay.style.lineHeight = "1.4";

            commentSection.appendChild(commentLabel);
            commentSection.appendChild(commentDisplay);
            questionItem.appendChild(commentSection);
        }
    } else {
        // Handle yes/no questions - create radio-like display
        const radioContainer = document.createElement("div");
        radioContainer.className = "radio-container readonly";

        const radioWrapper = document.createElement("div");
        radioWrapper.className = "radio-wrapper readonly";

        // Create Yes option display
        const yesLabel = document.createElement("div");
        yesLabel.className = `radio-label yes-label readonly ${
            response === "yes" || response === true ? "selected" : ""
        }`;

        const yesIndicator = document.createElement("div");
        yesIndicator.className = "radio-indicator";
        yesIndicator.textContent =
            response === "yes" || response === true ? "●" : "○";

        const yesText = document.createElement("span");
        yesText.textContent = "Yes";

        yesLabel.appendChild(yesIndicator);
        yesLabel.appendChild(yesText);

        // Create No option display
        const noLabel = document.createElement("div");
        noLabel.className = `radio-label no-label readonly ${
            response === "no" || response === false ? "selected" : ""
        }`;

        const noIndicator = document.createElement("div");
        noIndicator.className = "radio-indicator";
        noIndicator.textContent =
            response === "no" || response === false ? "●" : "○";

        const noText = document.createElement("span");
        noText.textContent = "No";

        noLabel.appendChild(noIndicator);
        noLabel.appendChild(noText);

        radioWrapper.appendChild(yesLabel);
        radioWrapper.appendChild(noLabel);
        radioContainer.appendChild(radioWrapper);
        questionItem.appendChild(radioContainer);
    }

    return questionItem;
}

function updateFeedbackComments(feedback) {
    const commentsSection = document.getElementById(
        "feedback-comments-section"
    );
    const commentsContent = document.getElementById(
        "feedback-comments-content"
    );

    if (feedback.comments && feedback.comments.trim()) {
        if (commentsSection) commentsSection.style.display = "block";
        if (commentsContent) {
            commentsContent.innerHTML = `<p>${feedback.comments}</p>`;
        }
    } else {
        if (commentsSection) commentsSection.style.display = "none";
    }
}

function updateFeedbackSystemInfo(feedback) {
    const systemInfoGrid = document.getElementById("feedback-system-info-grid");
    if (!systemInfoGrid) return;

    systemInfoGrid.innerHTML = "";

    if (feedback.systemInfo && Object.keys(feedback.systemInfo).length > 0) {
        Object.entries(feedback.systemInfo).forEach(([key, value]) => {
            const detailItem = document.createElement("div");
            detailItem.className = "detail-item";

            const label = document.createElement("label");
            label.textContent =
                key
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase()) + ":";

            const span = document.createElement("span");
            span.textContent =
                typeof value === "object"
                    ? JSON.stringify(value)
                    : String(value);

            detailItem.appendChild(label);
            detailItem.appendChild(span);
            systemInfoGrid.appendChild(detailItem);
        });
    } else {
        const noInfo = document.createElement("div");
        noInfo.className = "detail-item";
        noInfo.innerHTML = "<span>No system information available</span>";
        systemInfoGrid.appendChild(noInfo);
    }
}

function updateFeedbackPostprocessingInfo(feedback) {
    const postprocessingSection = document.getElementById(
        "feedback-postprocessing-section"
    );
    const postprocessingContent = document.getElementById(
        "feedback-postprocessing-content"
    );
    const postprocessingInfo = document.getElementById(
        "feedback-postprocessing-info"
    );

    if (
        !postprocessingSection ||
        !postprocessingContent ||
        !postprocessingInfo
    ) {
        return;
    }

    // Only show for arrangement mode
    if (feedback.processingMode !== "arrangement") {
        postprocessingSection.style.display = "none";
        return;
    }

    // Check if postprocessing data exists
    if (!feedback.postprocessing) {
        postprocessingSection.style.display = "none";
        return;
    }

    postprocessingSection.style.display = "block";
    postprocessingInfo.innerHTML = "";

    const postprocessing = feedback.postprocessing;
    const enabled = postprocessing.enabled || false;
    const parameters = postprocessing.parameters || {};

    // Create container for postprocessing info
    const infoContainer = document.createElement("div");
    infoContainer.className = "postprocessing-info-container";

    // Enable Postprocessing status
    const enabledItem = document.createElement("div");
    enabledItem.className = "detail-item";
    const enabledLabel = document.createElement("label");
    enabledLabel.textContent = "Enable Postprocessing:";
    const enabledValue = document.createElement("span");
    enabledValue.textContent = enabled ? "Yes" : "No";
    enabledValue.style.color = enabled ? "#4caf50" : "#f44336";
    enabledItem.appendChild(enabledLabel);
    enabledItem.appendChild(enabledValue);
    infoContainer.appendChild(enabledItem);

    // Only show parameters if postprocessing was enabled
    if (enabled && parameters) {
        // Gap Range Min
        if (parameters.gap_min !== undefined && parameters.gap_min !== null) {
            const gapMinItem = document.createElement("div");
            gapMinItem.className = "detail-item";
            const gapMinLabel = document.createElement("label");
            gapMinLabel.textContent = "Gap Range Min (mm):";
            const gapMinValue = document.createElement("span");
            gapMinValue.textContent = parameters.gap_min.toFixed(2);
            gapMinItem.appendChild(gapMinLabel);
            gapMinItem.appendChild(gapMinValue);
            infoContainer.appendChild(gapMinItem);
        }

        // Gap Range Max
        if (parameters.gap_max !== undefined && parameters.gap_max !== null) {
            const gapMaxItem = document.createElement("div");
            gapMaxItem.className = "detail-item";
            const gapMaxLabel = document.createElement("label");
            gapMaxLabel.textContent = "Gap Range Max (mm):";
            const gapMaxValue = document.createElement("span");
            gapMaxValue.textContent = parameters.gap_max.toFixed(2);
            gapMaxItem.appendChild(gapMaxLabel);
            gapMaxItem.appendChild(gapMaxValue);
            infoContainer.appendChild(gapMaxItem);
        }

        // Tightness
        if (
            parameters.tightness !== undefined &&
            parameters.tightness !== null
        ) {
            const tightnessItem = document.createElement("div");
            tightnessItem.className = "detail-item";
            const tightnessLabel = document.createElement("label");
            tightnessLabel.textContent = "Tightness (mm):";
            const tightnessValue = document.createElement("span");
            tightnessValue.textContent = parameters.tightness.toFixed(2);
            tightnessItem.appendChild(tightnessLabel);
            tightnessItem.appendChild(tightnessValue);
            infoContainer.appendChild(tightnessItem);
        }

        // Central Axis Mode
        if (
            parameters.central_axis_mode !== undefined &&
            parameters.central_axis_mode !== null
        ) {
            const centralAxisItem = document.createElement("div");
            centralAxisItem.className = "detail-item";
            const centralAxisLabel = document.createElement("label");
            centralAxisLabel.textContent = "Central Axis Mode:";
            const centralAxisValue = document.createElement("span");
            centralAxisValue.textContent =
                parameters.central_axis_mode === "both"
                    ? "Both"
                    : parameters.central_axis_mode === "upper"
                      ? "Upper"
                      : parameters.central_axis_mode;
            centralAxisItem.appendChild(centralAxisLabel);
            centralAxisItem.appendChild(centralAxisValue);
            infoContainer.appendChild(centralAxisItem);
        }

        // Space Management (formerly Custom Gap Rules)
        if (
            parameters.custom_gap_rules &&
            Array.isArray(parameters.custom_gap_rules) &&
            parameters.custom_gap_rules.length > 0
        ) {
            const customRulesItem = document.createElement("div");
            customRulesItem.className = "detail-item";
            customRulesItem.style.flexDirection = "column";
            customRulesItem.style.alignItems = "flex-start";

            const customRulesLabel = document.createElement("label");
            customRulesLabel.textContent = "Space Management:";
            customRulesLabel.style.marginBottom = "0.5rem";
            customRulesItem.appendChild(customRulesLabel);

            const rulesList = document.createElement("div");
            rulesList.className = "custom-gap-rules-list";
            rulesList.style.width = "100%";
            rulesList.style.marginTop = "0.5rem";

            parameters.custom_gap_rules.forEach((rule, index) => {
                const ruleItem = document.createElement("div");
                ruleItem.className = "custom-gap-rule-item";
                ruleItem.style.padding = "0.5rem";
                ruleItem.style.marginBottom = "0.25rem";
                ruleItem.style.background = "#2d2d2d";
                ruleItem.style.border = "1px solid #555";
                ruleItem.style.borderRadius = "4px";

                const ruleText = document.createElement("span");
                ruleText.style.fontSize = "0.85rem";
                ruleText.style.color = "#e9ecef";

                let ruleTextContent = `Rule ${index + 1}: Tooth ${
                    rule.tooth_a
                } - Tooth ${rule.tooth_b}`;
                if (rule.gap_min !== undefined && rule.gap_min !== null) {
                    ruleTextContent += `, Min: ${rule.gap_min.toFixed(2)} mm`;
                }
                if (rule.gap_max !== undefined && rule.gap_max !== null) {
                    ruleTextContent += `, Max: ${rule.gap_max.toFixed(2)} mm`;
                }

                ruleText.textContent = ruleTextContent;
                ruleItem.appendChild(ruleText);
                rulesList.appendChild(ruleItem);
            });

            customRulesItem.appendChild(rulesList);
            infoContainer.appendChild(customRulesItem);
        } else if (enabled) {
            // Show "No custom rules" if postprocessing is enabled but no custom rules
            const noRulesItem = document.createElement("div");
            noRulesItem.className = "detail-item";
            const noRulesLabel = document.createElement("label");
            noRulesLabel.textContent = "Space Management:";
            const noRulesValue = document.createElement("span");
            noRulesValue.textContent = "None";
            noRulesValue.style.color = "#adb5bd";
            noRulesItem.appendChild(noRulesLabel);
            noRulesItem.appendChild(noRulesValue);
            infoContainer.appendChild(noRulesItem);
        }
    }

    postprocessingInfo.appendChild(infoContainer);
}

function switchToProcessingMode(mode) {
    const processingModeDropdown = document.getElementById(
        "processing-mode-dropdown"
    );

    if (processingModeDropdown && processingModeDropdown.value !== mode) {
        console.log(
            `🔄 Auto-switching processing mode from ${processingModeDropdown.value} to ${mode}`
        );

        // Update the dropdown value
        processingModeDropdown.value = mode;

        // Update mode instructions
        updateModeInstructions(mode);

        // Update file selection UI (but don't clear files in feedback mode)
        if (!globalState.feedbackMode) {
            updateFileSelectionUI(mode);
        }

        // Update model options section visibility
        updateModelOptionsSectionVisibility(mode);

        // Show toast notification
        showToast(`Switched to ${formatProcessingMode(mode)} mode`, "info");
    }
}

// Function to setup additional controls
function setupControls() {
    // Setup sidebar collapse/expand functionality
    setupSidebarControls();

    // Setup keypoint label toggle
    const showKeypointLabelsCheckbox = document.getElementById(
        "show-keypoint-labels"
    );
    if (showKeypointLabelsCheckbox) {
        showKeypointLabelsCheckbox.addEventListener("change", function () {
            // Update keypoint label visibility if they exist
            const keypointLabels = document.querySelectorAll(".keypoint-label");
            keypointLabels.forEach((label) => {
                label.style.display = this.checked ? "block" : "none";
            });
        });
    }

    // Setup boundaries toggle for teeth separation
    const showBoundariesCheckbox = document.getElementById(
        "show-separation-boundaries"
    );
    if (showBoundariesCheckbox) {
        showBoundariesCheckbox.addEventListener("change", function () {
            // Update boundary marker visibility
            const boundaryMarkers =
                document.querySelectorAll(".boundary-marker");
            boundaryMarkers.forEach((marker) => {
                marker.style.display = this.checked ? "block" : "none";
            });
        });
    }
}

function setupSidebarControls() {
    // Left sidebar collapse/expand
    const collapseLeftBtn = document.getElementById("collapse-left");
    if (collapseLeftBtn) {
        collapseLeftBtn.addEventListener("click", toggleLeftSidebar);
    }

    // Right sidebar collapse/expand
    const collapseRightBtn = document.getElementById("collapse-right");
    if (collapseRightBtn) {
        collapseRightBtn.addEventListener("click", toggleRightSidebar);
    }

    // Application mode toggle
    const appModeToggle = document.getElementById("app-mode-toggle");
    if (appModeToggle) {
        appModeToggle.addEventListener("click", (e) => {
            if (e.target.classList.contains("toggle-option")) {
                // Remove active class from all options
                appModeToggle
                    .querySelectorAll(".toggle-option")
                    .forEach((option) => {
                        option.classList.remove("active");
                    });
                // Add active class to clicked option
                e.target.classList.add("active");
                // Switch mode
                const mode = e.target.dataset.mode;
                switchApplicationMode(mode);
            }
        });
    }

    // Feedback directory selection
    const feedbackDirBtn = document.getElementById("feedback-directory-btn");
    if (feedbackDirBtn) {
        feedbackDirBtn.addEventListener(
            "click",
            handleFeedbackDirectorySelection
        );
    }

    // Feedback navigation
    const feedbackPrevBtn = document.getElementById("feedback-prev");
    const feedbackNextBtn = document.getElementById("feedback-next");

    if (feedbackPrevBtn) {
        feedbackPrevBtn.addEventListener("click", () => navigateFeedback(-1));
    }

    if (feedbackNextBtn) {
        feedbackNextBtn.addEventListener("click", () => navigateFeedback(1));
    }

    // Visibility controls for jaws, gingiva, and brackets
    const showUpperJawCheckbox = document.getElementById("show-upper-jaw");
    if (showUpperJawCheckbox) {
        showUpperJawCheckbox.addEventListener("change", function () {
            const visible = this.checked;
            // Determine which scene(s) to update based on current view mode
            const sceneKey = globalState.multiViewLayoutInitialized
                ? "both"
                : "main";
            toggleJawVisibility(0, visible, sceneKey); // 0 for upper jaw
        });
    }

    const showLowerJawCheckbox = document.getElementById("show-lower-jaw");
    if (showLowerJawCheckbox) {
        showLowerJawCheckbox.addEventListener("change", function () {
            const visible = this.checked;
            // Determine which scene(s) to update based on current view mode
            const sceneKey = globalState.multiViewLayoutInitialized
                ? "both"
                : "main";
            toggleJawVisibility(1, visible, sceneKey); // 1 for lower jaw
        });
    }

    const showGingivaCheckbox = document.getElementById("show-gingiva");
    if (showGingivaCheckbox) {
        showGingivaCheckbox.addEventListener("change", function () {
            const visible = this.checked;
            // Determine which scene(s) to update based on current view mode
            const sceneKey = globalState.multiViewLayoutInitialized
                ? "both"
                : "main";
            toggleGingivaVisibility(visible, sceneKey);
        });
    }

    const showBracketsCheckbox = document.getElementById("show-brackets");
    if (showBracketsCheckbox) {
        showBracketsCheckbox.addEventListener("change", function () {
            const visible = this.checked;
            // Determine which scene(s) to update based on current view mode
            const sceneKey = globalState.multiViewLayoutInitialized
                ? "both"
                : "main";
            toggleBracketsVisibility(visible, sceneKey);
        });
    }
}

function toggleLeftSidebar() {
    const leftSidebar = document.getElementById("left-sidebar");
    const appContainer = document.querySelector(".app-container");
    const collapseBtn = document.getElementById("collapse-left");

    if (leftSidebar.classList.contains("collapsed")) {
        // Expand
        leftSidebar.classList.remove("collapsed");
        appContainer.classList.remove("left-collapsed");
        collapseBtn.innerHTML = "❮";
        collapseBtn.title = "Collapse sidebar";
    } else {
        // Collapse
        leftSidebar.classList.add("collapsed");
        appContainer.classList.add("left-collapsed");
        collapseBtn.innerHTML = "❯";
        collapseBtn.title = "Expand sidebar";
    }

    // Update feedback button visibility based on new sidebar state
    updateStartOverButtonVisibility();

    // Trigger resize to update Three.js renderer
    setTimeout(() => {
        onWindowResize();
    }, 300); // Wait for CSS transition
}

/**
 * Collapse the left sidebar (used during processing to maximize viewer space)
 */
function collapseLeftSidebar() {
    const leftSidebar = document.getElementById("left-sidebar");
    const appContainer = document.querySelector(".app-container");
    const collapseBtn = document.getElementById("collapse-left");

    // Only collapse if not already collapsed
    if (leftSidebar && !leftSidebar.classList.contains("collapsed")) {
        leftSidebar.classList.add("collapsed");
        appContainer?.classList.add("left-collapsed");
        if (collapseBtn) {
            collapseBtn.innerHTML = "❯";
            collapseBtn.title = "Expand sidebar";
        }

        // Trigger resize to update Three.js renderer
        setTimeout(() => {
            onWindowResize();
        }, 300); // Wait for CSS transition
    }
}

function toggleRightSidebar() {
    const rightSidebar = document.getElementById("right-sidebar");
    const appContainer = document.querySelector(".app-container");
    const collapseBtn = document.getElementById("collapse-right");

    if (rightSidebar.classList.contains("right-sidebar-collapsed")) {
        // Expand
        rightSidebar.classList.remove("right-sidebar-collapsed");
        appContainer.classList.remove("right-collapsed");
        collapseBtn.innerHTML = "❯";
        collapseBtn.title = "Collapse sidebar";
        const sidebarTitle = rightSidebar.querySelector(".sidebar-title");
        if (sidebarTitle) {
            sidebarTitle.textContent = "Details";
        }
    } else {
        // Collapse
        rightSidebar.classList.add("right-sidebar-collapsed");
        appContainer.classList.add("right-collapsed");
        collapseBtn.innerHTML = "❮";
        collapseBtn.title = "Expand sidebar";
        const sidebarTitle = rightSidebar.querySelector(".sidebar-title");
        if (sidebarTitle) {
            sidebarTitle.textContent = "Details";
        }
    }

    // Trigger resize to update Three.js renderer
    setTimeout(() => {
        onWindowResize();
    }, 300); // Wait for CSS transition
}

/**
 * Apply 4x4 transformation matrix to vertices
 * @param {Array} vertices - Array of [x, y, z] vertex positions
 * @param {Array} transformMatrix - 16-element transformation matrix (row-major)
 * @returns {Array} Transformed vertices
 */
function applyTransformationMatrix(vertices, transformMatrix) {
    if (!transformMatrix || transformMatrix.length !== 16) {
        console.warn("Invalid transformation matrix:", transformMatrix);
        return vertices; // Return original if invalid matrix
    }

    // Check if this is an identity matrix
    const isIdentity = transformMatrix.every((val, idx) => {
        const expectedIdentity = [
            1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1
        ];
        return Math.abs(val - expectedIdentity[idx]) < 0.0001;
    });

    if (isIdentity) {
        return vertices;
    }

    const transformedVertices = [];
    let maxMovement = 0;

    vertices.forEach((vertex, idx) => {
        const [x, y, z] = vertex;

        // Apply 4x4 transformation matrix
        // The matrix is in row-major format from NumPy (flattened row by row)
        // Matrix layout: [m00, m01, m02, m03, m10, m11, m12, m13, m20, m21, m22, m23, m30, m31, m32, m33]
        // For point transformation: [x', y', z', w'] = Matrix * [x, y, z, 1]^T
        const transformedX =
            transformMatrix[0] * x +
            transformMatrix[1] * y +
            transformMatrix[2] * z +
            transformMatrix[3];
        const transformedY =
            transformMatrix[4] * x +
            transformMatrix[5] * y +
            transformMatrix[6] * z +
            transformMatrix[7];
        const transformedZ =
            transformMatrix[8] * x +
            transformMatrix[9] * y +
            transformMatrix[10] * z +
            transformMatrix[11];

        transformedVertices.push([transformedX, transformedY, transformedZ]);

        // Calculate movement for debugging (sample first few vertices)
        if (idx < 5) {
            const movement = Math.sqrt(
                Math.pow(transformedX - x, 2) +
                    Math.pow(transformedY - y, 2) +
                    Math.pow(transformedZ - z, 2)
            );
            maxMovement = Math.max(maxMovement, movement);
        }
    });

    // Transformation applied successfully

    return transformedVertices;
}

/**
 * Clean up any existing floating jaw toggle controls from the DOM
 */
function cleanupFloatingJawControls() {
    const existingControls = document.getElementById("jaw-toggle-controls");
    if (existingControls) {
        existingControls.remove();
        console.log("🧹 Cleaned up existing floating jaw controls");
    }
}

// Note: addJawToggleControls removed - visibility controls are now in the left sidebar

/**
 * Toggle visibility of jaw objects in scenes
 * @param {number} jawGroup - 0 for upper, 1 for lower
 * @param {boolean} visible - Whether to show or hide
 * @param {string} sceneKey - Key for the scene ('original', 'segmented', 'both', or 'main')
 */
function toggleJawVisibility(jawGroup, visible, sceneKey) {
    const jawName = jawGroup === 0 ? "upper" : "lower";

    if (sceneKey === "both") {
        // Apply to both original and segmented views
        ["original", "segmented"].forEach((viewKey) => {
            const scene = globalState.multiViewScenes[viewKey];
            if (scene) {
                scene.traverse((object) => {
                    if (
                        object.isMesh &&
                        object.userData &&
                        object.userData.group === jawGroup
                    ) {
                        // Check if this object should be visible based on all toggle states
                        const shouldBeVisible = shouldObjectBeVisible(
                            object,
                            visible
                        );
                        object.visible = shouldBeVisible;
                    }
                    // Also handle labels for arrangement objects
                    if (
                        object.isCSS2DObject &&
                        object.name.startsWith("arrangement-label-")
                    ) {
                        // Get the associated mesh object to check jaw group
                        const associatedObjectName = object.name.replace(
                            "arrangement-label-",
                            ""
                        );
                        const associatedMesh = scene.children.find(
                            (child) =>
                                child.isMesh &&
                                child.userData &&
                                child.userData.group === jawGroup &&
                                (child.name === associatedObjectName ||
                                    child.name.includes(associatedObjectName))
                        );

                        if (associatedMesh) {
                            // Label visibility should respect both jaw visibility and label toggle
                            const showArrangementLabels =
                                document.getElementById(
                                    "show-arrangement-labels"
                                );
                            const labelsEnabled = showArrangementLabels
                                ? showArrangementLabels.checked
                                : false;
                            const shouldBeVisible = shouldObjectBeVisible(
                                associatedMesh,
                                visible
                            );
                            object.visible = labelsEnabled && shouldBeVisible;
                        }
                    }
                });
            }
        });

        // Also apply to overlay view if active
        if (globalState.overlayScene) {
            globalState.overlayScene.traverse((object) => {
                if (
                    object.isMesh &&
                    object.userData &&
                    object.userData.group === jawGroup
                ) {
                    const shouldBeVisible = shouldObjectBeVisible(
                        object,
                        visible
                    );
                    object.visible = shouldBeVisible;
                }
            });
        }
    } else {
        // Apply to single view
        const scene =
            sceneKey === "main"
                ? globalState.scene
                : globalState.multiViewScenes[sceneKey];
        if (scene) {
            scene.traverse((object) => {
                if (
                    object.isMesh &&
                    object.userData &&
                    object.userData.group === jawGroup
                ) {
                    // Check if this object should be visible based on all toggle states
                    const shouldBeVisible = shouldObjectBeVisible(
                        object,
                        visible
                    );
                    object.visible = shouldBeVisible;
                }
                // Also handle labels for arrangement objects
                if (
                    object.isCSS2DObject &&
                    object.name.startsWith("arrangement-label-")
                ) {
                    // Get the associated mesh object to check jaw group
                    const associatedObjectName = object.name.replace(
                        "arrangement-label-",
                        ""
                    );
                    const associatedMesh = scene.children.find(
                        (child) =>
                            child.isMesh &&
                            child.userData &&
                            child.userData.group === jawGroup &&
                            (child.name === associatedObjectName ||
                                child.name.includes(associatedObjectName))
                    );

                    if (associatedMesh) {
                        // Label visibility should respect both jaw visibility and label toggle
                        const showArrangementLabels = document.getElementById(
                            "show-arrangement-labels"
                        );
                        const labelsEnabled = showArrangementLabels
                            ? showArrangementLabels.checked
                            : false;
                        const shouldBeVisible = shouldObjectBeVisible(
                            associatedMesh,
                            visible
                        );
                        object.visible = labelsEnabled && shouldBeVisible;
                    }
                }
            });
        }
    }
}

/**
 * Display arrangement objects in a specific scene with optional jaw toggles
 * @param {Array} objects - Array of object data
 * @param {THREE.Scene} scene - The THREE.js scene
 * @param {String} title - Title for the view
 * @param {boolean} enableJawToggles - Whether to enable jaw toggle functionality
 */
function displayArrangementObjectsInScene(
    objects,
    scene,
    title,
    enableJawToggles = false
) {
    if (!objects || !Array.isArray(objects)) {
        console.warn("Invalid objects data for scene:", title);
        return;
    }

    console.log(`🎨 Displaying ${objects.length} objects in scene: ${title}`);

    // Clear existing meshes and labels
    const meshesToRemove = scene.children.filter((child) => child.isMesh);
    const labelsToRemove = scene.children.filter(
        (child) => child.isCSS2DObject
    );

    meshesToRemove.forEach((mesh) => {
        scene.remove(mesh);
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
            if (Array.isArray(mesh.material)) {
                mesh.material.forEach((mat) => mat.dispose());
            } else {
                mesh.material.dispose();
            }
        }
    });

    labelsToRemove.forEach((label) => {
        scene.remove(label);
    });

    // Track added objects to detect duplicates using index-based unique keys
    const addedObjectKeys = new Set();
    const brackets = objects.filter(
        (obj) => obj.type === 107 && obj.descr === "BrktAttm"
    );
    const teeth = objects.filter((obj) => obj.type >= 11 && obj.type <= 48);
    const others = objects.filter(
        (obj) => obj.type !== 107 && (obj.type < 11 || obj.type > 48)
    );

    // Create objects in the scene
    objects.forEach((objData, index) => {
        // Create unique key using index to avoid false duplicates
        const uniqueKey = `${index}_${objData.name || "unnamed"}`;

        // Check for duplicate keys (should never happen with index, but just in case)
        if (addedObjectKeys.has(uniqueKey)) {
            console.warn(
                `⚠️ DUPLICATE OBJECT KEY in ${title}: "${uniqueKey}" - skipping!`
            );
            return;
        }
        addedObjectKeys.add(uniqueKey);
        if (
            !objData.vertices ||
            !Array.isArray(objData.vertices) ||
            objData.vertices.length === 0
        ) {
            console.warn(`Object ${objData.name || index} has no vertices`);
            return;
        }

        // Create geometry from vertices
        const vertices = new Float32Array(objData.vertices.flat());
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute(
            "position",
            new THREE.BufferAttribute(vertices, 3)
        );

        // Add faces if available
        if (
            objData.faces &&
            Array.isArray(objData.faces) &&
            objData.faces.length > 0
        ) {
            const faces = objData.faces.flat();
            geometry.setIndex(faces);
            geometry.computeVertexNormals();
        }

        // Determine color - convert from 0-255 to 0-1 range if needed
        let color = [0.8, 0.8, 0.8]; // Default gray
        if (objData.color && Array.isArray(objData.color)) {
            // Check if values are in 0-255 range or 0-1 range
            const maxValue = Math.max(...objData.color);
            if (maxValue > 1) {
                // Convert from 0-255 to 0-1
                color = objData.color.map((c) => c / 255);
            } else {
                color = objData.color;
            }
        }

        // Special handling for brackets to ensure proper colors
        // Create a temporary mesh object to use with getObjectType function
        const tempMesh = {
            name: objData.name || "",
            userData: {
                type: objData.type,
                descr: objData.descr,
                teethID: objData.teethID,
                originalType: objData.originalType
            }
        };

        // Determine object type using unified classification
        const objectType = getObjectType(tempMesh);

        // Apply colors based on object type - GINGIVA ALWAYS GETS PINK
        if (objectType === "gingiva") {
            // Pink color for all gingiva/soft tissue objects (ALWAYS, regardless of visibility)
            color = [1.0, 0.5, 0.5]; // Pink for gingiva
        } else if (objectType === "bracket") {
            // Apply bracket color
            if (
                !objData.color ||
                (objData.color[0] === 255 &&
                    objData.color[1] === 255 &&
                    objData.color[2] === 255) ||
                (objData.color[0] === 0.8 &&
                    objData.color[1] === 0.8 &&
                    objData.color[2] === 0.8)
            ) {
                const bracketColor = getBracketColor();
                // Convert to 0-1 range
                color = bracketColor.map((c) => c / 255);
            }
        }

        // Create material
        const material = new THREE.MeshStandardMaterial({
            color: new THREE.Color(color[0], color[1], color[2]),
            side: THREE.DoubleSide,
            metalness: 0.3,
            roughness: 0.5,
            transparent: objData.name && objData.name.includes("Soft Tissue"),
            opacity:
                objData.name && objData.name.includes("Soft Tissue") ? 0.7 : 1.0
        });

        // Create mesh
        const mesh = new THREE.Mesh(geometry, material);
        mesh.name = objData.name || `Object_${index}`;

        // Store jaw group information for toggle functionality
        if (enableJawToggles) {
            mesh.userData = {
                group: objData.group, // 0 = upper, 1 = lower
                type: objData.type,
                descr: objData.descr, // Store description for bracket detection
                teethID: objData.teethID, // Store related tooth ID for brackets
                originalType: objData.originalType // Store original type from .obj file
            };
        } else {
            // Still store type for object detection even if jaw toggles aren't enabled
            mesh.userData = {
                type: objData.type,
                descr: objData.descr, // Store description for bracket detection
                teethID: objData.teethID, // Store related tooth ID for brackets
                originalType: objData.originalType // Store original type from .obj file
            };
        }

        // Set initial visibility based on object type and checkbox states
        mesh.visible = getInitialObjectVisibility(mesh);

        // Debug object visibility
        if (!mesh.visible) {
            console.log(
                `🙈 Object "${
                    objData.name || index
                }" created but invisible - Type: ${getObjectType(mesh)}`
            );
        } else {
            console.log(
                `👁️ Object "${
                    objData.name || index
                }" visible - Type: ${getObjectType(mesh)}`
            );
        }

        // Calculate center position for label placement
        geometry.computeBoundingBox();
        const boundingBox = geometry.boundingBox;
        const center = new THREE.Vector3();
        boundingBox.getCenter(center);

        // Create label for the object
        const labelDiv = document.createElement("div");
        labelDiv.className = "arrangement-object-label";

        // Create label text with object type indicator
        let labelText = objData.name || `Object_${index}`;
        if (objData.type === 107 && objData.descr === "BrktAttm") {
            labelText += " [Bracket]";
        } else if (objData.teethID) {
            labelText += ` [Tooth ${objData.teethID}]`;
        } else if (
            objData.name &&
            objData.name.toLowerCase().includes("soft tissue")
        ) {
            labelText += " [Gingiva]";
        }

        labelDiv.textContent = labelText;
        labelDiv.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
        labelDiv.style.color = "white";
        labelDiv.style.padding = "3px 6px";
        labelDiv.style.borderRadius = "4px";
        labelDiv.style.fontSize = "11px";
        labelDiv.style.fontFamily = "Arial, sans-serif";
        labelDiv.style.border = "1px solid rgba(255, 255, 255, 0.3)";
        labelDiv.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.5)";
        labelDiv.style.whiteSpace = "nowrap";

        const label = new CSS2DObject(labelDiv);
        label.position.copy(center);
        label.name = `arrangement-label-${objData.name || index}`;

        // Check if arrangement object labels should be visible
        const showArrangementLabels = document.getElementById(
            "show-arrangement-labels"
        );
        label.visible = showArrangementLabels
            ? showArrangementLabels.checked
            : false;

        // Add both mesh and label to scene
        scene.add(mesh);
        scene.add(label);
    });

    // Summary of created objects
    const visibleMeshes = scene.children.filter(
        (child) => child.isMesh && child.visible
    ).length;
    const totalMeshes = scene.children.filter((child) => child.isMesh).length;
    console.log(
        `📊 Scene "${title}" summary: ${totalMeshes} objects created, ${visibleMeshes} visible`
    );
}

/**
 * Display objects in the main scene (globalState.scene)
 * @param {Array} objects - Array of object data to display
 */
function displayOriginalObjectsInMainScene(objects) {
    if (!objects || !Array.isArray(objects)) {
        console.warn("Invalid objects data for main scene");
        return;
    }

    // Clear existing arrangement objects from main scene
    const meshesToRemove = globalState.scene.children.filter(
        (child) =>
            child.isMesh && child.userData && child.userData.originalObject
    );
    meshesToRemove.forEach((mesh) => {
        globalState.scene.remove(mesh);
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
            if (Array.isArray(mesh.material)) {
                mesh.material.forEach((mat) => mat.dispose());
            } else {
                mesh.material.dispose();
            }
        }
    });

    // Create objects in the main scene
    objects.forEach((objData, index) => {
        if (
            !objData.vertices ||
            !Array.isArray(objData.vertices) ||
            objData.vertices.length === 0
        ) {
            console.warn(`Object ${objData.name || index} has no vertices`);
            return;
        }

        // Create geometry from vertices
        const vertices = new Float32Array(objData.vertices.flat());
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute(
            "position",
            new THREE.BufferAttribute(vertices, 3)
        );

        // Add faces if available
        if (
            objData.faces &&
            Array.isArray(objData.faces) &&
            objData.faces.length > 0
        ) {
            const faces = objData.faces.flat();
            geometry.setIndex(faces);
            geometry.computeVertexNormals();
        }

        // Determine color - convert from 0-255 to 0-1 range if needed
        let color = [0.8, 0.8, 0.8]; // Default gray
        if (objData.color && Array.isArray(objData.color)) {
            // Check if values are in 0-255 range or 0-1 range
            const maxValue = Math.max(...objData.color);
            if (maxValue > 1) {
                // Convert from 0-255 to 0-1
                color = objData.color.map((c) => c / 255);
            } else {
                color = objData.color;
            }
        }

        // Special handling for brackets to ensure proper colors
        // Create a temporary mesh object to use with getObjectType function
        const tempMesh = {
            name: objData.name || "",
            userData: {
                type: objData.type,
                descr: objData.descr,
                teethID: objData.teethID,
                originalType: objData.originalType
            }
        };

        // Determine object type using unified classification
        const objectType = getObjectType(tempMesh);

        // Apply colors based on object type - GINGIVA ALWAYS GETS PINK
        if (objectType === "gingiva") {
            // Pink color for all gingiva/soft tissue objects (ALWAYS, regardless of visibility)
            color = [1.0, 0.5, 0.5]; // Pink for gingiva
        } else if (objectType === "bracket") {
            // Apply bracket color
            if (
                !objData.color ||
                (objData.color[0] === 255 &&
                    objData.color[1] === 255 &&
                    objData.color[2] === 255) ||
                (objData.color[0] === 0.8 &&
                    objData.color[1] === 0.8 &&
                    objData.color[2] === 0.8)
            ) {
                const bracketColor = getBracketColor();
                // Convert to 0-1 range
                color = bracketColor.map((c) => c / 255);
            }
        }

        // Create material
        const material = new THREE.MeshStandardMaterial({
            color: new THREE.Color(color[0], color[1], color[2]),
            side: THREE.DoubleSide,
            metalness: 0.3,
            roughness: 0.5,
            transparent: objData.name && objData.name.includes("Soft Tissue"),
            opacity:
                objData.name && objData.name.includes("Soft Tissue") ? 0.7 : 1.0
        });

        // Create mesh
        const mesh = new THREE.Mesh(geometry, material);
        mesh.name = objData.name || `Object_${index}`;

        // Store metadata for arrangement and jaw toggle functionality
        mesh.userData = {
            originalObject: true, // Mark as arrangement object for cleanup
            group: objData.group, // 0 = upper, 1 = lower
            type: objData.type || objData.Type, // Support both formats
            descr: objData.descr || objData.Descr, // Support both formats for description
            id: objData.id,
            teethID: objData.teethID, // Store related tooth ID for brackets
            originalType: objData.originalType // Store original type from .obj file
        };

        // Set initial visibility based on object type and checkbox states
        mesh.visible = getInitialObjectVisibility(mesh);

        // Calculate center position for label placement
        geometry.computeBoundingBox();
        const boundingBox = geometry.boundingBox;
        const center = new THREE.Vector3();
        boundingBox.getCenter(center);

        // Create label for the object
        const labelDiv = document.createElement("div");
        labelDiv.className = "arrangement-object-label";

        // Create label text with object type indicator
        let labelText = objData.name || `Object_${index}`;
        if (objData.type === 107 && objData.descr === "BrktAttm") {
            labelText += " [Bracket]";
        } else if (objData.teethID) {
            labelText += ` [Tooth ${objData.teethID}]`;
        } else if (
            objData.name &&
            objData.name.toLowerCase().includes("soft tissue")
        ) {
            labelText += " [Gingiva]";
        }

        labelDiv.textContent = labelText;
        labelDiv.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
        labelDiv.style.color = "white";
        labelDiv.style.padding = "3px 6px";
        labelDiv.style.borderRadius = "4px";
        labelDiv.style.fontSize = "11px";
        labelDiv.style.fontFamily = "Arial, sans-serif";
        labelDiv.style.border = "1px solid rgba(255, 255, 255, 0.3)";
        labelDiv.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.5)";
        labelDiv.style.whiteSpace = "nowrap";

        const label = new CSS2DObject(labelDiv);
        label.position.copy(center);
        label.name = `arrangement-label-${objData.name || index}`;

        // Check if arrangement object labels should be visible
        const showArrangementLabels = document.getElementById(
            "show-arrangement-labels"
        );
        label.visible = showArrangementLabels
            ? showArrangementLabels.checked
            : false;

        // Add both mesh and label to main scene
        globalState.scene.add(mesh);
        globalState.scene.add(label);
    });
}

/**
 * Check if both arrangement files are selected and load them for immediate preview
 */
async function checkAndLoadArrangementFiles() {
    const processingMode =
        document.getElementById("processing-mode-dropdown")?.value ||
        "arrangement";
    if (processingMode === "arrangement") {
        updateArrangementActionButtons();
    }

    // Get current input mode
    const inputMode = getInputMode();
    console.log(
        "🔧 checkAndLoadArrangementFiles called - input mode:",
        inputMode
    );

    if (isAzureInputMode()) {
        // For Azure mode, download the files for preview
        const lowerJawAzurePath = document
            .getElementById("lower-jaw-azure-path")
            .value.trim();
        const upperJawAzurePath = document
            .getElementById("upper-jaw-azure-path")
            .value.trim();

        // Only proceed if both Azure paths are provided and valid
        if (!lowerJawAzurePath || !upperJawAzurePath) {
            return; // Wait for both paths to be entered
        }

        // Validate that paths look like .obj files
        if (
            !lowerJawAzurePath.toLowerCase().endsWith(".obj") ||
            !upperJawAzurePath.toLowerCase().endsWith(".obj")
        ) {
            return; // Wait for valid .obj paths
        }

        showToast("Loading arrangement files from Azure preview...", "info");

        try {
            // Download both files from Azure
            console.log("📥 Downloading files from Azure:", {
                lowerJawAzurePath,
                upperJawAzurePath
            });
            const [lowerJawFile, upperJawFile] = await Promise.all([
                ApiService.downloadAzureFile(lowerJawAzurePath),
                ApiService.downloadAzureFile(upperJawAzurePath)
            ]);

            console.log("📥 Downloaded files:", {
                lowerJaw: { name: lowerJawFile.name, size: lowerJawFile.size },
                upperJaw: { name: upperJawFile.name, size: upperJawFile.size }
            });

            // Parse both downloaded OBJ files
            console.log("🔍 Parsing downloaded OBJ files...");
            const [lowerJawResult, upperJawResult] = await Promise.all([
                parseObjFile(lowerJawFile),
                parseObjFile(upperJawFile)
            ]);

            console.log("🔍 Parse results:", {
                lowerJawObjects: lowerJawResult.objects?.length || 0,
                upperJawObjects: upperJawResult.objects?.length || 0
            });

            // The rest of the processing is the same as file mode
            // Extract objects arrays from the results
            const lowerJawObjects = lowerJawResult.objects || [];
            const upperJawObjects = upperJawResult.objects || [];

            // Combine all objects for preview using the same logic as file mode
            const allObjects = [];

            // Add lower jaw objects (group 1) - same processing as file mode
            lowerJawObjects.forEach((obj) => {
                let finalTeethID = obj.teethID;
                if (finalTeethID === null && obj.name) {
                    const nameMatch = obj.name.match(/^(\d+):/);
                    if (nameMatch) {
                        finalTeethID = parseInt(nameMatch[1]);
                    }
                }

                const tempTestObj = {
                    name: obj.name || "",
                    userData: {
                        type: null,
                        descr: obj.metadata?.Type,
                        teethID: finalTeethID,
                        originalType: obj.metadata?.Type
                    }
                };

                const detectedType = getObjectType(tempTestObj);
                const isExplicitBracket = obj.metadata?.Type === "BrktAttm";
                const isImplicitBracket =
                    finalTeethID !== null &&
                    obj.name &&
                    isBracketByName(obj.name);
                const isBracket = isExplicitBracket || isImplicitBracket;

                let objectType = null;
                let objectDescr = obj.metadata?.Type;

                if (detectedType === "gingiva") {
                    objectType = "gingiva";
                    objectDescr = "Gingiva";
                } else if (detectedType === "tooth") {
                    objectType = "tooth";
                    objectDescr = "Tooth";
                } else if (isBracket) {
                    objectType = 107;
                    objectDescr = "BrktAttm";
                }

                const mappedObj = {
                    name: obj.name,
                    vertices: obj.vertices,
                    faces: obj.faces,
                    group: 1, // Lower jaw
                    type: objectType,
                    descr: objectDescr,
                    teethID: finalTeethID,
                    originalType: obj.metadata?.Type,
                    id: obj.id
                };

                allObjects.push(mappedObj);
            });

            // Add upper jaw objects (group 0) - same processing as file mode
            upperJawObjects.forEach((obj) => {
                let finalTeethID = obj.teethID;
                if (finalTeethID === null && obj.name) {
                    const nameMatch = obj.name.match(/^(\d+):/);
                    if (nameMatch) {
                        finalTeethID = parseInt(nameMatch[1]);
                    }
                }

                const tempTestObj = {
                    name: obj.name || "",
                    userData: {
                        type: null,
                        descr: obj.metadata?.Type,
                        teethID: finalTeethID,
                        originalType: obj.metadata?.Type
                    }
                };

                const detectedType = getObjectType(tempTestObj);
                const isExplicitBracket = obj.metadata?.Type === "BrktAttm";
                const isImplicitBracket =
                    finalTeethID !== null &&
                    obj.name &&
                    isBracketByName(obj.name);
                const isBracket = isExplicitBracket || isImplicitBracket;

                let objectType = null;
                let objectDescr = obj.metadata?.Type;

                if (detectedType === "gingiva") {
                    objectType = "gingiva";
                    objectDescr = "Gingiva";
                } else if (detectedType === "tooth") {
                    objectType = "tooth";
                    objectDescr = "Tooth";
                } else if (isBracket) {
                    objectType = 107;
                    objectDescr = "BrktAttm";
                }

                const mappedObj = {
                    name: obj.name,
                    vertices: obj.vertices,
                    faces: obj.faces,
                    group: 0, // Upper jaw
                    type: objectType,
                    descr: objectDescr,
                    teethID: finalTeethID,
                    originalType: obj.metadata?.Type,
                    id: obj.id
                };

                allObjects.push(mappedObj);
            });

            console.log("📊 Total objects created:", allObjects.length);
            console.log(
                "📊 Object details:",
                allObjects.map((obj) => ({
                    name: obj.name,
                    type: obj.type,
                    descr: obj.descr,
                    group: obj.group,
                    vertexCount: obj.vertices?.length || 0
                }))
            );

            if (allObjects.length === 0) {
                console.warn(
                    "⚠️ No objects found after processing Azure files"
                );
                console.log("Debug info:", {
                    lowerJawObjects: lowerJawObjects.length,
                    upperJawObjects: upperJawObjects.length,
                    lowerJawSample: lowerJawObjects.slice(0, 2),
                    upperJawSample: upperJawObjects.slice(0, 2)
                });
                showToast(
                    "No objects found in the Azure files",
                    "exclamation-triangle"
                );
                return;
            }

            // Display objects in main scene
            displayOriginalObjectsInMainScene(allObjects);

            // Note: Jaw toggle controls removed - visibility controls are now in the left sidebar
            cleanupFloatingJawControls();

            const processButton = document.getElementById("process-button");
            processButton.disabled = false;

            showToast(
                `Loaded ${allObjects.length} objects from Azure - Ready for arrangement`,
                "check-circle"
            );
        } catch (error) {
            console.error("Error loading Azure arrangement files:", error);
            showToast(
                `Error loading Azure arrangement files: ${error.message}`,
                "exclamation-triangle"
            );
        }
        return;
    }

    const lowerJawFile = document.getElementById("lower-jaw-file").files[0];
    const upperJawFile = document.getElementById("upper-jaw-file").files[0];

    // Only proceed if both files are selected and are .obj files
    if (!lowerJawFile || !upperJawFile) {
        return; // Wait for both files to be selected
    }

    if (
        !lowerJawFile.name.toLowerCase().endsWith(".obj") ||
        !upperJawFile.name.toLowerCase().endsWith(".obj")
    ) {
        return; // Wait for valid .obj files
    }

    showToast("Loading arrangement files preview...", "info");

    try {
        // Parse both OBJ files
        const [lowerJawResult, upperJawResult] = await Promise.all([
            parseObjFile(lowerJawFile),
            parseObjFile(upperJawFile)
        ]);

        // Extract objects arrays from the results
        const lowerJawObjects = lowerJawResult.objects || [];
        const upperJawObjects = upperJawResult.objects || [];

        // Combine all objects for preview
        const allObjects = [];

        // Add lower jaw objects (group 1)
        lowerJawObjects.forEach((obj) => {
            // Extract teethID from object name if not already present
            let finalTeethID = obj.teethID;
            if (finalTeethID === null && obj.name) {
                const nameMatch = obj.name.match(/^(\d+):/);
                if (nameMatch) {
                    finalTeethID = parseInt(nameMatch[1]);
                }
            }

            // Create a temporary object for classification testing
            const tempTestObj = {
                name: obj.name || "",
                userData: {
                    type: null,
                    descr: obj.metadata?.Type,
                    teethID: finalTeethID,
                    originalType: obj.metadata?.Type
                }
            };

            // Detect object type using unified classification
            const detectedType = getObjectType(tempTestObj);

            // Determine if this is a bracket object (legacy check)
            const isExplicitBracket = obj.metadata?.Type === "BrktAttm";
            const isImplicitBracket =
                finalTeethID !== null && obj.name && isBracketByName(obj.name);
            const isBracket = isExplicitBracket || isImplicitBracket;

            // Enhanced mapping with proper type detection
            let objectType = null;
            let objectDescr = obj.metadata?.Type;

            if (detectedType === "gingiva") {
                objectType = "gingiva"; // Custom type for gingiva
                objectDescr = "Gingiva";
            } else if (detectedType === "tooth") {
                objectType = "tooth"; // Custom type for teeth
                objectDescr = "Tooth";
            } else if (isBracket) {
                objectType = 107; // Standard bracket type
                objectDescr = "BrktAttm";
            }

            const mappedObj = {
                name: obj.name,
                vertices: obj.vertices,
                faces: obj.faces,
                group: 1, // Lower jaw
                type: objectType,
                descr: objectDescr,
                teethID: finalTeethID,
                originalType: obj.metadata?.Type,
                id: obj.id
            };

            allObjects.push(mappedObj);
        });

        // Add upper jaw objects (group 0)
        upperJawObjects.forEach((obj) => {
            // Extract teethID from object name if not already present
            let finalTeethID = obj.teethID;
            if (finalTeethID === null && obj.name) {
                const nameMatch = obj.name.match(/^(\d+):/);
                if (nameMatch) {
                    finalTeethID = parseInt(nameMatch[1]);
                }
            }

            // Create a temporary object for classification testing
            const tempTestObj = {
                name: obj.name || "",
                userData: {
                    type: null,
                    descr: obj.metadata?.Type,
                    teethID: finalTeethID,
                    originalType: obj.metadata?.Type
                }
            };

            // Detect object type using unified classification
            const detectedType = getObjectType(tempTestObj);

            // Determine if this is a bracket object (legacy check)
            const isExplicitBracket = obj.metadata?.Type === "BrktAttm";
            const isImplicitBracket =
                finalTeethID !== null && obj.name && isBracketByName(obj.name);
            const isBracket = isExplicitBracket || isImplicitBracket;

            // Enhanced mapping with proper type detection
            let objectType = null;
            let objectDescr = obj.metadata?.Type;

            if (detectedType === "gingiva") {
                objectType = "gingiva"; // Custom type for gingiva
                objectDescr = "Gingiva";
            } else if (detectedType === "tooth") {
                objectType = "tooth"; // Custom type for teeth
                objectDescr = "Tooth";
            } else if (isBracket) {
                objectType = 107; // Standard bracket type
                objectDescr = "BrktAttm";
            }

            const mappedObj = {
                name: obj.name,
                vertices: obj.vertices,
                faces: obj.faces,
                group: 0, // Upper jaw
                type: objectType,
                descr: objectDescr,
                teethID: finalTeethID,
                originalType: obj.metadata?.Type,
                id: obj.id
            };

            allObjects.push(mappedObj);
        });

        if (allObjects.length === 0) {
            showToast(
                "No objects found in the selected files",
                "exclamation-triangle"
            );
            return;
        }

        // Display objects in main scene
        displayOriginalObjectsInMainScene(allObjects);

        // Note: Jaw toggle controls removed - visibility controls are now in the left sidebar
        cleanupFloatingJawControls();

        const processButton = document.getElementById("process-button");
        processButton.disabled = false;

        showToast(
            `Loaded ${allObjects.length} objects - Ready for arrangement`,
            "check-circle"
        );
    } catch (error) {
        console.error("Error loading arrangement files:", error);
        showToast(
            `Error loading arrangement files: ${error.message}`,
            "exclamation-triangle"
        );
    }
}

// Note: addJawToggleControlsForMainView removed - visibility controls are now in the left sidebar

/**
 * Toggle visibility of gingiva/soft tissue objects in multiple scenes
 * @param {boolean} visible - Whether to show or hide
 * @param {string} sceneKey - Key for the scene ('original', 'segmented', or 'both')
 */
function toggleGingivaVisibility(visible, sceneKey) {
    if (sceneKey === "both") {
        // Apply to both original and segmented views
        ["original", "segmented"].forEach((viewKey) => {
            const scene = globalState.multiViewScenes[viewKey];
            if (scene) {
                scene.traverse((object) => {
                    if (object.isMesh && getObjectType(object) === "gingiva") {
                        // Check jaw visibility for this object
                        const jawVisible = getJawVisibility(
                            object.userData?.group
                        );
                        const shouldBeVisible = visible && jawVisible;
                        object.visible = shouldBeVisible;
                    }
                    // Also handle labels for gingiva objects
                    if (
                        object.isCSS2DObject &&
                        object.name.startsWith("arrangement-label-")
                    ) {
                        // Get the associated mesh object to check if it's gingiva
                        const associatedObjectName = object.name.replace(
                            "arrangement-label-",
                            ""
                        );
                        const associatedMesh = scene.children.find(
                            (child) =>
                                child.isMesh &&
                                (child.name === associatedObjectName ||
                                    child.name.includes(associatedObjectName))
                        );

                        if (
                            associatedMesh &&
                            getObjectType(associatedMesh) === "gingiva"
                        ) {
                            // Label visibility should respect both gingiva visibility and label toggle
                            const showArrangementLabels =
                                document.getElementById(
                                    "show-arrangement-labels"
                                );
                            const labelsEnabled = showArrangementLabels
                                ? showArrangementLabels.checked
                                : false;
                            const jawVisible = getJawVisibility(
                                associatedMesh.userData?.group
                            );
                            const shouldBeVisible = visible && jawVisible;
                            object.visible = labelsEnabled && shouldBeVisible;
                        }
                    }
                });
            }
        });

        // Also apply to overlay view if active
        if (globalState.overlayScene) {
            globalState.overlayScene.traverse((object) => {
                if (object.isMesh && getObjectType(object) === "gingiva") {
                    const jawVisible = getJawVisibility(object.userData?.group);
                    const shouldBeVisible = visible && jawVisible;
                    object.visible = shouldBeVisible;
                }
            });
        }
    } else {
        // Apply to single view
        const scene =
            sceneKey === "main"
                ? globalState.scene
                : globalState.multiViewScenes[sceneKey];
        if (scene) {
            scene.traverse((object) => {
                if (object.isMesh && getObjectType(object) === "gingiva") {
                    // Check jaw visibility for this object
                    const jawVisible = getJawVisibility(object.userData?.group);
                    const shouldBeVisible = visible && jawVisible;
                    object.visible = shouldBeVisible;
                }
                // Also handle labels for gingiva objects
                if (
                    object.isCSS2DObject &&
                    object.name.startsWith("arrangement-label-")
                ) {
                    // Get the associated mesh object to check if it's gingiva
                    const associatedObjectName = object.name.replace(
                        "arrangement-label-",
                        ""
                    );
                    const associatedMesh = scene.children.find(
                        (child) =>
                            child.isMesh &&
                            (child.name === associatedObjectName ||
                                child.name.includes(associatedObjectName))
                    );

                    if (
                        associatedMesh &&
                        getObjectType(associatedMesh) === "gingiva"
                    ) {
                        // Label visibility should respect both gingiva visibility and label toggle
                        const showArrangementLabels = document.getElementById(
                            "show-arrangement-labels"
                        );
                        const labelsEnabled = showArrangementLabels
                            ? showArrangementLabels.checked
                            : false;
                        const jawVisible = getJawVisibility(
                            associatedMesh.userData?.group
                        );
                        const shouldBeVisible = visible && jawVisible;
                        object.visible = labelsEnabled && shouldBeVisible;
                    }
                }
            });
        }
    }
}

/**
 * Determine the type of a dental object
 * @param {THREE.Object3D} object - The object to classify
 * @returns {string} Object type: 'gingiva', 'tooth', 'bracket', or 'other'
 */
function getObjectType(object) {
    if (!object.name && !object.userData) return "other";

    const objectName = object.name || "unnamed";
    const name = object.name ? object.name.toLowerCase() : "";

    // Check for gingiva/soft tissue first (highest priority)
    const gingivaKeywords = [
        "gingiva",
        "soft tissue",
        "maxilla",
        "mandible",
        "gum",
        "tissue",
        "soft"
    ];

    if (gingivaKeywords.some((keyword) => name.includes(keyword))) {
        return "gingiva";
    }

    // Check for tooth objects
    const toothPatterns = [
        /^tooth\s*\d+$/i, // "Tooth 1", "Tooth1"
        /^\d+\s*tooth$/i, // "1 Tooth", "1tooth"
        /^[a-z]*\s*\d{1,2}$/i, // "M1", "P2", "C", "I1" (dental notation)
        /^(central|lateral|canine|premolar|molar|incisor)/i // Tooth types
    ];

    if (toothPatterns.some((pattern) => pattern.test(name))) {
        return "tooth";
    }

    // Check for tooth-specific keywords (but exclude if it contains bracket keywords)
    const toothKeywords = ["crown", "root", "enamel", "dentin"];
    const hasToothKeyword = toothKeywords.some((keyword) =>
        name.includes(keyword)
    );

    if (hasToothKeyword) {
        const bracketKeywords = ["bracket", "brkt", "tube", "attachment"];
        const hasBracketKeyword = bracketKeywords.some((keyword) =>
            name.includes(keyword)
        );
        if (!hasBracketKeyword) {
            return "tooth";
        }
    }

    // Check for explicit object types set during mapping
    if (object.userData) {
        // Check for explicitly set gingiva type
        if (
            object.userData.type === "gingiva" ||
            object.userData.descr === "Gingiva"
        ) {
            return "gingiva";
        }

        // Check for explicitly set tooth type
        if (
            object.userData.type === "tooth" ||
            object.userData.descr === "Tooth"
        ) {
            return "tooth";
        }

        // Check for bracket type (Type 107 is bracket in InfoJson)
        if (object.userData.type === 107) {
            return "bracket";
        }

        // Check for bracket description
        if (object.userData.descr === "BrktAttm") {
            return "bracket";
        }

        // Check for original type from .obj file
        if (object.userData.originalType === "BrktAttm") {
            return "bracket";
        }

        // Check if teethID is set (brackets have associated tooth IDs)
        if (
            object.userData.teethID !== undefined &&
            object.userData.teethID !== null
        ) {
            // Additional check using name pattern to confirm it's a bracket
            if (object.name && isBracketByName(object.name)) {
                return "bracket";
            }
        }
    }

    // Check by name pattern as fallback for brackets
    if (object.name && isBracketByName(object.name)) {
        console.log(
            `⚠️ Object "${objectName}" classified as bracket by name pattern only - missing metadata`
        );
        return "bracket";
    }

    // Default: other object type
    return "other";
}

/**
 * Toggle visibility of bracket objects in multiple scenes
 * @param {boolean} visible - Whether to show or hide
 * @param {string} sceneKey - Key for the scene ('original', 'segmented', or 'both')
 */
function toggleBracketsVisibility(visible, sceneKey) {
    if (sceneKey === "both") {
        // Apply to both original and segmented views
        ["original", "segmented"].forEach((viewKey) => {
            const scene = globalState.multiViewScenes[viewKey];
            if (scene) {
                scene.traverse((object) => {
                    if (object.isMesh && getObjectType(object) === "bracket") {
                        // Check jaw visibility for this object
                        const jawVisible = getJawVisibility(
                            object.userData?.group
                        );
                        const shouldBeVisible = visible && jawVisible;
                        object.visible = shouldBeVisible;
                    }
                    // Also handle labels for bracket objects
                    if (
                        object.isCSS2DObject &&
                        object.name.startsWith("arrangement-label-")
                    ) {
                        // Get the associated mesh object to check if it's a bracket
                        const associatedObjectName = object.name.replace(
                            "arrangement-label-",
                            ""
                        );
                        const associatedMesh = scene.children.find(
                            (child) =>
                                child.isMesh &&
                                (child.name === associatedObjectName ||
                                    child.name.includes(associatedObjectName))
                        );

                        if (
                            associatedMesh &&
                            getObjectType(associatedMesh) === "bracket"
                        ) {
                            // Label visibility should respect both bracket visibility and label toggle
                            const showArrangementLabels =
                                document.getElementById(
                                    "show-arrangement-labels"
                                );
                            const labelsEnabled = showArrangementLabels
                                ? showArrangementLabels.checked
                                : false;
                            const jawVisible = getJawVisibility(
                                associatedMesh.userData?.group
                            );
                            const shouldBeVisible = visible && jawVisible;
                            object.visible = labelsEnabled && shouldBeVisible;
                        }
                    }
                });
            }
        });

        // Also apply to overlay view if active
        if (globalState.overlayScene) {
            globalState.overlayScene.traverse((object) => {
                if (object.isMesh && getObjectType(object) === "bracket") {
                    const jawVisible = getJawVisibility(object.userData?.group);
                    const shouldBeVisible = visible && jawVisible;
                    object.visible = shouldBeVisible;
                }
            });
        }
    } else {
        // Apply to single view
        const scene =
            sceneKey === "main"
                ? globalState.scene
                : globalState.multiViewScenes[sceneKey];
        if (scene) {
            scene.traverse((object) => {
                if (object.isMesh && getObjectType(object) === "bracket") {
                    // Check jaw visibility for this object
                    const jawVisible = getJawVisibility(object.userData?.group);
                    const shouldBeVisible = visible && jawVisible;
                    object.visible = shouldBeVisible;
                }
                // Also handle labels for bracket objects
                if (
                    object.isCSS2DObject &&
                    object.name.startsWith("arrangement-label-")
                ) {
                    // Get the associated mesh object to check if it's a bracket
                    const associatedObjectName = object.name.replace(
                        "arrangement-label-",
                        ""
                    );
                    const associatedMesh = scene.children.find(
                        (child) =>
                            child.isMesh &&
                            (child.name === associatedObjectName ||
                                child.name.includes(associatedObjectName))
                    );

                    if (
                        associatedMesh &&
                        getObjectType(associatedMesh) === "bracket"
                    ) {
                        // Label visibility should respect both bracket visibility and label toggle
                        const showArrangementLabels = document.getElementById(
                            "show-arrangement-labels"
                        );
                        const labelsEnabled = showArrangementLabels
                            ? showArrangementLabels.checked
                            : false;
                        const jawVisible = getJawVisibility(
                            associatedMesh.userData?.group
                        );
                        const shouldBeVisible = visible && jawVisible;
                        object.visible = labelsEnabled && shouldBeVisible;
                    }
                }
            });
        }
    }
}

/**
 * Check if an object is a bracket based on name patterns
 * @param {string} name - The object name to check
 * @returns {boolean} True if the name indicates a bracket
 */
function isBracketByName(name) {
    if (!name) return false;

    const nameLower = name.toLowerCase();

    // First, check for explicit exclusion patterns (definitely NOT brackets)
    const exclusionPatterns = [
        "tooth ", // "Tooth 37", "Tooth 16", etc.
        "gingiva", // Gingival tissue
        "soft tissue", // "Mandible (Soft Tissue)", etc.
        "hard tissue", // Hard tissue objects
        "plane", // Occlusal planes
        "mandible", // Jaw bones (unless they have bracket pattern)
        "maxilla" // Jaw bones (unless they have bracket pattern)
    ];

    // If name matches any exclusion pattern, it's NOT a bracket
    if (exclusionPatterns.some((pattern) => nameLower.includes(pattern))) {
        return false;
    }

    // Check for positive bracket indicators
    // Brackets can have explicit keywords OR follow the pattern "number: model - description"
    const bracketKeywords = [
        "bracket",
        "brkt",
        "tube",
        "attachment",
        "attm",
        "brace"
    ];

    // Check for explicit bracket keywords
    if (bracketKeywords.some((keyword) => nameLower.includes(keyword))) {
        return true;
    }

    // Check for bracket naming pattern: "number: model - description"
    // Examples: "21: 1X-1 - Metal SL", "17: 4T-1 - Molar Tube"
    // Pattern: starts with digits, colon, then model number/description
    const bracketPattern = /^\d+:\s*[\w-]+/;
    if (bracketPattern.test(name)) {
        // Additional check: if it has a colon and doesn't match exclusions, likely a bracket
        return true;
    }

    // Default: not a bracket
    return false;
}

/**
 * Get the current visibility state of a jaw group
 * @param {number} jawGroup - 0 for upper, 1 for lower
 * @returns {boolean} True if the jaw should be visible
 */
function getJawVisibility(jawGroup) {
    if (jawGroup === 0) {
        // Upper jaw
        const upperCheckbox = document.getElementById("show-upper-jaw");
        return upperCheckbox ? upperCheckbox.checked : true;
    } else if (jawGroup === 1) {
        // Lower jaw
        const lowerCheckbox = document.getElementById("show-lower-jaw");
        return lowerCheckbox ? lowerCheckbox.checked : true;
    }
    // Default: assume visible if no group specified
    return true;
}

/**
 * Check if an object should be visible based on all toggle states
 * @param {THREE.Object3D} object - The object to check
 * @param {boolean} jawVisible - Whether the jaw containing this object should be visible
 * @returns {boolean} True if the object should be visible
 */
function shouldObjectBeVisible(object, jawVisible) {
    // If the jaw is hidden, hide everything in that jaw
    if (!jawVisible) {
        return false;
    }

    // If jaw is visible, check individual object type toggles
    const objectType = getObjectType(object);

    switch (objectType) {
        case "gingiva":
            const gingivaCheckbox = document.getElementById("show-gingiva");
            return gingivaCheckbox ? gingivaCheckbox.checked : false;

        case "bracket":
            const bracketsCheckbox = document.getElementById("show-brackets");
            return bracketsCheckbox ? bracketsCheckbox.checked : false;

        case "tooth":
        case "other":
        default:
            // For regular teeth and other objects, follow jaw visibility
            return jawVisible;
    }
}

/**
 * Get initial visibility for an object based on its type and checkbox states
 * @param {THREE.Object3D} object - The object to check
 * @returns {boolean} True if the object should be visible initially
 */
function getInitialObjectVisibility(object) {
    const objectType = getObjectType(object);

    // Handle object type visibility first
    switch (objectType) {
        case "gingiva":
            // Gingiva hidden by default
            const gingivaCheckbox = document.getElementById("show-gingiva");
            return gingivaCheckbox ? gingivaCheckbox.checked : false;

        case "bracket":
            // Brackets hidden by default
            const bracketsCheckbox = document.getElementById("show-brackets");
            return bracketsCheckbox ? bracketsCheckbox.checked : false;

        case "tooth":
        case "other":
        default:
            // For teeth and other objects, check jaw group visibility
            if (object.userData && typeof object.userData.group === "number") {
                if (object.userData.group === 0) {
                    // Upper jaw
                    const upperCheckbox =
                        document.getElementById("show-upper-jaw");
                    return upperCheckbox ? upperCheckbox.checked : true;
                } else if (object.userData.group === 1) {
                    // Lower jaw
                    const lowerCheckbox =
                        document.getElementById("show-lower-jaw");
                    return lowerCheckbox ? lowerCheckbox.checked : true;
                }
            }
            // Default: show the object
            return true;
    }
}

/**
 * Clear only inference results (keeps files loaded but clears processing results)
 * This is called when new files are selected to ensure clean state for new inference
 */
function clearInferenceResults() {
    console.log("🧹 Clearing previous inference results...");

    // Clear inference data
    globalState.lastInferenceResult = null;
    globalState.lastArrangementInfoJson = null;
    globalState.displacementTable = null;

    // Clear separation phase data
    globalState.separationPhase = 0;
    globalState.separationData = {
        originalMesh: null,
        segmentedMesh: null,
        analyzedTeeth: null,
        finalResult: null,
        reconstructionResult: null
    };

    // Remove existing class labels from scene
    if (globalState.classLabels) {
        globalState.classLabels.forEach((label) =>
            globalState.scene.remove(label)
        );
        globalState.classLabels = [];
    }

    // Remove keypoint objects and labels
    for (let i = globalState.scene.children.length - 1; i >= 0; i--) {
        const child = globalState.scene.children[i];
        if (child.name?.startsWith("keypoint-")) {
            globalState.scene.remove(child);
        }
    }
    globalState.keypoints = {};
    globalState.keypointLabels = [];

    // Remove arrangement object labels from main scene
    for (let i = globalState.scene.children.length - 1; i >= 0; i--) {
        const child = globalState.scene.children[i];
        if (
            child.isCSS2DObject &&
            child.name?.startsWith("arrangement-label-")
        ) {
            globalState.scene.remove(child);
        }
    }

    // Remove boundary points and lines
    if (globalState.boundaryPoints) {
        globalState.boundaryScene.remove(globalState.boundaryPoints);
        globalState.boundaryPoints = null;
    }
    if (globalState.boundaryLines) {
        globalState.boundaryScene.remove(globalState.boundaryLines);
        globalState.boundaryLines = null;
    }
    if (globalState.simplifiedBoundaryPoints) {
        globalState.simplifiedBoundaryScene.remove(
            globalState.simplifiedBoundaryPoints
        );
        globalState.simplifiedBoundaryPoints = null;
    }
    if (globalState.simplifiedBoundaryLines) {
        globalState.simplifiedBoundaryScene.remove(
            globalState.simplifiedBoundaryLines
        );
        globalState.simplifiedBoundaryLines = null;
    }

    // Clear multi-view scenes if active
    if (globalState.renderMultiView && globalState.multiViewScenes) {
        Object.keys(globalState.multiViewScenes).forEach((view) => {
            if (globalState.multiViewScenes[view]) {
                // Keep only lights by removing other objects
                for (
                    let i =
                        globalState.multiViewScenes[view].children.length - 1;
                    i >= 0;
                    i--
                ) {
                    const obj = globalState.multiViewScenes[view].children[i];
                    if (!(obj instanceof THREE.Light)) {
                        globalState.multiViewScenes[view].remove(obj);
                        // Dispose of geometry and materials
                        if (obj.geometry) obj.geometry.dispose();
                        if (obj.material) {
                            if (Array.isArray(obj.material)) {
                                obj.material.forEach((mat) => mat.dispose());
                            } else {
                                obj.material.dispose();
                            }
                        }
                    }
                }
            }
        });

        // Hide multi-view container and show main view
        const multiViewContainer = document.getElementById(
            "multi-view-container"
        );
        if (multiViewContainer) {
            multiViewContainer.style.display = "none";
        }
        globalState.renderer.domElement.style.display = "block";
        globalState.labelRenderer.domElement.style.display = "block";
        globalState.renderMultiView = false;
    }

    // Hide result-related buttons
    hideInfoJsonActions();

    const nextPhaseButton = document.getElementById("next-phase-button");
    if (nextPhaseButton) {
        nextPhaseButton.style.display = "none";
    }

    const downloadButtonsContainer = document.getElementById(
        "download-buttons-container"
    );
    if (downloadButtonsContainer) {
        downloadButtonsContainer.style.display = "none";
    }

    const downloadResultsButton = document.getElementById(
        "download-results-button"
    );
    if (downloadResultsButton) {
        downloadResultsButton.style.display = "none";
    }

    const downloadObjButton = document.getElementById("download-obj-button");
    if (downloadObjButton) {
        downloadObjButton.style.display = "none";
    }

    // Hide start over button
    hideStartOverAndFeedbackButtons();

    const processButton = document.getElementById("process-button");
    if (processButton) {
        processButton.textContent = "Process";
        processButton.disabled = false;
    }

    // Re-enable keypoint labels checkbox when clearing results
    enableKeypointLabelsCheckbox();

    // Update postprocessing section visibility after clearing results
    updatePostprocessingSectionVisibility();
    const currentMode =
        document.getElementById("processing-mode-dropdown")?.value ||
        "arrangement";
    updateModelOptionsSectionVisibility(currentMode);
    updateClearButtonFromState();
    updateArrangementActionButtons();

    console.log("✅ Inference results cleared");
}

/**
 * Clear all files and state when switching processing modes
 */
function clearAllFilesAndState() {
    // Clear the main file and all processing results
    clearFile();

    // Clear arrangement file inputs
    const lowerJawFile = document.getElementById("lower-jaw-file");
    const upperJawFile = document.getElementById("upper-jaw-file");
    const infoJsonFile = document.getElementById("info-json-file");
    const landmarksCsvFile = document.getElementById("landmarks-csv-file");
    if (lowerJawFile) lowerJawFile.value = "";
    if (upperJawFile) upperJawFile.value = "";
    if (infoJsonFile) infoJsonFile.value = "";
    if (landmarksCsvFile) landmarksCsvFile.value = "";

    // Clear Azure path inputs
    const singleAzurePath = document.getElementById("azure-path-input");
    const lowerJawAzurePath = document.getElementById("lower-jaw-azure-path");
    const upperJawAzurePath = document.getElementById("upper-jaw-azure-path");
    const infoJsonAzurePath = document.getElementById("info-json-azure-path");
    const landmarksCsvAzurePath = document.getElementById(
        "landmarks-csv-azure-path"
    );
    if (singleAzurePath) singleAzurePath.value = "";
    if (lowerJawAzurePath) lowerJawAzurePath.value = "";
    if (upperJawAzurePath) upperJawAzurePath.value = "";
    if (infoJsonAzurePath) infoJsonAzurePath.value = "";
    if (landmarksCsvAzurePath) landmarksCsvAzurePath.value = "";

    // Clear the Azure folder input and its resolution preview/error
    const arrangementFolderPath = document.getElementById(
        "arrangement-folder-path"
    );
    if (arrangementFolderPath) arrangementFolderPath.value = "";
    const folderResolvePreview = document.getElementById(
        "folder-resolve-preview"
    );
    if (folderResolvePreview) {
        folderResolvePreview.textContent = "";
        folderResolvePreview.style.display = "none";
    }
    const folderResolveError = document.getElementById("folder-resolve-error");
    if (folderResolveError) {
        folderResolveError.textContent = "";
        folderResolveError.style.display = "none";
    }
    const overrideAzurePathsCheckbox = document.getElementById(
        "override-azure-paths-checkbox"
    );
    if (overrideAzurePathsCheckbox) overrideAzurePathsCheckbox.checked = false;

    // Clear arrangement file name displays
    const lowerJawFileName = document.getElementById("lower-jaw-file-name");
    const upperJawFileName = document.getElementById("upper-jaw-file-name");
    const infoJsonFileName = document.getElementById("info-json-file-name");
    const landmarksCsvFileName = document.getElementById(
        "landmarks-csv-file-name"
    );
    if (lowerJawFileName) lowerJawFileName.textContent = "";
    if (upperJawFileName) upperJawFileName.textContent = "";
    if (infoJsonFileName) infoJsonFileName.textContent = "";
    if (landmarksCsvFileName) landmarksCsvFileName.textContent = "";

    // Clear single file name display
    const fileNameDiv = document.getElementById("file-name");
    if (fileNameDiv) fileNameDiv.textContent = "";

    // Hide all mode-specific buttons
    hideInfoJsonActions();

    const nextPhaseButton = document.getElementById("next-phase-button");
    if (nextPhaseButton) {
        nextPhaseButton.style.display = "none";
    }

    // Hide view mode toggle
    const viewModeContainer = document.getElementById(
        "arrangement-view-mode-container"
    );
    if (viewModeContainer) {
        viewModeContainer.style.display = "none";
    }

    // Reset view mode to dual
    globalState.overlayViewMode = "dual";
    const overlayViewContainer = document.getElementById(
        "overlay-view-container"
    );
    if (overlayViewContainer) {
        overlayViewContainer.style.display = "none";
    }

    // Hide download buttons from teeth reconstruction
    const downloadButtonsContainer = document.getElementById(
        "download-buttons-container"
    );
    if (downloadButtonsContainer) {
        downloadButtonsContainer.style.display = "none";
    }

    const downloadResultsButton = document.getElementById(
        "download-results-button"
    );
    if (downloadResultsButton) {
        downloadResultsButton.style.display = "none";
    }

    const downloadObjButton = document.getElementById("download-obj-button");
    if (downloadObjButton) {
        downloadObjButton.style.display = "none";
    }

    // Remove jaw toggle controls if they exist
    const jawToggleControls = document.getElementById("jaw-toggle-controls");
    if (jawToggleControls) {
        jawToggleControls.remove();
    }

    // Reset process button
    const processButton = document.getElementById("process-button");
    if (processButton) {
        processButton.textContent = "Process";
        processButton.disabled = false;
    }

    // Reset processing state
    globalState.processingFile = false;
    globalState.separationPhase = 0;
    globalState.lastInferenceResult = null;
    globalState.lastArrangementInfoJson = null;

    // Clear separation data
    globalState.separationData = {
        originalMesh: null,
        segmentedMesh: null,
        analyzedTeeth: null,
        finalResult: null,
        reconstructionResult: null
    };

    // Hide processing indicator
    const processingIndicator = document.getElementById("processing-indicator");
    if (processingIndicator) {
        processingIndicator.style.display = "none";
    }

    // Clear multi-view scenes if they exist
    if (globalState.multiViewScenes) {
        Object.keys(globalState.multiViewScenes).forEach((view) => {
            if (globalState.multiViewScenes[view]) {
                // Keep only lights by removing other objects
                for (
                    let i =
                        globalState.multiViewScenes[view].children.length - 1;
                    i >= 0;
                    i--
                ) {
                    const obj = globalState.multiViewScenes[view].children[i];
                    if (!(obj instanceof THREE.Light)) {
                        globalState.multiViewScenes[view].remove(obj);
                        // Dispose of geometry and materials
                        if (obj.geometry) obj.geometry.dispose();
                        if (obj.material) {
                            if (Array.isArray(obj.material)) {
                                obj.material.forEach((mat) => mat.dispose());
                            } else {
                                obj.material.dispose();
                            }
                        }
                    }
                }
            }
        });
    }

    // Reset multi-view container to default state
    resetMultiViewContainer();

    // Turn off multi-view rendering
    globalState.renderMultiView = false;
    globalState.multiViewLayoutInitialized = false;

    // Hide start over button and feedback button
    hideStartOverAndFeedbackButtons();
    hideFeedbackButton();

    // Update postprocessing section visibility after clearing all files and state
    updatePostprocessingSectionVisibility();
    const currentMode =
        document.getElementById("processing-mode-dropdown")?.value ||
        "arrangement";
    updateModelOptionsSectionVisibility(currentMode);
    updateClearButtonFromState();
    updateArrangementActionButtons();

    // Show success message
    showToast("Files cleared for new processing mode", "check-circle");
}

/**
 * Displacement table coloring: same thresholds as tools/arrangement/check_displacement_calculation.py
 * Returns "green" | "yellow" | "red" | null (no class).
 */
function getDisplacementCellColorLevel(rawVal, movementKey, toothNum) {
    if (rawVal == null || toothNum == null) return null;

    const anteriorTeeth = new Set([
        11, 12, 13, 21, 22, 23, 31, 32, 33, 41, 42, 43
    ]);
    const posteriorTeeth = new Set([
        14, 15, 16, 17, 18, 24, 25, 26, 27, 28, 34, 35, 36, 37, 38, 44, 45, 46,
        47, 48
    ]);

    let region = null;
    if (anteriorTeeth.has(toothNum)) region = "anterior";
    else if (posteriorTeeth.has(toothNum)) region = "posterior";

    if (movementKey === "mesial_distal") region = "posterior";
    if (region == null) return null;

    const thresholds = {
        mesial_distal: { anterior: null, posterior: [2.0, 3.0, 3.0] },
        buccal_lingual: {
            anterior: [2.0, 3.0, 3.0],
            posterior: [1.5, 2.5, 2.5]
        },
        vertical: { anterior: [2.5, 5.0, 5.0], posterior: [1.5, 2.5, 2.5] },
        rotation: {
            anterior: [45.0, 90.0, 90.0],
            posterior: [30.0, 45.0, 45.0]
        },
        tip: { anterior: [10.0, 15.0, 15.0], posterior: [8.0, 12.0, 12.0] },
        torque: { anterior: [12.0, 18.0, 18.0], posterior: [10.0, 15.0, 15.0] }
    };

    const thr = thresholds[movementKey] && thresholds[movementKey][region];
    if (!thr) return null;
    const [gMax, yMax, rMin] = thr;
    const mag = Math.abs(rawVal);

    if (mag <= gMax) return "green";
    if (mag <= yMax) return "yellow";
    if (mag > rMin) return "red";
    return null;
}

/**
 * Show displacement table in modal
 */
function showDisplacementTable() {
    if (
        !globalState.displacementTable ||
        globalState.displacementTable.length === 0
    ) {
        showToast(
            "No displacement table data available",
            "exclamation-triangle"
        );
        return;
    }

    const modal = document.getElementById("displacement-table-modal");
    const container = document.getElementById("displacement-table-container");

    if (!modal || !container) {
        console.error("Displacement table modal elements not found");
        return;
    }

    // Clear previous content
    container.innerHTML = "";

    // Create a map to organize data by tooth_id
    const dataByToothId = new Map();
    const toothIds = [];

    // Collect all unique tooth IDs and organize data
    globalState.displacementTable.forEach((record) => {
        const toothId = record.tooth_id || "-";
        if (!dataByToothId.has(toothId)) {
            dataByToothId.set(toothId, record);
            toothIds.push(toothId);
        }
    });

    // Separate tooth IDs into Maxilla (upper) and Mandible (lower)
    const maxillaIds = [];
    const mandibleIds = [];

    toothIds.forEach((toothId) => {
        if (String(toothId).startsWith("upper-")) {
            maxillaIds.push(toothId);
        } else if (String(toothId).startsWith("lower-")) {
            mandibleIds.push(toothId);
        }
    });

    // Desired column order: Maxilla 18..11, 21..28; Mandible 48..41, 31..38
    const MAXILLA_ORDER = [
        18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28
    ];
    const MANDIBLE_ORDER = [
        48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38
    ];

    const sortByOrder = (order) => (a, b) => {
        const getFDI = (id) => {
            const sid = String(id);
            const m = sid.match(/(?:upper|lower)-(\d+)/);
            return m ? parseInt(m[1], 10) : NaN;
        };
        const idxA = order.indexOf(getFDI(a));
        const idxB = order.indexOf(getFDI(b));
        const posA = idxA === -1 ? 999 : idxA;
        const posB = idxB === -1 ? 999 : idxB;
        return posA - posB;
    };

    maxillaIds.sort(sortByOrder(MAXILLA_ORDER));
    mandibleIds.sort(sortByOrder(MANDIBLE_ORDER));

    // Define metrics and their properties (rendered as rows within each jaw
    // table).
    const metrics = [
        {
            name: "Mesial/Distal (mm)",
            key: "mesial_distal",
            index: 0,
            decimals: 1
        },
        {
            name: "Buccal/Lingual (mm)",
            key: "buccal_lingual",
            index: 1,
            decimals: 1
        },
        {
            name: "Occlusal/Gingiva (mm)",
            key: "vertical",
            index: 2,
            decimals: 1
        },
        { name: "Torque (°)", key: "torque", index: 3, decimals: 0 },
        { name: "Angulation (°)", key: "tip", index: 4, decimals: 0 },
        { name: "Rotation (°)", key: "rotation", index: 5, decimals: 0 },
        {
            name: "Space/Intersection (mm)",
            key: "space_intersection",
            index: null,
            decimals: 1,
            isSpace: true
        }
    ];

    // Build one table for a single jaw. Tables are stacked vertically (Maxilla
    // on top, Mandible on bottom) so each jaw reads as its own block.
    const buildJawTable = (jawLabel, jawColor, jawToothIds) => {
        const table = document.createElement("table");
        table.style.width = "100%";
        table.style.borderCollapse = "collapse";
        table.style.marginTop = "20px";
        table.style.fontSize = "15px";

        // Header: Metric (spans 2 rows) + the jaw group label, then a row of
        // individual tooth IDs.
        const thead = document.createElement("thead");

        const groupHeaderRow = document.createElement("tr");
        const metricHeader = document.createElement("th");
        metricHeader.textContent = "Metric";
        metricHeader.style.padding = "10px";
        metricHeader.style.border = "1px solid #ddd";
        metricHeader.style.backgroundColor = "#f2f2f2";
        metricHeader.style.textAlign = "left";
        metricHeader.style.minWidth = "200px";
        metricHeader.style.whiteSpace = "nowrap";
        metricHeader.setAttribute("rowspan", "2");
        groupHeaderRow.appendChild(metricHeader);

        const jawHeader = document.createElement("th");
        jawHeader.textContent = jawLabel;
        jawHeader.style.padding = "10px";
        jawHeader.style.border = "1px solid #ddd";
        jawHeader.style.backgroundColor = jawColor;
        jawHeader.style.textAlign = "center";
        jawHeader.style.fontWeight = "bold";
        jawHeader.setAttribute("colspan", jawToothIds.length.toString());
        groupHeaderRow.appendChild(jawHeader);
        thead.appendChild(groupHeaderRow);

        const toothHeaderRow = document.createElement("tr");
        jawToothIds.forEach((toothId) => {
            const th = document.createElement("th");
            // Remove "upper-" or "lower-" prefix from tooth ID
            const displayText = String(toothId).replace(/^(upper|lower)-/, "");
            th.textContent = displayText;
            th.style.padding = "10px";
            th.style.border = "1px solid #ddd";
            th.style.backgroundColor = "#f2f2f2";
            th.style.textAlign = "center";
            toothHeaderRow.appendChild(th);
        });
        thead.appendChild(toothHeaderRow);
        table.appendChild(thead);

        // Body: one row per metric.
        const tbody = document.createElement("tbody");

        metrics.forEach((metric) => {
            const row = document.createElement("tr");

            // Metric name cell
            const metricCell = document.createElement("td");
            metricCell.textContent = metric.name;
            metricCell.style.padding = "8px";
            metricCell.style.border = "1px solid #ddd";
            metricCell.style.fontWeight = "bold";
            metricCell.style.backgroundColor = "#f9f9f9";
            metricCell.style.minWidth = "200px";
            metricCell.style.whiteSpace = "nowrap";
            row.appendChild(metricCell);

            // Values for each tooth
            jawToothIds.forEach((toothId) => {
                const record = dataByToothId.get(toothId);
                const cell = document.createElement("td");
                cell.style.padding = "8px";
                cell.style.border = "1px solid #ddd";
                cell.style.textAlign = "right";

                let displayValue = "-";
                let rawVal = null;
                let toothNum = null;

                // Extract tooth number for coloring
                if (record && record.tooth_id != null) {
                    const sid = String(record.tooth_id);
                    const m = sid.match(/(?:upper|lower)-(\d+)/);
                    toothNum = m ? parseInt(m[1], 10) : parseInt(sid, 10);
                    if (toothNum !== toothNum) toothNum = null; // NaN
                }

                if (metric.isSpace) {
                    // Space/Intersection value
                    if (
                        record &&
                        record.space_intersection != null &&
                        typeof record.space_intersection === "number"
                    ) {
                        rawVal = record.space_intersection;
                        displayValue = rawVal.toFixed(metric.decimals);
                        // Color coding based on tooth type
                        const absVal = Math.abs(rawVal);
                        if (toothNum != null) {
                            const anteriorTeeth = new Set([
                                11, 12, 13, 14, 15, 21, 22, 23, 24, 25, 31, 32,
                                33, 34, 35, 41, 42, 43, 44, 45
                            ]);
                            const posteriorTeeth = new Set([
                                16, 17, 18, 26, 27, 28, 36, 37, 38, 46, 47, 48
                            ]);

                            if (anteriorTeeth.has(toothNum)) {
                                // Anterior (5-5): absVal <= 0.2 - green, absVal <= 0.4 - yellow, absVal > 0.4 - red
                                if (absVal <= 0.2) {
                                    cell.classList.add("disp-green");
                                } else if (absVal <= 0.4) {
                                    cell.classList.add("disp-yellow");
                                } else {
                                    cell.classList.add("disp-red");
                                }
                            } else if (posteriorTeeth.has(toothNum)) {
                                // Posterior (5-8): absVal >= 0.1 - red, otherwise green
                                if (absVal >= 0.05) {
                                    cell.classList.add("disp-red");
                                } else {
                                    cell.classList.add("disp-green");
                                }
                            } else {
                                // Unknown tooth type, default to green
                                cell.classList.add("disp-green");
                            }
                        } else {
                            // No tooth number, default to green
                            cell.classList.add("disp-green");
                        }
                    }
                } else {
                    // Displacement value
                    if (
                        record &&
                        record.displacement &&
                        Array.isArray(record.displacement) &&
                        metric.index !== null
                    ) {
                        const value = record.displacement[metric.index];
                        rawVal = typeof value === "number" ? value : null;
                        if (rawVal != null) {
                            displayValue = rawVal.toFixed(metric.decimals);
                            // Apply coloring
                            if (toothNum != null) {
                                const level = getDisplacementCellColorLevel(
                                    rawVal,
                                    metric.key,
                                    toothNum
                                );
                                if (level) cell.classList.add("disp-" + level);
                            }
                        } else if (value != null) {
                            displayValue = String(value);
                        }
                    }
                }

                cell.textContent = displayValue;
                row.appendChild(cell);
            });

            tbody.appendChild(row);
        });

        table.appendChild(tbody);
        return table;
    };

    // Stack the jaws vertically: Maxilla on top, Mandible on bottom.
    if (maxillaIds.length > 0) {
        container.appendChild(buildJawTable("Maxilla", "#e3f2fd", maxillaIds));
    }
    if (mandibleIds.length > 0) {
        container.appendChild(
            buildJawTable("Mandible", "#fff3e0", mandibleIds)
        );
    }
    modal.style.display = "block";
}

/**
 * Download InfoJSON file from the last arrangement result
 */
function downloadInfoJson() {
    if (!globalState.lastArrangementInfoJson) {
        showToast(
            "No InfoJSON data available for download",
            "exclamation-triangle"
        );
        return;
    }

    try {
        // Strip internal processing state from the downloaded file: the rigid A-P
        // shift the molar correction applied (scalar MolarMovementShiftZ) with its
        // per-FDI record (MolarMovementShiftByFdi), and the per-tooth archform
        // expansion (ArchformOffsets). All three are kept only so Refine can
        // reverse them, and none should leak into the InfoJSON the user downloads.
        // Shallow clone so the live object Refine re-uploads keeps them.
        const infoJsonForDownload = { ...globalState.lastArrangementInfoJson };
        delete infoJsonForDownload.MolarMovementShiftZ;
        delete infoJsonForDownload.MolarMovementShiftByFdi;
        delete infoJsonForDownload.ArchformOffsets;

        // Create a blob with the InfoJSON data
        const jsonString = JSON.stringify(infoJsonForDownload, null, 2);
        const blob = new Blob([jsonString], { type: "application/json" });

        // Create a download link
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "arrangement_info.json";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        showToast("InfoJSON file downloaded successfully", "check-circle");
    } catch (error) {
        console.error("Error downloading InfoJSON:", error);
        showToast("Error downloading InfoJSON file", "exclamation-triangle");
    }
}

/**
 * Show the start over / feedback button (CSS handles collapsed/expanded visibility)
 */
function showStartOverAndFeedbackButtons() {
    const startOverButton = document.getElementById("start-over-button");
    const feedbackButton = document.getElementById("feedback-button");
    const collapsedFeedbackButton = document.getElementById(
        "feedback-button-collapsed"
    );

    if (startOverButton) {
        startOverButton.style.visibility = "visible";
    }
    if (feedbackButton) {
        feedbackButton.style.visibility = "visible";
    }
    if (collapsedFeedbackButton) {
        // Remove display override and let CSS handle visibility based on sidebar state
        collapsedFeedbackButton.style.visibility = "visible";
        collapsedFeedbackButton.style.display = "";
    }
}

/**
 * Hide the start over / feedback button
 */
function hideStartOverAndFeedbackButtons() {
    const startOverButton = document.getElementById("start-over-button");
    const feedbackButton = document.getElementById("feedback-button");
    const collapsedFeedbackButton = document.getElementById(
        "feedback-button-collapsed"
    );
    const displacementTableButton = document.getElementById(
        "displacement-table-button"
    );

    if (startOverButton) {
        startOverButton.style.visibility = "hidden";
    }
    if (feedbackButton) {
        feedbackButton.style.visibility = "hidden";
    }
    if (collapsedFeedbackButton) {
        // Hide by setting display to none, overriding CSS
        collapsedFeedbackButton.style.display = "none";
    }
    if (displacementTableButton) {
        displacementTableButton.style.display = "none";
    }
}

/**
 * Update start over button visibility based on results availability
 * (CSS handles the collapsed/expanded state visibility)
 */
function updateStartOverButtonVisibility() {
    const startOverButton = document.getElementById("start-over-button");
    const feedbackButton = document.getElementById("feedback-button");
    const collapsedFeedbackButton = document.getElementById(
        "feedback-button-collapsed"
    );

    if (startOverButton) {
        // Check if we have processed results
        const hasResults =
            globalState.lastInferenceResult ||
            globalState.lastArrangementInfoJson ||
            globalState.mesh;

        // CSS will handle collapsed/expanded visibility
        // We just need to control whether the button exists based on results
        if (hasResults) {
            startOverButton.style.visibility = "visible";
            if (feedbackButton) {
                feedbackButton.style.visibility = "visible";
            }
            if (collapsedFeedbackButton) {
                // Remove display override and let CSS handle visibility based on sidebar state
                collapsedFeedbackButton.style.visibility = "visible";
                collapsedFeedbackButton.style.display = "";
            }
        } else {
            startOverButton.style.visibility = "hidden";
            if (feedbackButton) {
                feedbackButton.style.visibility = "hidden";
            }
            if (collapsedFeedbackButton) {
                // Hide by setting display to none, overriding CSS
                collapsedFeedbackButton.style.display = "none";
            }
        }
    }
}

/**
 * Enable the keypoint labels checkbox
 */
function enableKeypointLabelsCheckbox() {
    const checkbox = document.getElementById("show-keypoint-labels");
    if (checkbox) {
        checkbox.disabled = false;
        const parentLabel = checkbox.closest(".checkbox-item");
        if (parentLabel) {
            parentLabel.style.opacity = "1";
            parentLabel.style.cursor = "pointer";
        }
    }
}

/**
 * Disable the keypoint labels checkbox
 */
function disableKeypointLabelsCheckbox() {
    const checkbox = document.getElementById("show-keypoint-labels");
    if (checkbox) {
        checkbox.disabled = true;
        const parentLabel = checkbox.closest(".checkbox-item");
        if (parentLabel) {
            parentLabel.style.opacity = "0.5";
            parentLabel.style.cursor = "not-allowed";
        }
    }
}

/**
 * Enable the arrangement labels checkbox
 */
function enableArrangementLabelsCheckbox() {
    const checkbox = document.getElementById("show-arrangement-labels");
    if (checkbox) {
        checkbox.disabled = false;
        const parentLabel = checkbox.closest(".checkbox-item");
        if (parentLabel) {
            parentLabel.style.display = "block";
        }
    }
}

/**
 * Disable the arrangement labels checkbox
 */
function disableArrangementLabelsCheckbox() {
    const checkbox = document.getElementById("show-arrangement-labels");
    if (checkbox) {
        checkbox.disabled = true;
        const parentLabel = checkbox.closest(".checkbox-item");
        if (parentLabel) {
            parentLabel.style.display = "none";
        }
    }
}

/**
 * Update visibility of keypoint labels across all scenes
 * @param {boolean} visible - Whether labels should be visible
 */
function updateKeypointLabelsVisibility(visible) {
    // Update labels in main scene
    globalState.scene.traverse((object) => {
        if (object.isCSS2DObject && object.name.startsWith("keypoint-label-")) {
            object.visible = visible;
        }
    });

    // Update labels in multi-view scenes
    if (globalState.multiViewScenes) {
        Object.values(globalState.multiViewScenes).forEach((scene) => {
            if (scene) {
                scene.traverse((object) => {
                    if (
                        object.isCSS2DObject &&
                        object.name.startsWith("keypoint-label-")
                    ) {
                        object.visible = visible;
                    }
                });
            }
        });
    }

    // Also update labels in keypointLabels array if it exists
    if (
        globalState.keypointLabels &&
        Array.isArray(globalState.keypointLabels)
    ) {
        globalState.keypointLabels.forEach((label) => {
            label.visible = visible;
        });
    }
}

/**
 * Update visibility of arrangement object labels across all scenes
 * @param {boolean} visible - Whether labels should be visible
 */
function updateArrangementLabelsVisibility(visible) {
    // Helper function to check if an object should be visible based on current toggles
    const shouldLabelBeVisible = (scene, labelName) => {
        if (!visible) return false;

        // Get the associated mesh object
        const associatedObjectName = labelName.replace(
            "arrangement-label-",
            ""
        );
        const associatedMesh = scene.children.find(
            (child) =>
                child.isMesh &&
                (child.name === associatedObjectName ||
                    child.name.includes(associatedObjectName))
        );

        if (!associatedMesh) return visible;

        // Check if the associated mesh is currently visible (respects jaw and bracket toggles)
        return associatedMesh.visible;
    };

    // Update labels in main scene
    globalState.scene.traverse((object) => {
        if (
            object.isCSS2DObject &&
            object.name.startsWith("arrangement-label-")
        ) {
            object.visible = shouldLabelBeVisible(
                globalState.scene,
                object.name
            );
        }
    });

    // Update labels in multi-view scenes
    if (globalState.multiViewScenes) {
        Object.values(globalState.multiViewScenes).forEach((scene) => {
            if (scene) {
                scene.traverse((object) => {
                    if (
                        object.isCSS2DObject &&
                        object.name.startsWith("arrangement-label-")
                    ) {
                        object.visible = shouldLabelBeVisible(
                            scene,
                            object.name
                        );
                    }
                });
            }
        });
    }
}

// Export functions that need to be used by other modules
export { collapseLeftSidebar };
